// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { joyoKanji } from "./joyoKanji";
import { jinmeiyoKanji } from "./jinmeiyoKanji";
import { joyoKanjiMeaningsWithJlpt, allKanjiMeanings, totalKanjiCount } from "./allKanjiMeanings";
import {
    godanFiveSteps,
    verbGroupNames,
    godanVsIchidan,
    irregularForms,
    verbGroupRecognition,
    godanLookAlikes,
    verbSamePairs,
    godanTeForms,
    godanFiveStepRows,
    godanEndingRows,
    godanVsIchidanRows,
    irregularRows,
    godanLookAlikeRows,
    verbSamePairRows,
    godanTeFormRows,
} from "./verbGroups";
import { JLPT_OVERSEAS_DIRECTORY_URL, JLPT_OVERSEAS_MOBILE_DIRECTORY_URL } from "./jlptOverseasSites";

// PT-PT labels for the verb-group tables in the Godan vs Ichidan post below. The
// Japanese cells are shared with the English post (see verbGroups.ts); only
// these label arrays, in the same order as the shared data, differ.
const ptVerbFormLabels = [
    "Negativa informal (～ない)",
    "Forma formal (～ます)",
    "Forma de dicionário",
    "Condicional (～ば)",
    "Volitiva: vamos / eu vou (～う / ～よう)",
];
const ptGodanStepUses = [
    "Negativa informal (～ない)",
    "Forma formal (～ます)",
    "Forma de dicionário",
    "Condicional (～ば)",
    "Volitiva: vamos / eu vou (～う)",
];
const ptGodanLookAlikeMeanings = [
    "voltar para casa",
    "entrar",
    "correr",
    "cortar",
    "saber/conhecer",
    "precisar",
    "escorregar",
    "agarrar",
];
const ptVerbSamePairMeanings = [
    "vestir",
    "cortar",
    "mudar",
    "voltar para casa",
    "estar (pessoas e animais) - normalmente escrito いる",
    "precisar",
];
const ptTeFormChanges = [
    "う・つ・る → って",
    "う・つ・る → って",
    "う・つ・る → って",
    "む・ぶ・ぬ → んで",
    "む・ぶ・ぬ → んで",
    "む・ぶ・ぬ → んで",
    "く → いて",
    "ぐ → いで",
    "す → して",
    "Exceção: く → って",
];
const ptTeFormMeanings = [
    "vender",
    "comprar",
    "esperar",
    "viver (num sítio)",
    "brincar/divertir-se",
    "morrer",
    "escrever",
    "nadar",
    "falar",
    "ir",
];
// [textbook label, also called, dictionary form ends in] per verbGroupNames row.
const ptVerbGroupNames = [
    ["Grupo 1", "verbos em u", "Qualquer som em u: う く ぐ す つ ぬ ぶ む る"],
    ["Grupo 2", "verbos em ru", "る depois de um som い ou え"],
    ["Grupo 3", "verbos irregulares", "Só する e 来る (くる)"],
];
// [rule, group, why] per verbGroupRecognition row; the examples column is shared.
const ptVerbGroupRecognition = [
    ["う, く, ぐ, す, つ, ぬ, ぶ ou む", "Godan - sempre", "Só os verbos em る podem ser ichidan."],
    [
        "る depois de um som あ, う ou お",
        "Godan - sempre",
        "Os verbos ichidan só existem depois de um som い ou え. É aqui que se encaixa 売る (うる).",
    ],
    [
        "る depois de um som い ou え",
        "Normalmente ichidan",
        "A maioria é ichidan, mas uma pequena lista é godan - veja as exceções abaixo.",
    ],
    ["する ou 来る", "Irregular", "Cada um conjuga-se à sua maneira, incluindo todos os compostos com する."],
];

export const blogPosts = [
    {
        slug: "keigo-teineigo-sonkeigo-kenjogo-explained",
        ptSlug: "keigo-linguagem-de-respeito-em-japones",
        title: "Keigo Explained: Teineigo, Sonkeigo and Kenjōgo",
        date: "2026-10-08",
        excerpt:
            "Keigo (敬語) is how Japanese shows respect. Learn the three types, teineigo, sonkeigo and kenjōgo, " +
            "with simple examples and one question that sorts them out.",
        tags: ["Grammar", "Beginner"],
        keywords: ["keigo", "teineigo", "sonkeigo", "kenjōgo", "Japanese honorific speech", "polite Japanese"],
        content: [
            {
                text:
                    "Keigo (敬語, けいご) is the way Japanese shows respect through word choice and verb forms. It " +
                    "has a reputation for being hard, but one simple model takes you a long way. There are three " +
                    "types. For sonkeigo and kenjōgo the key question is: whose action is this, and who am I " +
                    "showing respect to? Teineigo is simpler: it is just politeness toward the person you are " +
                    "talking to.",
            },
            {
                table: {
                    caption: "The three types of keigo",
                    headers: ["Type", "Japanese", "Purpose"],
                    rows: [
                        ["Teineigo", "丁寧語 (ていねいご)", "Polite speech"],
                        ["Sonkeigo", "尊敬語 (そんけいご)", "Respectful speech, elevates the other person"],
                        ["Kenjōgo", "謙譲語 (けんじょうご)", "Humble speech, lowers your own actions"],
                    ],
                },
            },
            { heading: "Teineigo (丁寧語): everyday polite speech" },
            {
                text:
                    "Teineigo is the polite style you learn first: sentences ending in です and verbs ending in " +
                    "ます. It does not raise or lower anyone in particular. Compare the plain 勉強する " +
                    "(べんきょうする, \"to study\") with the polite 勉強します.",
            },
            {
                list: [
                    "私は学生です。 (Watashi wa gakusei desu.) - I am a student.",
                    "毎日日本語を勉強します。 (Mainichi nihongo o benkyō shimasu.) - I study Japanese every day.",
                ],
            },
            {
                parts: [
                    "This is the foundation, and the style to use with teachers, shop staff and anyone you do not " +
                    "know well. If ます is still new, start with the ",
                    { to: "/grammar/n5-masu-polite-present", label: "ます grammar page" },
                    ".",
                ],
            },
            { heading: "Sonkeigo (尊敬語): raising the other person" },
            {
                text:
                    "Sonkeigo shows respect by elevating someone else's actions. Use it for what the other person " +
                    "does, such as a teacher or a customer, or your boss when you are talking inside your own " +
                    "company, and never for your own actions. Several everyday verbs have a special sonkeigo form:",
            },
            {
                list: [
                    "食べる, 飲む (たべる, のむ, to eat, to drink) → 召し上がる (めしあがる)",
                    "行く, 来る, いる (to go, to come, to be) → いらっしゃる",
                    "言う (いう, to say) → おっしゃる",
                    "見る (みる, to see) → ご覧になる (ごらんになる)",
                    "する (to do) → なさる",
                ],
            },
            {
                list: [
                    "先生はコーヒーを召し上がります。 (Sensei wa kōhī o meshiagarimasu.) - The teacher will have some coffee.",
                    "社長はいらっしゃいますか。 (Shachō wa irasshaimasu ka.) - Is the president in?",
                ],
            },
            {
                text:
                    "Many other verbs follow a pattern: お + the ます stem + になる. For example, 読む (よむ, \"to " +
                    "read\") becomes お読みになる (およみになる). Before ます, いらっしゃる, おっしゃる and なさる take " +
                    "い rather than り: いらっしゃいます, おっしゃいます, なさいます.",
            },
            { heading: "Kenjōgo (謙譲語): lowering your own side" },
            {
                text:
                    "Kenjōgo shows respect from the other direction: it lowers your own actions, or those of your " +
                    "side, such as your family or your company, so the other person is raised by comparison. When " +
                    "you talk to someone from outside your company or family, your own boss or parent counts as " +
                    "your side too, so you lower them rather than raise them. Use it for what you do, especially " +
                    "when it involves the other person (visiting them, giving or showing them something, receiving " +
                    "from them), and never for what the other person does. Many everyday actions, such as 勉強する, " +
                    "have no special kenjōgo form and simply stay in teineigo. These verbs have special forms:",
            },
            {
                list: [
                    "食べる, 飲む (to eat, to drink) → いただく (also \"to receive\")",
                    "行く, 来る (to go, to come) → 参る (まいる) or 伺う (うかがう)",
                    "言う (to say) → 申す (もうす) or 申し上げる (もうしあげる)",
                    "見る (to see) → 拝見する (はいけんする)",
                    "する (to do) → いたす",
                ],
            },
            {
                list: [
                    "私はコーヒーをいただきます。 (Watashi wa kōhī o itadakimasu.) - I will have some coffee.",
                    "私は田中と申します。 (Watashi wa Tanaka to mōshimasu.) - I am Tanaka. (The standard humble self-introduction.)",
                    "明日伺います。 (Ashita ukagaimasu.) - I will visit (you) tomorrow.",
                ],
            },
            {
                text:
                    "The matching pattern is お + the ます stem + する. For example, 持つ (もつ, \"to " +
                    "hold, to carry\") becomes お持ちする (おもちする), and お持ちします means \"I will carry it for " +
                    "you.\" Use 伺う when you are going to visit the person you are respecting (their home, office " +
                    "or place). 参る is more general: it simply says \"go\" or \"come\" humbly and can be used for " +
                    "any destination.",
            },
            { heading: "The same verbs side by side" },
            {
                text:
                    "Here are the same verbs at each level. Apart from the plain form, every column is in ます " +
                    "form.",
            },
            {
                table: {
                    caption: "The same verbs in plain form, teineigo, sonkeigo and kenjōgo",
                    headers: ["Meaning", "Plain", "Teineigo", "Sonkeigo", "Kenjōgo"],
                    rows: [
                        ["to eat / drink", "食べる / 飲む", "食べます / 飲みます", "召し上がります (めしあがります)", "いただきます"],
                        ["to say", "言う", "言います", "おっしゃいます", "申します (もうします)"],
                        ["to go", "行く", "行きます", "いらっしゃいます", "参ります (まいります) / 伺います (うかがいます)"],
                        ["to see", "見る", "見ます", "ご覧になります (ごらんになります)", "拝見します (はいけんします)"],
                        ["to do", "する", "します", "なさいます", "いたします"],
                    ],
                },
            },
            { heading: "A common mistake" },
            {
                callout:
                    "Do not use sonkeigo for yourself. ×私がいらっしゃいます is wrong because it raises you; say " +
                    "私が参ります (Watashi ga mairimasu, \"I will go\") instead. Likewise, never use kenjōgo for " +
                    "the other person's actions. When in doubt, go back to the question: if the action is " +
                    "theirs, use sonkeigo; if it is yours and it involves them (visiting, giving, showing, asking), " +
                    "use kenjōgo; for everything else about yourself, ordinary teineigo is enough. Teineigo is " +
                    "usually underneath, because sonkeigo and kenjōgo verbs are normally combined with ます.",
            },
            { heading: "Where you will meet keigo" },
            {
                text:
                    "You will hear keigo in shops and restaurants, at work, in emails and in announcements. " +
                    "いらっしゃいませ (Irasshaimase, \"Welcome\") uses the sonkeigo verb いらっしゃる, and " +
                    "申し訳ございません (もうしわけございません, mōshiwake gozaimasen, \"I am very sorry\") is a " +
                    "very polite apology. Even いただきます, said before a meal, comes from the kenjōgo verb " +
                    "いただく, which literally means \"to humbly receive\".",
            },
            {
                parts: [
                    "Master です and ます first, since they carry you through most conversations. Then learn a " +
                    "handful of set phrases from the other two types, such as 〜と申します, いただきます and " +
                    "いらっしゃいませ. Some textbooks, " +
                    "and the Japanese government's 2007 guideline (which has five), use more categories: kenjōgo " +
                    "is split in two, which is why 伺う and 参る behave differently, and a fifth type covers the " +
                    "polite お in words like お茶. Three is the right place to start. To check how any verb takes " +
                    "ます, use the ",
                    { to: "/conjugation", label: "Verb Conjugation page" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "japanese-times-table-kuku",
        ptSlug: "tabuada-em-japones-kuku",
        title: "How to Say the Times Table in Japanese: 九九 (Kuku)",
        date: "2026-10-02",
        excerpt: "Learn the Japanese word for the times table, how to read multiplication with かける, and how to chant the two times table using traditional 九九, with hiragana and romaji.",
        tags: ["Beginner", "Reference"],
        keywords: ["Japanese times table", "九九", "kuku", "multiplication", "Japanese numbers"],
        content: [
            { text: "How do you say 'times table' in Japanese? The word you want is 九九 (くく, kuku): the traditional multiplication table for the numbers 1 to 9. Multiplication itself is 掛け算 (かけざん, kakezan). A multiplication chart can be called 九九表 (くくひょう, kukuhyō)." },
            { heading: "How to read multiplication aloud" },
            { text: "In an equation, × is read as かける (kakeru), 'times'. The = sign can be read as イコール (ikōru), 'equals'. So you can read 2 × 3 = 6 as に かける さん イコール ろく (ni kakeru san ikōru roku). You can also say に かける さん は ろく です (ni kakeru san wa roku desu): 'two times three is six'. The particle は is written ha, but pronounced wa here." },
            { table: {
                caption: "Examples of multiplication read with かける",
                headers: ["Equation", "Japanese reading", "Romaji"],
                rows: [
                    ["2 × 3 = 6", "に かける さん は ろく です", "ni kakeru san wa roku desu"],
                    ["4 × 5 = 20", "よん かける ご は にじゅう です", "yon kakeru go wa nijū desu"],
                    ["7 × 8 = 56", "なな かける はち は ごじゅうろく です", "nana kakeru hachi wa gojūroku desu"],
                ],
            } },
            { heading: "九九: chanting the times table" },
            { text: "九九 has a short, rhythmic way of reciting multiplication, which differs from reading an equation normally. Instead of saying かける and イコール, you combine the two factors and the result into a rhythmic phrase. For example, 2 × 3 = 6 becomes にさんがろく (ni san ga roku). Learn these as little phrases: simply joining the usual number readings does not always produce the traditional chant." },
            { heading: "The two times table: 二の段 (にのだん, ni no dan)" },
            { text: "段 (だん, dan) identifies each times table: 二の段 is the two times table, and 三の段 (さんのだん, san no dan) is the three times table. Start with this sequence. The spaces separate the factors from the result to make the reading easier; Japanese does not require these spaces." },
            { table: {
                caption: "The two times table in the traditional 九九 chant",
                headers: ["Equation", "Hiragana", "Romaji"],
                rows: [
                    ["2 × 1 = 2", "にいち が に", "ni ichi ga ni"],
                    ["2 × 2 = 4", "ににん が し", "ni nin ga shi"],
                    ["2 × 3 = 6", "にさん が ろく", "ni san ga roku"],
                    ["2 × 4 = 8", "にし が はち", "ni shi ga hachi"],
                    ["2 × 5 = 10", "にご じゅう", "ni go jū"],
                    ["2 × 6 = 12", "にろく じゅうに", "ni roku jūni"],
                    ["2 × 7 = 14", "にしち じゅうし", "ni shichi jūshi"],
                    ["2 × 8 = 16", "にはち じゅうろく", "ni hachi jūroku"],
                    ["2 × 9 = 18", "にく じゅうはち", "ni ku jūhachi"],
                ],
            } },
            { heading: "Why do shi, shichi and ku appear?" },
            { text: "When learning to count, you will commonly learn 4 as よん (yon), 7 as なな (nana), and 9 as きゅう (kyū). The traditional chant frequently uses the readings し (shi), しち (shichi), and く (ku). That is why 2 × 7 = 14 is にしちじゅうし, while a normal reading can use なな and じゅうよん (jūyon). These are readings used in different contexts." },
            { text: "The rhythm also has its own contractions: 2 × 2 = 4 is ににんがし (ni nin ga shi), and 3 × 3 = 9 is さざんがく (sazan ga ku). が appears in the formulas with a single-digit result, such as にさんがろく; in the table above, it disappears from にごじゅう onwards. Learn these as fixed recitations, without using が as a general replacement for the = sign." },
            { heading: "How to practise" },
            { list: [
                "First read an equation with かける to understand the structure: に かける さん は ろく です.",
                "Recite the two times table while looking at the hiragana, then cover the results and try to recall them.",
                "Compare the normal reading with 九九: よん when counting, but し in にしがはち.",
                "Move on to the other times tables with a pronunciation reference. The rhythm includes forms you cannot work out from the individual numbers alone.",
            ] },
            { parts: ["To review the numbers and look up kanji, hiragana and romaji, use the ", { to: "/numbers", label: "Japanese numbers page" }, ". That tool shows the usual number readings; the contractions in 九九 need to be learnt separately."] },
            { heading: "References for further practice" },
            { parts: ["Visit the ", { href: "https://www.kobun.co.jp/download/appendix-kukucard/dl/", label: "九九 materials page from school publisher 光文書院" }, " for tables and a reading guide. To hear the sequence, try the ", { href: "https://www.douwa-douyou.jp/contents/html/timestable/timestable.shtml", label: "九九 recitation on 童謡童話王国" }, ". These resources are in Japanese."] },
        ],
    },
    {
        slug: "tabuada-em-japones-kuku",
        title: "Como Dizer a Tabuada em Japonês: 九九 (Kuku)",
        date: "2026-10-02",
        lang: "pt-PT",
        excerpt: "Aprenda a dizer «tabuada» em japonês, a ler contas com かける e a recitar a tabuada do 2 com o ritmo tradicional do 九九, com hiragana e romaji.",
        tags: ["Beginner", "Reference"],
        keywords: ["tabuada em japonês", "九九", "kuku", "multiplicação", "números japoneses"],
        content: [
            { text: "Como se diz «tabuada» em japonês? A palavra que procura é 九九 (くく, kuku): a tabuada tradicional dos números de 1 a 9. Para falar de multiplicação, usa-se 掛け算 (かけざん, kakezan). Uma tabela de multiplicação pode chamar-se 九九表 (くくひょう, kukuhyō)." },
            { heading: "Como ler uma multiplicação em voz alta" },
            { text: "Numa conta, o sinal × lê-se かける (kakeru), «vezes». O sinal = pode ler-se イコール (ikōru), «igual». Assim, pode ler 2 × 3 = 6 como に かける さん イコール ろく (ni kakeru san ikōru roku). Também pode dizer に かける さん は ろく です (ni kakeru san wa roku desu): «dois vezes três são seis». A partícula は escreve-se ha, mas aqui pronuncia-se wa." },
            { table: {
                caption: "Exemplos de multiplicações lidas com かける",
                headers: ["Conta", "Leitura em japonês", "Romaji"],
                rows: [
                    ["2 × 3 = 6", "に かける さん は ろく です", "ni kakeru san wa roku desu"],
                    ["4 × 5 = 20", "よん かける ご は にじゅう です", "yon kakeru go wa nijū desu"],
                    ["7 × 8 = 56", "なな かける はち は ごじゅうろく です", "nana kakeru hachi wa gojūroku desu"],
                ],
            } },
            { heading: "九九: a tabuada recitada com ritmo" },
            { text: "O 九九 tem uma forma curta de recitação, diferente da leitura normal de uma conta. Em vez de dizer かける e イコール, juntam-se os dois fatores e o resultado numa expressão rítmica. Por exemplo, 2 × 3 = 6 torna-se にさんがろく (ni san ga roku). Estas formas aprendem-se como pequenas frases: não basta juntar sempre as leituras habituais dos números." },
            { heading: "A tabuada do 2: 二の段 (にのだん, ni no dan)" },
            { text: "段 (だん, dan) identifica cada tabuada: 二の段 é a do 2 e 三の段 (さんのだん, san no dan) é a do 3. Comece por esta sequência. Os espaços separam os fatores do resultado para facilitar a leitura; não são obrigatórios na escrita japonesa." },
            { table: {
                caption: "Tabuada do 2 na recitação tradicional do 九九",
                headers: ["Conta", "Hiragana", "Romaji"],
                rows: [
                    ["2 × 1 = 2", "にいち が に", "ni ichi ga ni"],
                    ["2 × 2 = 4", "ににん が し", "ni nin ga shi"],
                    ["2 × 3 = 6", "にさん が ろく", "ni san ga roku"],
                    ["2 × 4 = 8", "にし が はち", "ni shi ga hachi"],
                    ["2 × 5 = 10", "にご じゅう", "ni go jū"],
                    ["2 × 6 = 12", "にろく じゅうに", "ni roku jūni"],
                    ["2 × 7 = 14", "にしち じゅうし", "ni shichi jūshi"],
                    ["2 × 8 = 16", "にはち じゅうろく", "ni hachi jūroku"],
                    ["2 × 9 = 18", "にく じゅうはち", "ni ku jūhachi"],
                ],
            } },
            { heading: "Porque aparecem shi, shichi e ku?" },
            { text: "Na contagem habitual, é comum aprender 4 como よん (yon), 7 como なな (nana) e 9 como きゅう (kyū). Na recitação tradicional, encontra frequentemente as leituras し (shi), しち (shichi) e く (ku). Por isso, 2 × 7 = 14 é にしちじゅうし, enquanto uma leitura normal pode usar なな e じゅうよん (jūyon). Ambas pertencem a contextos de leitura diferentes." },
            { text: "Há ainda contrações próprias do ritmo: 2 × 2 = 4 diz-se ににんがし (ni nin ga shi), e 3 × 3 = 9 diz-se さざんがく (sazan ga ku). O が aparece nas fórmulas com resultado de um só algarismo, como にさんがろく; na tabuada acima, desaparece a partir de にごじゅう. Aprenda estas expressões como recitações fixas, sem usar が como substituto geral do sinal =." },
            { heading: "Como praticar" },
            { list: [
                "Leia primeiro uma conta com かける para perceber a estrutura: に かける さん は ろく です.",
                "Recite a tabuada do 2 olhando para o hiragana; depois tape os resultados e tente recordá-los.",
                "Compare a leitura normal com o 九九: よん na contagem, mas し em にしがはち.",
                "Passe às outras tabuadas com uma referência de pronúncia. O ritmo inclui formas que não se deduzem apenas dos números isolados.",
            ] },
            { parts: ["Para rever os números e consultar kanji, hiragana e romaji, use a ", { to: "/pt/numbers", label: "página de números japoneses" }, ". Essa ferramenta mostra a leitura habitual dos números; as contrações do 九九 são uma aprendizagem à parte."] },
            { heading: "Referências para continuar" },
            { parts: ["Consulte a ", { href: "https://www.kobun.co.jp/download/appendix-kukucard/dl/", label: "página de materiais de 九九 da editora escolar 光文書院" }, " para encontrar tabelas e um guia de leitura. Para ouvir a sequência, experimente a ", { href: "https://www.douwa-douyou.jp/contents/html/timestable/timestable.shtml", label: "recitação de 九九 do site 童謡童話王国" }, ". Os recursos estão em japonês."] },
        ],
    },
    {
        slug: "joyo-kanji-list",
        title: "The Full Jōyō Kanji List, With Meanings",
        date: "2026-08-27",
        excerpt:
            `All ${joyoKanji.length.toLocaleString()} official jōyō kanji in one place, each paired with its ` +
            "common English meaning - browse the complete list in more detail than the lessons page has room for.",
        tags: ["Kanji", "Reference"],
        keywords: ["kanji", "jōyō", "meanings", "JLPT", "characters"],
        content: [
            {
                text:
                    `The jōyō (常用, "regular use") kanji are the ${joyoKanji.length.toLocaleString()} characters ` +
                    "officially designated by Japan's Ministry of Education for use in laws, newspapers, and " +
                    "everyday publications, in a list last revised in 2010. They're taught throughout compulsory " +
                    "education, and together they're what a fluent adult reader is expected to recognize without " +
                    "furigana.",
            },
            {
                text:
                    "Unlike hiragana and katakana, kanji represent meaning rather than just sound, and most have " +
                    "several possible readings depending on context. The list below pairs every jōyō kanji with " +
                    "one of its common English meanings, so you can browse it as a reference rather than just a " +
                    "wall of characters.",
            },
            { heading: `All ${joyoKanji.length.toLocaleString()} kanji, with meanings` },
            {
                text:
                    "Use the filters below to narrow the list down to a single JLPT level - handy if you're " +
                    "studying toward a specific exam rather than browsing the whole set at once.",
            },
            { kanjiMeaningGrid: joyoKanjiMeaningsWithJlpt },
            {
                parts: [
                    "This list only covers meaning, not pronunciation - most jōyō kanji have both an on'yomi " +
                    "(Chinese-derived) and kun'yomi (native Japanese) reading, which is a separate thing to " +
                    "learn for each one. For an introduction to how kanji fit alongside hiragana and katakana, " +
                    "see the ",
                    { to: "/lessons", label: "lessons page" },
                    ", or try the ",
                    { to: "/converter", label: "hiragana-to-kanji converter" },
                    " to see which kanji are commonly used to write everyday words. To see how kanji are built " +
                    "from smaller parts, browse the ",
                    { to: "/radicals", label: "radicals reference" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "full-kanji-list",
        title: "The Full Kanji List: Jōyō and Jinmeiyō, With Meanings",
        date: "2026-08-28",
        excerpt:
            `Every kanji in Kana Learn's reference - all ${totalKanjiCount.toLocaleString()} of them - in one ` +
            "browsable list, covering the jōyō kanji plus the jinmeiyō kanji used in names.",
        tags: ["Kanji", "Reference"],
        keywords: ["kanji", "jōyō", "jinmeiyō", "meanings", "names"],
        content: [
            {
                text:
                    `This list combines both kanji sets covered on this site: the ${joyoKanji.length.toLocaleString()} ` +
                    "jōyō (常用, \"regular use\") kanji taught in compulsory education and used throughout everyday " +
                    `writing, and the ${jinmeiyoKanji.length.toLocaleString()} jinmeiyō (人名用, "for use in personal ` +
                    "names\") kanji, which are approved for names but aren't part of the standard curriculum.",
            },
            {
                text:
                    "Together they add up to every kanji you'll find on a detail page in Kana Learn. Since jinmeiyō " +
                    "kanji aren't tested on the JLPT, they only show up when browsing \"All\" below rather than " +
                    "under a specific level.",
            },
            { heading: `All ${totalKanjiCount.toLocaleString()} kanji, with meanings` },
            {
                text:
                    "Use the filters below to narrow the list down to a single JLPT level, or browse everything " +
                    "at once with \"All\".",
            },
            { kanjiMeaningGrid: allKanjiMeanings },
            {
                parts: [
                    "Looking for just the jōyō kanji on their own? See the ",
                    { to: "/blog/joyo-kanji-list", label: "jōyō kanji list" },
                    ", or head to the ",
                    { to: "/lessons", label: "lessons page" },
                    " for an introduction to how kanji fit alongside hiragana and katakana. Curious how kanji are " +
                    "built from smaller parts? Browse the ",
                    { to: "/radicals", label: "radicals reference" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "hiragana-vs-katakana",
        title: "Hiragana vs Katakana: What's the Difference?",
        date: "2026-08-27",
        excerpt:
            "Hiragana and katakana cover the exact same sounds, so why does Japanese bother with two scripts? " +
            "Here's how to tell them apart at a glance and know which one a word should use.",
        tags: ["Kana", "Beginner"],
        keywords: ["hiragana", "katakana", "kana", "scripts", "differences"],
        content: [
            {
                text:
                    "If you're new to Japanese, the two kana scripts can feel like a strange redundancy: hiragana " +
                    "(ひらがな) and katakana (カタカナ) represent the exact same set of sounds, just with different " +
                    "shapes. They're not alternatives you pick between, though - each has its own job.",
            },
            { heading: "Spot the difference by shape" },
            {
                text:
                    "Hiragana is rounded and flowing - あ, ひ, ら curve and loop, since the script grew out of " +
                    "cursive handwriting. Katakana is angular and blocky - ア, ヒ, ラ are made of short, straight " +
                    "strokes, since each character was originally just a fragment cut from a more complex kanji. " +
                    "Once you've seen a few side by side, the two styles are easy to tell apart even before you " +
                    "can read them.",
            },
            { heading: "Spot the difference by use" },
            {
                list: [
                    "Hiragana writes native Japanese words, grammar particles (は, を, に…), and verb/adjective endings.",
                    "Katakana writes foreign loanwords (コーヒー, kōhī, \"coffee\"), foreign names, and onomatopoeia.",
                    "Katakana is also used for emphasis, similar to italics in English.",
                ],
            },
            {
                text:
                    "A simple rule of thumb: if a word came from English, French, or another foreign language " +
                    "relatively recently, it's almost certainly written in katakana. Everything else - the " +
                    "grammatical glue of a sentence especially - is hiragana.",
            },
            {
                parts: [
                    "Because they map one-to-one, once you've learned hiragana, learning katakana is mostly just " +
                    "memorizing a second set of shapes for sounds you already know. Try both scripts yourself " +
                    "with the practice keyboard, or read the full breakdown in the ",
                    { to: "/lessons", label: "hiragana and katakana lessons" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "type-japanese-without-japanese-keyboard",
        title: "How to Type Japanese Without a Japanese Keyboard",
        date: "2026-08-20",
        excerpt:
            "You don't need a Japanese IME installed or a special keyboard layout to write in kana. " +
            "Romaji input lets you type Japanese sounds with the keys you already have.",
        tags: ["Typing", "Tools"],
        keywords: ["typing", "romaji", "keyboard", "IME", "hiragana"],
        content: [
            {
                text:
                    "Most people trying to write Japanese for the first time assume they need to install an " +
                    "input method editor (IME) or switch their whole keyboard layout. You don't - the standard " +
                    "way Japanese speakers themselves type kana on a regular QWERTY keyboard is romaji input, " +
                    "and it works the same way here.",
            },
            { heading: "How romaji input works" },
            {
                text:
                    "Romaji input converts the Latin letters you type into the kana that sound the same. Type " +
                    "\"ka\" and you get か; type \"kya\" and you get きゃ. There's no need to memorize where each " +
                    "kana lives on some special layout - you're just spelling out the sound.",
            },
            {
                list: [
                    "Single vowels: a, i, u, e, o → あ, い, う, え, お",
                    "Consonant + vowel: ka, shi, tsu, etc. → か, し, つ",
                    "Combination sounds: kya, sho, ryu, etc. → きゃ, しょ, りゅ",
                    "Doubled consonants: type the consonant twice, e.g. \"kitte\" → きって (the small っ)",
                    "The nasal ん: type \"n\" (or \"nn\" before a vowel, to avoid it merging into na/ni/nu/ne/no)",
                ],
            },
            { heading: "Try it directly in the browser" },
            {
                parts: [
                    "The ",
                    { to: "/keyboard", label: "on-screen keyboard" },
                    " on this site supports exactly this: type romaji into the input field and watch it convert " +
                    "to hiragana or katakana in real time, or click characters directly if you'd rather not type " +
                    "romaji at all. There's nothing to install, and nothing to configure.",
                ],
            },
            {
                parts: [
                    "Already have hiragana and want the kanji it's usually written with? The ",
                    { to: "/converter", label: "hiragana-to-kanji converter" },
                    " suggests common kanji for beginner vocabulary as you type.",
                ],
            },
        ],
    },
    {
        slug: "tips-to-memorize-kana-faster",
        title: "5 Tips for Memorizing Hiragana and Katakana Faster",
        date: "2026-08-13",
        excerpt:
            "92 characters between the two scripts sounds like a lot, but most learners have both down within " +
            "a couple of weeks using the right approach. Here's what actually works.",
        tags: ["Study Tips", "Beginner"],
        keywords: ["hiragana", "katakana", "kana", "memorization", "tips"],
        content: [
            {
                text:
                    "Hiragana and katakana together are 46 characters each, plus voiced and combination forms - " +
                    "but unlike kanji, that's a fixed, finite set you only need to learn once. Here's how to get " +
                    "there quickly.",
            },
            { heading: "1. Learn hiragana completely before starting katakana" },
            {
                text:
                    "It's tempting to study both at once since they represent the same sounds, but mixing them " +
                    "early just doubles the chance of confusing similar-looking characters. Get hiragana solid " +
                    "first - katakana goes much faster afterward since you're only learning new shapes, not new sounds.",
            },
            { heading: "2. Drill by row, not alphabetically" },
            {
                text:
                    "Learn the five vowels first, then one consonant row at a time (か・き・く・け・こ, then " +
                    "さ・し・す・せ・そ, and so on). This mirrors how the characters are actually organized " +
                    "(the gojūon grid) and makes the voiced forms (が, ざ, だ…) trivial to pick up afterward, " +
                    "since they're just the same rows with a dakuten added.",
            },
            { heading: "3. Write each character by hand" },
            {
                parts: [
                    "Typing and reading are useful, but the muscle memory from actually drawing a character - " +
                    "even just a few times on paper - makes it stick far faster than recognition alone. The ",
                    { to: "/kana-writing-practice", label: "kana writing practice" },
                    " page animates each hiragana and katakana character's stroke order and lets you trace it " +
                    "yourself.",
                ],
            },
            { heading: "4. Use mnemonics for the ones that don't stick" },
            {
                text:
                    "Most learners get through 80% of kana quickly and then get stuck on a handful of " +
                    "look-alikes (ぬ/め/ね, シ/ツ, ソ/ン). A silly image tied to the shape - \"ン looks like it's " +
                    "about to fall over\" - beats pure repetition for exactly these stragglers.",
            },
            { heading: "5. Practice with real input, not just flashcards" },
            {
                parts: [
                    "Recognizing a character passively is a different skill from producing it. Typing real " +
                    "words on the ",
                    { to: "/keyboard", label: "practice keyboard" },
                    " - or working through the ",
                    { to: "/lessons", label: "lessons" },
                    " character by character - forces active recall, which is what actually transfers to " +
                    "reading real Japanese text.",
                ],
            },
        ],
    },
    {
        slug: "translate-japanese-from-a-photo",
        title: "How to Translate Japanese From a Photo",
        date: "2026-09-05",
        excerpt:
            "Point your camera at a menu, sign, or page of Japanese text and get an English translation in " +
            "seconds - no typing, and no separate translation app to install.",
        tags: ["Translation", "Tools"],
        keywords: ["translation", "photo", "OCR", "camera", "menu"],
        content: [
            {
                text:
                    "The fastest way to translate Japanese you can't type yourself is usually a photo - a " +
                    "restaurant menu, a street sign, a page from a book, or a screenshot. KanaLearn's translator " +
                    "reads the Japanese text out of a photo directly in your browser, then translates it to " +
                    "English, without you typing a single character.",
            },
            { heading: "Translating a photo, step by step" },
            {
                list: [
                    "Open the translator page.",
                    "Click \"Upload a photo instead\" and choose a photo containing Japanese text - a " +
                    "screenshot works just as well as a picture taken with a camera.",
                    "Wait a few seconds while the text is read from the image.",
                    "Check the extracted Japanese text, and fix anything that looks wrong before translating.",
                    "Click \"Translate\" to get the English translation.",
                ],
            },
            {
                parts: [
                    "Try it now on the ",
                    { to: "/translate", label: "Japanese photo translator" },
                    " - it's free, and the photo never leaves your browser during the text-reading step.",
                ],
            },
            { heading: "Why read the text out first, instead of translating the photo directly" },
            {
                text:
                    "Extracting the text before translating it means you get to see - and fix - exactly what " +
                    "was read, before it's turned into English. Optical character recognition (OCR) can " +
                    "occasionally misread a character in a blurry photo, an unusual font, or handwriting, and " +
                    "catching that at the text stage avoids a translation that reads confidently but is wrong " +
                    "for a reason you'd never spot in the English output alone.",
            },
            { heading: "Getting a photo that reads reliably" },
            {
                list: [
                    "Good, even lighting - avoid heavy glare or shadows falling across the text",
                    "Hold the camera roughly parallel to the page or sign, not at a steep angle",
                    "Fill the frame with the text rather than a lot of surrounding background",
                    "Printed text reads far more reliably than handwriting",
                ],
            },
            {
                parts: [
                    "Already have the text typed or copied somewhere? The same ",
                    { to: "/translate", label: "translator" },
                    " accepts plain Japanese text too - the photo step is only there for text you can't type " +
                    "yourself.",
                ],
            },
        ],
    },
    {
        slug: "jlpt-europe-countries-with-no-test-site",
        title: "European Countries With No JLPT Test Site (And Where to Go Instead)",
        date: "2026-09-22",
        excerpt:
            "Not every country in Europe currently has a JLPT exam site. Here's which ones are affected right " +
            "now, and the nearest place to sit the test if you live in one of them.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["JLPT", "Europe", "exam", "venues", "travel"],
        content: [
            {
                text:
                    "The JLPT's overseas test sites change from year to year - a country can lose its usual venue, " +
                    "or have it suspended for a cycle, without much warning. Right now, two countries in Europe fall " +
                    "into that gap: Portugal and Belgium. If you live in either, here's what's actually going on " +
                    "and where the nearest real alternative is.",
            },
            { heading: "Portugal: no active site" },
            {
                text:
                    "Portugal's listed JLPT site is currently marked temporarily suspended, with no session " +
                    "scheduled. The closest country with real, active sites is Spain, which runs the exam across " +
                    "several cities each year.",
            },
            { heading: "Belgium: suspended for 2026" },
            {
                text:
                    "Belgium normally runs the JLPT in Leuven and Ghent, but both are suspended for 2026. The " +
                    "nearest active alternative is the Netherlands (Leiden) - closer than the next-nearest options " +
                    "in Germany or France.",
            },
            { heading: "Finding your nearest option automatically" },
            {
                parts: [
                    "Rather than cross-checking this by hand, save your country on your ",
                    { to: "/profile", label: "profile page" },
                    " and it'll carry over to the ",
                    { to: "/jlpt", label: "JLPT test site search" },
                    ": if your country currently has no active site, the page tells you clearly and offers the " +
                    "nearest real alternative with one click - without changing the country saved on your profile, " +
                    "so you can always search anywhere else too.",
                ],
            },
            {
                text:
                    "This list reflects the site data at the time of writing and will get out of date as exam " +
                    "sites change - always confirm directly on the official JLPT site before making travel plans.",
            },
            {
                parts: [
                    "If you're studying from Portugal specifically, see our ",
                    { to: "/blog/jlpt-study-plan-no-portugal-test-site", label: "full self-study plan for the gap" },
                    ", or read about ",
                    { to: "/blog/learning-japanese-in-portugal-da-vinci-school", label: "what a real remote N5 course from Portugal looks like" },
                    " if you'd rather have a class than study alone. Already know you'll be registering through " +
                        "Spain? See our ",
                    { to: "/blog/jlpt-registration-portugal", label: "complete step-by-step JLPT registration guide" },
                    " for the actual application process.",
                ],
            },
        ],
    },
    {
        slug: "learning-japanese-in-portugal-da-vinci-school",
        ptSlug: "aprender-japones-em-portugal-curso-n5",
        title: "Learning Japanese in Portugal: What a Real N5 Course Looks Like",
        date: "2026-09-22",
        excerpt:
            "Portugal doesn't currently have an active JLPT test site, but that doesn't mean there's nowhere to " +
            "study - here's a first-hand look at a real, fully remote N5 course from Da Vinci Language School.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["Portugal", "N5", "remote", "course", "JLPT"],
        content: [
            {
                parts: [
                    "As we covered in ",
                    { to: "/blog/jlpt-europe-countries-with-no-test-site", label: "our post on JLPT test sites in Europe" },
                    ", Portugal currently has no active JLPT venue. That's a real gap for taking the exam, but " +
                        "it's not the same as there being nowhere to actually learn Japanese in Portugal. One of us " +
                        "has been taking a fully remote Japanese course, so here's what that experience is " +
                        "actually like.",
                ],
            },
            { heading: "The school: Da Vinci Language School (remote, based in Faro)" },
            {
                parts: [
                    "Da Vinci Language School is run by Ginásios da Educação Da Vinci (GEDAVINCI LDA), a DGERT-" +
                        "certified training entity based in Faro, Portugal. Importantly, the Japanese course " +
                        "itself is entirely remote: live, real-time group classes over Zoom rather than pre-" +
                        "recorded video or in-person attendance. You don't need to live near Faro, or even in " +
                        "the Algarve - anyone in Portugal with a stable internet connection can join. See the " +
                        "current language courses, including Japanese, on ",
                    { href: "https://www.ginasiosdavinci.com/cursos-de-linguas/", label: "Da Vinci's own site" },
                    ".",
                ],
            },
            { heading: "How the course is structured" },
            {
                text:
                    "Beginner Japanese is split into two consecutive modules, N5.1 and N5.2, each 25 hours of " +
                    "class time delivered as two 1.5-hour sessions per week over roughly two months. Each module " +
                    "ends with a graded test, and finishing one earns a certificate. The school also runs a " +
                    "separate course listed simply as \"N5\" (no .1/.2 split) aimed specifically at JLPT exam " +
                    "preparation, distinct from the two beginner modules.",
            },
            {
                text:
                    "Pricing at the time of writing: the N5.1 module is 300€, N5.2 is 275€, and the standalone " +
                    "JLPT-prep \"N5\" course is 137.5€ - all for 25 hours of group instruction. Private lessons " +
                    "with the same teacher are also available outside the school's group courses, for anyone who " +
                    "wants a faster or more individual pace.",
            },
            { heading: "Practical things to know before signing up" },
            {
                text:
                    "A few policy details worth knowing if you're considering a similar course (these are common " +
                    "for this kind of certified group training, not unique to any one provider): attendance is " +
                    "tracked per session, and missing more than a set share of the total hours (commonly around " +
                    "10-20%) can mean you don't pass the module. Withdrawing partway through generally doesn't " +
                    "come with a refund unless the delay or cancellation is on the school's side - e.g. a group " +
                    "not reaching its minimum size. Getting the official certificate at the end usually requires " +
                    "meeting the attendance minimum and passing the course, not just finishing the calendar. " +
                    "Always check the specific school's own terms before paying, since these details vary.",
            },
            { heading: "Is this a substitute for the missing test site?" },
            {
                parts: [
                    "No - taking a course like this doesn't change the fact that Portugal has no active JLPT " +
                        "venue right now. If you're aiming to actually sit the exam, you'll still need to travel, " +
                        "and our ",
                    { to: "/jlpt-test-sites", label: "JLPT test site page" },
                    " can point you to the nearest active alternative. What a course like this does solve is the " +
                        "more basic question of where to actually learn the language in the meantime, with real " +
                        "feedback and structure rather than studying alone.",
                ],
            },
            {
                parts: [
                    "If you're in Portugal and looking for a structured option, it's worth searching for a local " +
                        "language school offering live or in-person Japanese classes - and pairing that with the " +
                        "free tools here for daily practice: ",
                    { to: "/lessons", label: "lessons" },
                    ", ",
                    { to: "/kanji-roulette", label: "kanji practice" },
                    ", and ",
                    { to: "/study-materials", label: "study material recommendations" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "jlpt-study-plan-no-portugal-test-site",
        ptSlug: "plano-estudo-jlpt-portugal-sem-exame-no-pais",
        title: "Studying for the JLPT From Portugal When There's No Local Test Site",
        date: "2026-10-07",
        excerpt:
            "Portugal doesn't currently have an active JLPT exam site. Here's a full self-study path using " +
            "NihongoTech's own lessons, grammar, kanji, and conjugation tools, a real remote-course alternative, " +
            "and where to actually go once you're ready to sit the exam.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["JLPT", "Portugal", "study plan", "self-study", "exam"],
        content: [
            {
                parts: [
                    "If you're studying for the JLPT in Portugal right now, you've probably already found the " +
                        "inconvenient part: there's no active overseas JLPT site in the country at the moment. We " +
                        "cover why, and the nearest real alternative, in ",
                    { to: "/blog/jlpt-europe-countries-with-no-test-site", label: "our post on which European countries currently have no JLPT site" },
                    " - this one picks up from there with the part that matters most once you already know that: " +
                        "what to actually study with, and where things go once you're ready to travel for the exam.",
                ],
            },
            { heading: "A full self-study path on NihongoTech, in order" },
            {
                parts: [
                    "You don't need five different apps to cover this. One sensible order through NihongoTech's " +
                        "own routes: start with ",
                    { to: "/lessons", label: "Lessons" },
                    " for structure, move into ",
                    { to: "/grammar", label: "Grammar" },
                    " once the basics are down, build kanji recognition with the ",
                    { to: "/radicals", label: "radicals reference" },
                    ", drill verb forms with ",
                    { to: "/conjugation", label: "Conjugation practice" },
                    ", and print ",
                    { to: "/worksheets", label: "writing worksheets" },
                    " for the stroke-order repetition a screen alone can't replace.",
                ],
            },
            { heading: "Want a class instead of self-study?" },
            {
                parts: [
                    "Self-study isn't the only option if you're based in Portugal: ",
                    { to: "/blog/learning-japanese-in-portugal-da-vinci-school", label: "our write-up of a real, fully remote N5 course from Da Vinci Language School" },
                    " covers what a live, Zoom-based class actually looks like, if you'd rather have an " +
                        "instructor and classmates than work through this alone.",
                ],
            },
            { heading: "When you're ready to actually sit the exam" },
            {
                parts: [
                    "Once you've put in the study time, the exam itself still has to happen somewhere with an " +
                        "active site - currently Spain is the nearest real option to Portugal. Our ",
                    { to: "/jlpt-test-sites", label: "JLPT overseas test site directory" },
                    " lists every current host by country and city, and suggests the nearest alternative " +
                        "automatically if your own country doesn't have one. When you're ready to actually " +
                        "apply, our ",
                    { to: "/blog/jlpt-registration-portugal", label: "complete step-by-step JLPT registration guide" },
                    " walks through choosing a host institution, the application itself, fees, payment, and " +
                        "what to bring on test day.",
                ],
            },
            { heading: "FAQ" },
            {
                parts: [
                    "Is Portugal's JLPT suspension permanent? No end date has been given publicly as of this " +
                        "writing - see ",
                    { to: "/blog/jlpt-europe-countries-with-no-test-site", label: "the Europe test-site post" },
                    " for the latest, and always confirm directly on the official JLPT site before booking travel.",
                ],
            },
            {
                text:
                    "Is a Portuguese-language version of this site available? Yes - every route named above " +
                    "(lessons, grammar, radicals, conjugation, worksheets) also has a European Portuguese version; " +
                    "switch language from the header, or go to the /pt/ version of each page directly.",
            },
            {
                parts: [
                    "Ready to see the whole path at once? ",
                    { to: "/learning-roadmap", label: "See the full learning roadmap" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "plano-estudo-jlpt-portugal-sem-exame-no-pais",
        title: "Como Estudar para o JLPT em Portugal, Sem Exame no País",
        date: "2026-10-07",
        lang: "pt-PT",
        excerpt:
            "Portugal não tem atualmente um local de exame JLPT ativo. Eis um percurso de autoestudo completo " +
            "com as próprias ferramentas do NihongoTech, uma alternativa real em curso remoto, e para onde ir " +
            "quando estiver pronto/a para fazer o exame.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["JLPT", "Portugal", "plano de estudo", "autoestudo", "exame"],
        content: [
            {
                parts: [
                    "Se está a estudar para o JLPT em Portugal neste momento, já deve ter percebido a parte mais " +
                        "incómoda: não há atualmente nenhum local de exame do JLPT ativo no país. Explicamos o " +
                        "porquê, e qual é a alternativa real mais próxima, no nosso ",
                    { to: "/pt/blog/jlpt-europe-countries-with-no-test-site", label: "artigo sobre os países europeus sem local de exame JLPT" },
                    " - este texto continua a partir daí, com a parte que mais importa depois de já saber isso: " +
                        "com o que estudar, e o que fazer quando estiver pronto/a para viajar até ao exame.",
                ],
            },
            { heading: "Um percurso de autoestudo completo no NihongoTech, em português" },
            {
                parts: [
                    "Não precisa de cinco aplicações diferentes para isto. Uma ordem sensata pelas próprias " +
                        "secções do NihongoTech: comece pelas ",
                    { to: "/pt/lessons", label: "Lições" },
                    " para ter uma estrutura, avance para a ",
                    { to: "/pt/grammar", label: "Gramática" },
                    " depois de ter as bases, construa o reconhecimento de kanji com a ",
                    { to: "/pt/radicals", label: "referência de radicais" },
                    ", pratique a conjugação a sério com a ",
                    { to: "/pt/conjugation", label: "prática de conjugação" },
                    ", e imprima ",
                    { to: "/pt/worksheets", label: "fichas de escrita" },
                    " para a repetição da ordem dos traços que o ecrã por si só não substitui.",
                ],
            },
            { heading: "Prefere uma aula a estudar sozinho/a?" },
            {
                parts: [
                    "Estudar sozinho/a não é a única opção para quem está em Portugal: o nosso ",
                    { to: "/blog/aprender-japones-em-portugal-curso-n5", label: "relato sobre um curso de N5 totalmente remoto na Da Vinci Language School" },
                    " mostra como é de facto uma aula ao vivo por Zoom, caso prefira ter um professor e colegas " +
                        "em vez de avançar sozinho/a.",
                ],
            },
            { heading: "Quando estiver pronto/a para fazer o exame" },
            {
                parts: [
                    "Depois de estudar, o exame em si continua a precisar de um local com sessão ativa - " +
                        "atualmente Espanha é a alternativa real mais próxima de Portugal. O nosso ",
                    { to: "/pt/jlpt-test-sites", label: "diretório de locais de exame JLPT no estrangeiro" },
                    " lista todos os locais atuais por país e cidade, e sugere automaticamente a alternativa " +
                        "mais próxima se o seu país não tiver nenhum. Quando estiver pronto/a para se candidatar " +
                        "de facto, o nosso ",
                    { to: "/blog/como-inscrever-se-no-jlpt-em-portugal", label: "guia completo, passo a passo, de inscrição no JLPT" },
                    " percorre a escolha da instituição anfitriã, a candidatura em si, as taxas, o pagamento e " +
                        "o que levar no dia do exame.",
                ],
            },
            { heading: "Perguntas frequentes" },
            {
                parts: [
                    "A suspensão do local em Portugal é permanente? Não foi dada nenhuma data de regresso até à " +
                        "data desta publicação - veja o artigo sobre ",
                    { to: "/pt/blog/jlpt-europe-countries-with-no-test-site", label: "os países sem local de exame" },
                    " para a informação mais recente, e confirme sempre diretamente no site oficial do JLPT " +
                        "antes de planear a viagem.",
                ],
            },
            {
                text:
                    "Posso estudar tudo isto em português? Sim - todas as secções referidas acima (lições, " +
                    "gramática, radicais, conjugação, fichas de escrita) têm versão em português europeu.",
            },
            {
                parts: [
                    "Quer ver o percurso completo de uma vez? ",
                    { to: "/pt/learning-roadmap", label: "Veja o roteiro de aprendizagem completo" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "jlpt-registration-portugal",
        ptSlug: "como-inscrever-se-no-jlpt-em-portugal",
        title: "JLPT Registration From Portugal: The Complete Step-by-Step Guide",
        date: "2026-10-09",
        excerpt:
            "Portugal has no active JLPT test site, so signing up for the exam means registering directly " +
            "through a host institution abroad. Here's the full process start to finish: choosing a level, " +
            "finding the right institution, applying, paying, confirmation, and what to bring on test day.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["JLPT", "Portugal", "JLPT registration", "how to sign up for JLPT", "Spain", "registration"],
        content: [
            {
                parts: [
                    "Portugal currently has no active JLPT test site - we cover why, and the timeline, in ",
                    { to: "/blog/jlpt-europe-countries-with-no-test-site", label: "our post on which European countries currently have none" },
                    ". That means if you're based in Portugal and want to sign up for the JLPT, \"registering\" " +
                        "actually means applying through a host institution in another country. This guide walks " +
                        "through that whole process, start to finish, using Spain - the nearest real option - as " +
                        "the worked example, plus what changes if you end up registering somewhere else.",
                ],
            },
            { heading: "There's no single JLPT registration site" },
            {
                text:
                    "This is the part that trips up almost everyone the first time: jlpt.jp doesn't run one " +
                    "central system where you sign up for the test. Every test site outside Japan is organized " +
                    "by a local host institution - usually a university or a Japanese-language association - and " +
                    "you apply directly on that institution's own site, with their own form, their own bank " +
                    "account for the fee, and their own deadlines. The official overseas directory itself is " +
                    "explicit about this: for registration details, it tells you to \"contact your local host " +
                    "institution\" rather than describing one shared process.",
            },
            {
                text:
                    "That's true even within a single country. In Spain, four of the six currently listed host " +
                    "institutions share one application portal run by the Japan Foundation's branch in Madrid - " +
                    "but Granada's host runs its own separate registration on its own university site instead, " +
                    "with no connection to that shared portal. There's no shortcut that works for \"your " +
                    "country\" in general - you have to check the specific host institution you're applying to, " +
                    "every time.",
            },
            { heading: "Step 1: Choose your level (N5 to N1)" },
            {
                text:
                    "The JLPT has five levels, N5 (easiest) through N1 (hardest). Per the official level " +
                    "summary, N5 and N4 cover basic Japanese - reading hiragana, katakana and common kanji, " +
                    "following slow, simple conversation. N3 bridges basic and advanced: everyday reading, the " +
                    "gist of somewhat harder material, and conversation closer to natural speed. N2 and N1 cover " +
                    "everyday and increasingly varied, complex Japanese - newspaper articles and abstract " +
                    "writing, natural-speed conversation and lectures. Choose based on how much you've actually " +
                    "studied, not how you'd like to be seen - each section has its own pass mark, and failing " +
                    "any one section fails the whole level regardless of your overall score.",
            },
            {
                table: {
                    caption: "The five JLPT levels, from easiest to hardest",
                    headers: ["Level", "What it tests", "Test sections and timing"],
                    rows: [
                        [
                            "N5",
                            "Basic Japanese: hiragana, katakana and common kanji phrases; slow, simple spoken conversation",
                            "3 parts - Vocabulary 20 min, Grammar/Reading 40 min, Listening 30 min",
                        ],
                        [
                            "N4",
                            "Basic classroom Japanese: everyday topics in simple vocabulary and kanji; slow spoken conversation",
                            "3 parts - Vocabulary 25 min, Grammar/Reading 55 min, Listening 35 min",
                        ],
                        [
                            "N3",
                            "A bridge to advanced Japanese: everyday texts, the gist of harder material, near-natural-speed conversation",
                            "3 parts - Vocabulary 30 min, Grammar/Reading 70 min, Listening 40 min",
                        ],
                        [
                            "N2",
                            "Everyday and varied real-world Japanese: newspaper and magazine articles, near-natural-speed conversation and news",
                            "2 parts - Language Knowledge/Reading 105 min, Listening 50 min",
                        ],
                        [
                            "N1",
                            "A wide range of Japanese: logically complex or abstract writing, natural-speed conversation and lectures",
                            "2 parts - Language Knowledge/Reading 110 min, Listening 55 min",
                        ],
                    ],
                },
            },
            {
                parts: [
                    "Build toward whichever level you choose with NihongoTech's own ",
                    { to: "/conjugation", label: "verb conjugation practice" },
                    ", ",
                    { to: "/grammar", label: "grammar reference" },
                    " and ",
                    { to: "/lessons", label: "lessons" },
                    " - or, if you're studying from Portugal specifically, follow the ",
                    {
                        to: "/blog/jlpt-study-plan-no-portugal-test-site",
                        label: "full self-study plan we put together for exactly that situation",
                    },
                    ".",
                ],
            },
            { heading: "Step 2: Find out where you can actually register" },
            {
                parts: [
                    "Since there's no central portal, your first real task is finding your host institution. " +
                        "Start at the official JLPT overseas test site directory ( ",
                    { href: JLPT_OVERSEAS_DIRECTORY_URL, label: "desktop" },
                    " · ",
                    { href: JLPT_OVERSEAS_MOBILE_DIRECTORY_URL, label: "mobile" },
                    " ), or use NihongoTech's own ",
                    { to: "/jlpt-test-sites", label: "JLPT test site search" },
                    ", which mirrors that same official list and automatically suggests the nearest alternative " +
                        "if your own country doesn't have one.",
                ],
            },
            {
                text:
                    "As already established on this site: Portugal is currently listed with no active host " +
                    "institution. Spain is the nearest country with several real, active ones.",
            },
            {
                list: [
                    "Barcelona - Universitat Autònoma de Barcelona (July and December)",
                    "Madrid - Universidad Complutense de Madrid (July only)",
                    "Madrid - Universidad Autónoma de Madrid, UAM (December only) - a separate host from Complutense, even though it's the same city",
                    "Granada - Centro de Lenguas Modernas, Universidad de Granada (July only)",
                    "Valencia - Asociación Cultural KOTOVA (July and December)",
                    "Las Palmas de Gran Canaria - Universidad de Las Palmas de Gran Canaria (December only)",
                ],
            },
            {
                text:
                    "Which sessions each host actually runs can change between cycles, so always confirm " +
                    "current availability on the official directory or the search above before making any plans.",
            },
            { heading: "Step 3: Go to that specific institution's own site" },
            {
                parts: [
                    "Once you know your host institution, registration happens on their site - not a generic " +
                        "JLPT page. For four of Spain's six host institutions above (Barcelona, Las Palmas de " +
                        "Gran Canaria, Madrid/UAM and Valencia), that shared site is ",
                    { href: "https://www.jlpt.es/", label: "jlpt.es" },
                    ", run by the Japan Foundation's branch in Madrid. Granada's host registers separately, on " +
                        "its own university site.",
                ],
            },
            {
                text:
                    "Madrid is a good example of why you can't assume: it has two separate host institutions " +
                    "for two different sessions (Universidad Complutense de Madrid in July, Universidad " +
                    "Autónoma de Madrid in December), and only one of them - UAM - currently registers through " +
                    "the shared jlpt.es portal.",
            },
            {
                parts: [
                    "It's also worth checking you've landed on the right page for the right purpose: " +
                        "Barcelona's own Japanese-studies department page, for instance, currently ",
                    {
                        href: "https://webs.uab.cat/oficinasakura/es/noken/",
                        label: "states that it doesn't offer direct support for the JLPT",
                    },
                    " and points candidates to the shared jlpt.es system instead - a reminder to check the " +
                        "actual current registration page for your host, not just the first official-looking " +
                        "page you find.",
                ],
            },
            { heading: "Step 4: Registration windows" },
            {
                text:
                    "Registration windows are set by each host institution, not by jlpt.jp globally - they're " +
                    "short, they don't repeat, and they won't wait for you to feel ready.",
            },
            {
                text:
                    "A concrete, current example: for the December 6, 2026 session, the Spain-wide jlpt.es " +
                    "portal ran its registration window from September 1 to September 30, 2026 - about two " +
                    "months before test day. We verified this directly on jlpt.es on October 9, 2026, by which " +
                    "point that window had already closed; the next session (July 2027) is expected to open " +
                    "registration around March 2027, per that host's own announcement, though this is " +
                    "host-specific and not guaranteed - confirm directly before planning around it.",
            },
            {
                text:
                    "Spain's host institutions also warn that places are limited per level and per host, and " +
                    "that a host can close registration early once it fills up, even before the official " +
                    "deadline. Apply as soon as the window opens rather than waiting.",
            },
            { heading: "Step 5: The application itself, step by step" },
            {
                text:
                    "Have two things ready before you start: a digital passport-style photo, and a digital copy " +
                    "(scan or clear photo) of your proof of payment (see Fees and payment below) - most host " +
                    "sites won't let you begin the form without both.",
            },
            {
                list: [
                    "Choose your venue first - once selected (and paid for), it can't be changed, and your application only counts for that one host",
                    "Personal details - your level, the ID number you'll bring on test day, your full name as it should appear on your results and certificate, date of birth, nationality, native language, postal address (where paper results are mailed), phone number, and an email address that becomes your main contact channel through test day",
                    "Set your own password (for Spain: exactly 8 digits, numbers only) for checking results online later, then upload your passport-style photo and proof of payment",
                    "A short background questionnaire - where and how you study Japanese, why you're taking the test, and how many times (if any) you've sat each level before - then a final consent checkbox before you submit",
                ],
            },
            {
                text:
                    "One concrete, host-specific detail worth knowing in advance: Spain's registration form " +
                    "doesn't accept certain accented characters, including ñ and ç, so a Portuguese name may " +
                    "need a substituted spelling on that particular form. Rules like this vary by host - check " +
                    "the actual form for yours rather than assuming.",
            },
            { heading: "Fees and payment" },
            {
                text:
                    "Fee, verified directly: 75 euros for all levels (N5 through N1), confirmed on the official " +
                    "registration pages of all four Spain host institutions that share the jlpt.es portal " +
                    "(Barcelona, Las Palmas de Gran Canaria, Madrid/UAM and Valencia) as of October 2026. " +
                    "jlpt.es itself notes this fee has applied \"since the December 2024 session\" - a useful " +
                    "reminder that fees are set per host institution and do change between cycles, so always " +
                    "confirm the live amount on your specific host's own page before paying, rather than " +
                    "relying on any figure here, including this one.",
            },
            {
                text:
                    "Payment, at least for every Spain host documented here, is by bank transfer to an account " +
                    "specific to that one host - not a card payment, and not a single jlpt.jp-wide system. " +
                    "Since Portugal and Spain are both in the eurozone, a transfer from Portugal is normally a " +
                    "standard SEPA transfer, with no currency conversion.",
            },
            {
                text:
                    "A real illustration of why \"always confirm live\" matters: ahead of the December 2026 " +
                    "exam, Valencia's host changed to a new organizing association with a brand-new bank " +
                    "account, and the official notice explicitly says they won't be responsible for payments " +
                    "sent to the old one. Get your bank details from the live page for your actual exam date - " +
                    "not an old instructions document, and not this post, months or years from now.",
            },
            {
                list: [
                    "Pay the full fee in a single transfer - don't combine payment for multiple applicants",
                    "Put the candidate's level and full name in the transfer reference, exactly as instructed by that host",
                    "Keep the digital payment receipt - you'll upload it as part of the application",
                    "Each host's bank account is specific to that host - a payment sent to the wrong account, or the wrong host, generally can't be recovered or transferred",
                    "No refunds once you've registered, for any reason, at any of the Spain host institutions documented here",
                ],
            },
            { heading: "Step 6: Confirmation and your Test Voucher" },
            {
                text:
                    "Right after you submit, you'll get an automatic email - but per the official instructions, " +
                    "that only confirms your submission was received, not that you're enrolled. Hosts check " +
                    "every application after the registration window closes, then send the real confirmation " +
                    "together with your Test Voucher (the document that doubles as your ID at the exam) by " +
                    "email - typically around a month after the window closes: roughly early June for a July " +
                    "sitting, early November for a December one, per the current Spain-wide schedule. You need " +
                    "to print the Test Voucher yourself before test day; a phone screen isn't accepted.",
            },
            { heading: "Step 7: What to bring on test day" },
            {
                list: [
                    "Your printed Test Voucher - a phone screenshot isn't accepted",
                    "The same photo ID you registered with - realistically your passport, for a Portugal-based applicant without Spanish ID",
                    "An HB or No. 2 pencil and an eraser",
                    "Nothing else electronic - no phone, smartwatch, or any device with a camera or communication function, not even during breaks",
                ],
            },
            {
                text:
                    "Arrive early: under the policy in force since the July 2026 sitting, candidates who arrive " +
                    "late to the admission period, or after a section has started, aren't allowed to sit that " +
                    "section at all.",
            },
            { heading: "If you need accommodations" },
            {
                parts: [
                    "The JLPT's official ",
                    { href: "https://www.jlpt.jp/e/application/sta.html", label: "Special Testing Accommodations (STA)" },
                    " process covers things like Braille or large-print papers, extra time, a separate room, or " +
                        "permission to bring a necessary medical device (electronic devices are otherwise " +
                        "banned). It's handled by your host institution, not centrally - request the form right " +
                        "after registering, and submit it with supporting documentation (such as a medical " +
                        "certificate) by the deadline your specific host sets.",
                ],
            },
            { heading: "Nearby-country alternatives, since Portugal has none right now" },
            {
                parts: [
                    "To repeat what's already established on this site rather than re-argue it: Portugal has " +
                        "no active JLPT host right now, and Spain is the nearest country with several real, " +
                        "active ones. For the current, always-up-to-date list, use ",
                    { to: "/jlpt-test-sites", label: "NihongoTech's JLPT test site search" },
                    ", which pulls from the same official directory and auto-suggests Spain if you search " +
                        "Portugal.",
                ],
            },
            { heading: "What we could not verify" },
            {
                text:
                    "In the interest of not stating things we didn't actually confirm: we could not " +
                    "independently verify the registration window, fee, or online-vs-paper process for " +
                    "Granada's host (Centro de Lenguas Modernas, Universidad de Granada) or for Universidad " +
                    "Complutense de Madrid, since neither appears to register through the jlpt.es portal " +
                    "documented in detail above. If one of these is your actual host, confirm the process " +
                    "directly on that institution's own site.",
            },
            {
                text:
                    "We also could not confirm whether Santiago de Compostela, which appears as an inactive " +
                    "navigation item on jlpt.es, is or will become an active JLPT host. It isn't currently " +
                    "listed on the official overseas directory or on NihongoTech's own JLPT test site search, " +
                    "so treat it as unconfirmed until it appears there.",
            },
            {
                text:
                    "Finally: fees, windows and even which organization runs a given city's exam can all change " +
                    "between cycles, as the Valencia example above shows. Every specific figure and date in " +
                    "this guide was confirmed directly against the official sources below on October 9, 2026 - " +
                    "check the live page for your actual exam before paying anything.",
            },
            {
                parts: [
                    "Official sources used for this guide: the JLPT overseas test site directory ( ",
                    { href: JLPT_OVERSEAS_DIRECTORY_URL, label: "desktop" },
                    " · ",
                    { href: JLPT_OVERSEAS_MOBILE_DIRECTORY_URL, label: "mobile" },
                    " ), the official ",
                    { href: "https://www.jlpt.jp/e/about/levelsummary.html", label: "level summary" },
                    ", the official ",
                    { href: "https://www.jlpt.jp/e/application/sta.html", label: "Special Testing Accommodations" },
                    " page, and the Spain registration portal run by the Japan Foundation's Madrid branch, ",
                    { href: "https://www.jlpt.es/", label: "jlpt.es" },
                    ".",
                ],
            },
            {
                parts: [
                    "Ready to start studying for whichever level you choose? Follow our ",
                    {
                        to: "/blog/jlpt-study-plan-no-portugal-test-site",
                        label: "full self-study plan for Portugal-based learners",
                    },
                    ", or jump straight into ",
                    { to: "/lessons", label: "lessons" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "como-inscrever-se-no-jlpt-em-portugal",
        title: "Como Inscrever-se no JLPT a Partir de Portugal: Guia Passo a Passo",
        date: "2026-10-09",
        lang: "pt-PT",
        excerpt:
            "Portugal não tem atualmente um local de exame JLPT ativo, por isso inscrever-se significa " +
            "candidatar-se diretamente através de uma instituição anfitriã no estrangeiro. Eis o processo " +
            "completo, do início ao fim: escolher o nível, encontrar a instituição certa, candidatar-se, pagar, " +
            "confirmação e o que levar no dia do exame.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["JLPT", "Portugal", "inscrição JLPT Portugal", "como inscrever-se no JLPT", "Espanha", "inscrição"],
        content: [
            {
                parts: [
                    "Portugal não tem atualmente nenhum local de exame JLPT ativo - explicamos porquê, e qual é " +
                        "o calendário, no nosso ",
                    {
                        to: "/pt/blog/jlpt-europe-countries-with-no-test-site",
                        label: "artigo sobre os países europeus que atualmente não têm nenhum",
                    },
                    ". Isto significa que, se está em Portugal e quer inscrever-se no JLPT, \"inscrever-se\" " +
                        "significa, na prática, candidatar-se através de uma instituição anfitriã noutro país. " +
                        "Este guia percorre todo esse processo, do início ao fim, usando Espanha - a " +
                        "alternativa real mais próxima - como exemplo prático, e explica o que muda se acabar " +
                        "por se inscrever noutro sítio.",
                ],
            },
            { heading: "Não existe um único site de inscrição do JLPT" },
            {
                text:
                    "Esta é a parte que confunde quase toda a gente à primeira: o jlpt.jp não gere um sistema " +
                    "central único onde se inscreve para o exame. Cada local de exame fora do Japão é " +
                    "organizado por uma instituição anfitriã local - normalmente uma universidade ou uma " +
                    "associação de língua japonesa - e a candidatura faz-se diretamente no site dessa " +
                    "instituição, com o formulário, a conta bancária para a taxa e os prazos próprios dela. O " +
                    "próprio diretório oficial no estrangeiro é explícito quanto a isto: para os detalhes de " +
                    "inscrição, remete para \"contactar a sua instituição anfitriã local\", em vez de descrever " +
                    "um processo único e partilhado.",
            },
            {
                text:
                    "Isto é verdade mesmo dentro de um único país. Em Espanha, quatro das seis instituições " +
                    "anfitriãs atualmente listadas partilham um único portal de inscrição, gerido pela " +
                    "delegação da Fundação Japão em Madrid - mas a instituição de Granada faz a sua própria " +
                    "inscrição separada, no site da própria universidade, sem ligação a esse portal partilhado. " +
                    "Não há um atalho que funcione para \"o seu país\" em geral - tem de verificar a " +
                    "instituição anfitriã específica à qual se está a candidatar, sempre.",
            },
            { heading: "Passo 1: Escolha o seu nível (de N5 a N1)" },
            {
                text:
                    "O JLPT tem cinco níveis, de N5 (mais fácil) a N1 (mais difícil). Segundo o resumo oficial " +
                    "dos níveis, N5 e N4 cobrem japonês básico - ler hiragana, katakana e kanji comuns, " +
                    "acompanhar conversas simples e lentas. O N3 faz a ponte entre o básico e o avançado: " +
                    "leitura do dia a dia, a ideia geral de material um pouco mais difícil, e conversas a um " +
                    "ritmo mais próximo do natural. N2 e N1 cobrem japonês do dia a dia e cada vez mais variado " +
                    "e complexo - artigos de jornal e textos abstratos, conversas e palestras a ritmo natural. " +
                    "Escolha com base no que já estudou de facto, não em como gostaria de ser visto/a - cada " +
                    "parte da prova tem a sua própria nota mínima, e chumbar numa única parte chumba o nível " +
                    "inteiro, por melhor que seja a nota geral.",
            },
            {
                table: {
                    caption: "Os cinco níveis do JLPT, do mais fácil ao mais difícil",
                    headers: ["Nível", "O que avalia", "Partes e duração da prova"],
                    rows: [
                        [
                            "N5",
                            "Japonês básico: hiragana, katakana e frases com kanji comuns; conversas simples e lentas",
                            "3 partes - Vocabulário 20 min, Gramática/Leitura 40 min, Compreensão auditiva 30 min",
                        ],
                        [
                            "N4",
                            "Japonês básico de sala de aula: temas do dia a dia com vocabulário e kanji simples; conversas lentas",
                            "3 partes - Vocabulário 25 min, Gramática/Leitura 55 min, Compreensão auditiva 35 min",
                        ],
                        [
                            "N3",
                            "Uma ponte para o japonês avançado: textos do dia a dia, a ideia geral de material mais difícil, conversas a ritmo quase natural",
                            "3 partes - Vocabulário 30 min, Gramática/Leitura 70 min, Compreensão auditiva 40 min",
                        ],
                        [
                            "N2",
                            "Japonês do dia a dia e de situações variadas: artigos de jornal e revista, conversas e notícias a ritmo quase natural",
                            "2 partes - Conhecimento da Língua/Leitura 105 min, Compreensão auditiva 50 min",
                        ],
                        [
                            "N1",
                            "Um largo leque de japonês: textos complexos ou abstratos, conversas e palestras a ritmo natural",
                            "2 partes - Conhecimento da Língua/Leitura 110 min, Compreensão auditiva 55 min",
                        ],
                    ],
                },
            },
            {
                parts: [
                    "Prepare-se para o nível que escolher com a ",
                    { to: "/pt/conjugation", label: "prática de conjugação de verbos" },
                    ", a ",
                    { to: "/pt/grammar", label: "referência de gramática" },
                    " e as ",
                    { to: "/pt/lessons", label: "lições" },
                    " do próprio NihongoTech - ou, se está a estudar a partir de Portugal especificamente, siga " +
                        "o ",
                    {
                        to: "/blog/plano-estudo-jlpt-portugal-sem-exame-no-pais",
                        label: "plano de estudo completo que preparámos exatamente para esta situação",
                    },
                    ".",
                ],
            },
            { heading: "Passo 2: Descubra onde pode realmente inscrever-se" },
            {
                parts: [
                    "Como não há um portal central, a primeira tarefa real é encontrar a sua instituição " +
                        "anfitriã. Comece pelo diretório oficial de locais de exame do JLPT no estrangeiro ( ",
                    { href: JLPT_OVERSEAS_DIRECTORY_URL, label: "desktop" },
                    " · ",
                    { href: JLPT_OVERSEAS_MOBILE_DIRECTORY_URL, label: "mobile" },
                    " ), ou use a própria ",
                    { to: "/pt/jlpt-test-sites", label: "pesquisa de locais de exame JLPT" },
                    " do NihongoTech, que espelha essa mesma lista oficial e sugere automaticamente a " +
                        "alternativa mais próxima se o seu país não tiver nenhuma.",
                ],
            },
            {
                text:
                    "Como já estabelecido neste site: Portugal está atualmente listado sem nenhuma instituição " +
                    "anfitriã ativa. Espanha é o país mais próximo com várias instituições reais e ativas.",
            },
            {
                list: [
                    "Barcelona - Universitat Autònoma de Barcelona (julho e dezembro)",
                    "Madrid - Universidad Complutense de Madrid (apenas julho)",
                    "Madrid - Universidad Autónoma de Madrid, UAM (apenas dezembro) - uma instituição diferente da Complutense, apesar de ser a mesma cidade",
                    "Granada - Centro de Lenguas Modernas, Universidad de Granada (apenas julho)",
                    "Valência - Asociación Cultural KOTOVA (julho e dezembro)",
                    "Las Palmas de Gran Canaria - Universidad de Las Palmas de Gran Canaria (apenas dezembro)",
                ],
            },
            {
                text:
                    "As sessões que cada instituição realiza de facto podem mudar de ciclo para ciclo, por isso " +
                    "confirme sempre a disponibilidade atual no diretório oficial ou na pesquisa acima antes de " +
                    "fazer planos.",
            },
            { heading: "Passo 3: Vá diretamente ao site dessa instituição" },
            {
                parts: [
                    "Depois de identificar a sua instituição anfitriã, a inscrição faz-se no site dela - não " +
                        "numa página genérica do JLPT. Para quatro das seis instituições espanholas acima " +
                        "(Barcelona, Las Palmas de Gran Canaria, Madrid/UAM e Valência), esse site partilhado é " +
                        "o ",
                    { href: "https://www.jlpt.es/", label: "jlpt.es" },
                    ", gerido pela delegação da Fundação Japão em Madrid. A instituição de Granada inscreve-se " +
                        "separadamente, no site da própria universidade.",
                ],
            },
            {
                text:
                    "Madrid é um bom exemplo de porque não deve presumir nada: tem duas instituições anfitriãs " +
                    "separadas para duas sessões diferentes (Universidad Complutense de Madrid em julho, " +
                    "Universidad Autónoma de Madrid em dezembro), e só uma delas - a UAM - se inscreve " +
                    "atualmente através do portal partilhado jlpt.es.",
            },
            {
                parts: [
                    "Vale também a pena confirmar que chegou à página certa para o fim certo: a própria página " +
                        "do departamento de estudos japoneses de Barcelona, por exemplo, atualmente ",
                    {
                        href: "https://webs.uab.cat/oficinasakura/es/noken/",
                        label: "indica que não presta apoio direto ao exame JLPT",
                    },
                    " e remete os candidatos para o sistema partilhado jlpt.es - um lembrete para verificar a " +
                        "página de inscrição atual e real da sua instituição, e não apenas a primeira página " +
                        "com ar oficial que encontrar.",
                ],
            },
            { heading: "Passo 4: Períodos de inscrição" },
            {
                text:
                    "Os períodos de inscrição são definidos por cada instituição anfitriã, não pelo jlpt.jp " +
                    "globalmente - são curtos, não se repetem, e não esperam que se sinta pronto/a.",
            },
            {
                text:
                    "Um exemplo concreto e atual: para a sessão de 6 de dezembro de 2026, o portal espanhol " +
                    "jlpt.es teve o seu período de inscrição de 1 a 30 de setembro de 2026 - cerca de dois " +
                    "meses antes do exame. Verificámos isto diretamente no jlpt.es a 9 de outubro de 2026, " +
                    "altura em que esse período já tinha fechado; espera-se que a próxima sessão (julho de " +
                    "2027) abra as inscrições por volta de março de 2027, segundo o anúncio dessa própria " +
                    "instituição - mas isto é específico dessa instituição e não está garantido, por isso " +
                    "confirme diretamente antes de planear em função disso.",
            },
            {
                text:
                    "As instituições espanholas também avisam que os lugares são limitados por nível e por " +
                    "instituição, e que uma instituição pode fechar as inscrições mais cedo assim que esgotar " +
                    "os lugares, mesmo antes do prazo oficial. Candidate-se assim que o período abrir, em vez " +
                    "de esperar.",
            },
            { heading: "Passo 5: A candidatura em si, passo a passo" },
            {
                text:
                    "Tenha duas coisas prontas antes de começar: uma fotografia digital tipo passe, e uma " +
                    "cópia digital (digitalização ou fotografia nítida) do comprovativo de pagamento (ver " +
                    "Taxas e pagamento, abaixo) - a maioria dos sites das instituições não deixa avançar no " +
                    "formulário sem as duas.",
            },
            {
                list: [
                    "Escolha primeiro o local/instituição - depois de selecionado (e pago), não pode ser alterado, e a candidatura só é válida para essa instituição",
                    "Dados pessoais - o nível, o número do documento de identificação que vai levar no dia do exame, o nome completo tal como deve aparecer nos resultados e no certificado, data de nascimento, nacionalidade, língua materna, morada postal (para onde são enviados os resultados em papel), número de telefone, e um endereço de email que passa a ser o principal canal de contacto até ao dia do exame",
                    "Defina a sua própria palavra-passe (em Espanha: exatamente 8 dígitos, só números) para consultar os resultados online mais tarde, e depois carregue a fotografia tipo passe e o comprovativo de pagamento",
                    "Um pequeno questionário sobre o seu percurso - onde e como estuda japonês, porque faz o exame, e quantas vezes (se alguma) já fez cada nível antes - seguido de uma caixa de confirmação final antes de submeter",
                ],
            },
            {
                text:
                    "Um detalhe concreto, específico desta instituição, que vale a pena saber com antecedência: " +
                    "o formulário de inscrição de Espanha não aceita certos caracteres acentuados, incluindo ñ " +
                    "e ç, por isso um nome português pode precisar de uma grafia substituída nesse formulário " +
                    "em concreto. Regras como esta variam de instituição para instituição - verifique o " +
                    "formulário real da sua, em vez de presumir.",
            },
            { heading: "Taxas e pagamento" },
            {
                text:
                    "Taxa, verificada diretamente: 75 euros para todos os níveis (de N5 a N1), confirmada nas " +
                    "páginas de inscrição oficiais das quatro instituições espanholas que partilham o portal " +
                    "jlpt.es (Barcelona, Las Palmas de Gran Canaria, Madrid/UAM e Valência), em outubro de " +
                    "2026. O próprio jlpt.es refere que esta taxa se aplica \"desde a convocatória de dezembro " +
                    "de 2024\" - um lembrete útil de que as taxas são definidas por cada instituição e mudam de " +
                    "ciclo para ciclo, por isso confirme sempre o valor atual na página da sua instituição " +
                    "antes de pagar, em vez de confiar em qualquer valor aqui indicado, incluindo este.",
            },
            {
                text:
                    "O pagamento, pelo menos em todas as instituições espanholas aqui documentadas, é feito por " +
                    "transferência bancária para uma conta específica dessa instituição - não por cartão, nem " +
                    "através de um único sistema do jlpt.jp. Como Portugal e Espanha estão ambos na zona euro, " +
                    "uma transferência a partir de Portugal é normalmente uma transferência SEPA normal, sem " +
                    "conversão de moeda.",
            },
            {
                text:
                    "Um exemplo real de porque \"confirme sempre em tempo real\" importa: antes do exame de " +
                    "dezembro de 2026, a instituição de Valência mudou para uma nova associação organizadora, " +
                    "com uma conta bancária totalmente nova, e o aviso oficial diz explicitamente que não se " +
                    "responsabilizam por pagamentos enviados para a conta antiga. Obtenha os dados bancários na " +
                    "página em tempo real para a sua data de exame real - não de um documento de instruções " +
                    "antigo, nem deste artigo, daqui a meses ou anos.",
            },
            {
                list: [
                    "Pague a taxa completa numa única transferência - não combine o pagamento de vários candidatos",
                    "Indique o nível e o nome completo do candidato na referência da transferência, exatamente como pedido por essa instituição",
                    "Guarde o comprovativo de pagamento digital - vai precisar de o carregar como parte da candidatura",
                    "A conta bancária de cada instituição é específica dessa instituição - um pagamento enviado para a conta errada, ou para a instituição errada, geralmente não pode ser recuperado nem transferido",
                    "Não há reembolso depois de concluída a inscrição, seja qual for o motivo, em nenhuma das instituições espanholas aqui documentadas",
                ],
            },
            { heading: "Passo 6: Confirmação e o Test Voucher" },
            {
                text:
                    "Logo após submeter, recebe um email automático - mas, segundo as instruções oficiais, " +
                    "isso confirma apenas que a candidatura foi recebida, não que está inscrito/a. As " +
                    "instituições verificam todas as candidaturas depois de o período de inscrição fechar, e " +
                    "só depois enviam a confirmação real, junto com o Test Voucher (o documento que também " +
                    "serve de identificação no exame), por email - normalmente cerca de um mês depois de o " +
                    "período fechar: por volta do início de junho para uma sessão de julho, início de novembro " +
                    "para uma de dezembro, segundo o calendário espanhol atual. Tem de imprimir o Test Voucher " +
                    "antes do dia do exame; o ecrã do telemóvel não é aceite.",
            },
            { heading: "Passo 7: O que levar no dia do exame" },
            {
                list: [
                    "O Test Voucher impresso - uma captura de ecrã do telemóvel não é aceite",
                    "O mesmo documento de identificação com que se inscreveu - na prática, o passaporte, para quem se inscreve a partir de Portugal sem documento de identificação espanhol",
                    "Um lápis HB ou n.º 2 e uma borracha",
                    "Mais nenhum dispositivo eletrónico - nada de telemóvel, smartwatch, ou qualquer aparelho com câmara ou função de comunicação, nem sequer nos intervalos",
                ],
            },
            {
                text:
                    "Chegue com antecedência: segundo a política em vigor desde a sessão de julho de 2026, os " +
                    "candidatos que cheguem atrasados ao período de admissão, ou depois de uma parte da prova " +
                    "já ter começado, não podem fazer essa parte.",
            },
            { heading: "Se precisar de adaptações" },
            {
                parts: [
                    "O processo oficial de ",
                    { href: "https://www.jlpt.jp/e/application/sta.html", label: "Adaptações Especiais de Exame (STA)" },
                    " do JLPT cobre situações como provas em Braille ou letra ampliada, tempo extra, uma sala " +
                        "separada, ou autorização para levar um dispositivo médico necessário (os dispositivos " +
                        "eletrónicos estão proibidos nos outros casos). É tratado pela sua instituição " +
                        "anfitriã, não de forma centralizada - peça o formulário logo depois de se inscrever, e " +
                        "submeta-o com a documentação de apoio (como um atestado médico) até ao prazo definido " +
                        "pela sua instituição.",
                ],
            },
            { heading: "Alternativas nos países vizinhos, já que Portugal não tem nenhum de momento" },
            {
                parts: [
                    "Para repetir o que já está estabelecido neste site, em vez de o questionar de novo: " +
                        "Portugal não tem atualmente nenhuma instituição anfitriã do JLPT ativa, e Espanha é o " +
                        "país mais próximo com várias instituições reais e ativas. Para a lista atual, sempre " +
                        "atualizada, use a ",
                    { to: "/pt/jlpt-test-sites", label: "pesquisa de locais de exame JLPT do NihongoTech" },
                    ", que usa o mesmo diretório oficial e sugere automaticamente Espanha se pesquisar por " +
                        "Portugal.",
                ],
            },
            { heading: "O que não conseguimos confirmar" },
            {
                text:
                    "Para não afirmar coisas que não confirmámos de facto: não conseguimos verificar de forma " +
                    "independente o período de inscrição, a taxa, nem se o processo é online ou em papel, para " +
                    "a instituição de Granada (Centro de Lenguas Modernas, Universidad de Granada) ou para a " +
                    "Universidad Complutense de Madrid, já que nenhuma das duas parece inscrever-se através do " +
                    "portal jlpt.es documentado em detalhe acima. Se uma destas for a sua instituição real, " +
                    "confirme o processo diretamente no site dessa instituição.",
            },
            {
                text:
                    "Também não conseguimos confirmar se Santiago de Compostela, que aparece como um item de " +
                    "navegação inativo no jlpt.es, é ou virá a ser uma instituição anfitriã ativa do JLPT. Não " +
                    "está atualmente listada no diretório oficial no estrangeiro nem na pesquisa de locais de " +
                    "exame JLPT do próprio NihongoTech, por isso trate-a como não confirmada até aparecer aí.",
            },
            {
                text:
                    "Por fim: as taxas, os períodos de inscrição e até a organização que gere o exame de uma " +
                    "determinada cidade podem mudar de ciclo para ciclo, como mostra o exemplo de Valência " +
                    "acima. Todos os valores e datas concretos deste guia foram confirmados diretamente junto " +
                    "das fontes oficiais abaixo a 9 de outubro de 2026 - confirme a página em tempo real para " +
                    "o seu exame real antes de pagar seja o que for.",
            },
            {
                parts: [
                    "Fontes oficiais usadas neste guia: o diretório de locais de exame JLPT no estrangeiro ( ",
                    { href: JLPT_OVERSEAS_DIRECTORY_URL, label: "desktop" },
                    " · ",
                    { href: JLPT_OVERSEAS_MOBILE_DIRECTORY_URL, label: "mobile" },
                    " ), o ",
                    { href: "https://www.jlpt.jp/e/about/levelsummary.html", label: "resumo oficial dos níveis" },
                    ", a página oficial de ",
                    { href: "https://www.jlpt.jp/e/application/sta.html", label: "Adaptações Especiais de Exame" },
                    ", e o portal de inscrição para Espanha gerido pela delegação da Fundação Japão em Madrid, " +
                        "o ",
                    { href: "https://www.jlpt.es/", label: "jlpt.es" },
                    ".",
                ],
            },
            {
                parts: [
                    "Pronto/a para começar a estudar para o nível que escolher? Siga o nosso ",
                    {
                        to: "/blog/plano-estudo-jlpt-portugal-sem-exame-no-pais",
                        label: "plano de estudo completo para quem aprende a partir de Portugal",
                    },
                    ", ou avance diretamente para as ",
                    { to: "/pt/lessons", label: "lições" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "romanization-systems",
        title: "What Is Romanization? A Beginner's Guide to Rōmaji, Pinyin, Korean Romanization and More",
        date: "2026-09-27",
        excerpt:
            "Rōmaji, Pinyin, Jyutping, Revised Romanization of Korean - different names for the same basic idea. " +
            "Here's what romanization actually means, and why so many systems exist for representing Asian " +
            "languages in the Latin alphabet.",
        tags: ["Rōmaji", "Beginner"],
        keywords: ["romanization", "rōmaji", "Pinyin", "Korean", "Hepburn"],
        content: [
            {
                text:
                    "Romanization is the process of writing a language that normally uses a different script - " +
                    "Japanese, Chinese, Korean, and many others - using the Latin alphabet instead. 日本語 becomes " +
                    "nihongo, 你好 becomes nǐ hǎo, 안녕하세요 becomes annyeonghaseyo. None of these are translations - " +
                    "the words still mean exactly what they meant before. Romanization only changes how the sounds " +
                    "are written down, not what they mean.",
            },
            {
                text:
                    "Languages adopt romanization systems for a few practical reasons: to make the language " +
                    "readable to people who don't know the native script, to allow typing on a standard keyboard, " +
                    "to standardize how names and places appear on maps and passports internationally, and to " +
                    "give learners a bridge into the language before they've mastered its actual writing system.",
            },
            { heading: "Rōmaji: Japanese written in the Latin alphabet" },
            {
                parts: [
                    "In Japanese, this romanized form is called rōmaji (ローマ字, literally \"Roman letters\"). The " +
                    "system you'll see by far the most - on street signs, in textbooks, and in this site's own ",
                    { to: "/keyboard", label: "romaji-input keyboard" },
                    " - is Hepburn romanization, developed in the 19th century by James Curtis Hepburn. Hepburn is " +
                    "designed to match how the sounds are actually pronounced by an English speaker, which is " +
                    "exactly why it's the standard for learners and for most English-language material about Japan.",
                ],
            },
            {
                text:
                    "Two other systems exist too, though you'll run into them far less often. Kunrei-shiki is the " +
                    "system officially prescribed by the Japanese government and taught in Japanese elementary " +
                    "schools; it follows the logic of the kana grid more strictly than pronunciation. Nihon-shiki " +
                    "is an older, even more grid-consistent system that Kunrei-shiki was based on. Both exist " +
                    "mainly for internal Japanese use and linguistic consistency, not for helping foreign learners " +
                    "read the language aloud - which is exactly why Hepburn remains the practical choice outside Japan.",
            },
            { heading: "The same kana, three different spellings" },
            {
                text:
                    "The clearest way to see the difference between these systems is a single character: し. In " +
                    "Hepburn, it's written shi, matching how it actually sounds to an English speaker. In Kunrei-" +
                    "shiki and Nihon-shiki, the same character is written si, because it sits in the \"s\" row of " +
                    "the kana grid alongside さ (sa), す (su), せ (se), そ (so) - internally consistent, but not a " +
                    "reliable guide to pronunciation for someone who doesn't already know the grid.",
            },
            {
                list: [
                    "ち is chi in Hepburn (again, matching pronunciation)",
                    "つ is tsu in Hepburn - both reflect real consonant sounds English speakers already recognize",
                    "ふ is fu in Hepburn, close to the actual sound, which sits between an English \"f\" and \"h\"",
                ],
            },
            {
                text:
                    "This is exactly why multiple romanization systems can exist for one language at all: each one " +
                    "optimizes for something different. Hepburn optimizes for pronunciation by outsiders. Kunrei-" +
                    "shiki optimizes for a clean, one-to-one match with the underlying kana grid. Neither is " +
                    "\"wrong\" - they're just built for different jobs.",
            },
            { heading: "Long vowels and macrons" },
            {
                text:
                    "Japanese distinguishes between short and long vowels, and a long vowel is a genuinely " +
                    "different sound, not just an emphasis - おばさん (obasan, \"aunt\") and おばあさん (obāsan, " +
                    "\"grandmother\") are different words. In Hepburn romanization, a long vowel is usually marked " +
                    "with a macron, the small line over the letter: ā, ī, ū, ē, ō. That's why you'll see Japan's " +
                    "capital written Tōkyō rather than Tokyo, and ありがとう written arigatō rather than arigatou - " +
                    "the macron over the final o represents the long う sound that follows it. Dropped macrons are " +
                    "extremely common in casual English writing, but they're not just decoration - they're part of " +
                    "accurately representing the word.",
            },
            { heading: "Rōmaji is a tool, not a replacement" },
            {
                parts: [
                    "It's worth being direct about this: rōmaji is not one of Japanese's actual writing systems. " +
                    "Real Japanese text is written in a mix of hiragana, katakana, and kanji - never in rōmaji, " +
                    "outside of learning material, signage aimed at tourists, or logos. If you're serious about " +
                    "reading Japanese, rōmaji is a stepping stone to lean on early and then largely leave behind, " +
                    "not a long-term substitute for actually learning ",
                    { to: "/lessons", label: "hiragana and katakana" },
                    ". Every native speaker reads and writes in kana and kanji, never in Latin letters.",
                ],
            },
            {
                text:
                    "That doesn't mean rōmaji is useless - far from it. It's genuinely helpful for typing Japanese " +
                    "on a regular keyboard, for looking up pronunciation before you can read kana fluently, and " +
                    "for following along with early lessons. The goal is just to treat it as scaffolding you " +
                    "eventually take away, the same way training wheels come off a bicycle.",
            },
            { heading: "Beyond Japanese: Mandarin Pinyin" },
            {
                text:
                    "Mandarin Chinese has its own widely used romanization system, Hanyu Pinyin (usually just " +
                    "called Pinyin), developed in mainland China and now the international standard. 你好 (\"hello\") " +
                    "becomes nǐ hǎo, and 中国 (\"China\") becomes Zhōngguó. Notice the small marks above the vowels " +
                    "in nǐ and Zhōngguó - those are tone marks, not the same thing as Japanese's long-vowel " +
                    "macrons. Mandarin is a tonal language, so Pinyin has to encode which of the four tones a " +
                    "syllable is spoken with, since the same sequence of letters can mean entirely different " +
                    "things depending on tone.",
            },
            { heading: "Cantonese Jyutping" },
            {
                text:
                    "Cantonese, a different Chinese language spoken mainly in Hong Kong, Macau, and Guangdong, " +
                    "isn't romanized with Pinyin - Pinyin was built specifically for Mandarin's sounds and tones, " +
                    "which don't match Cantonese's. Instead, one common system is Jyutping, developed by the " +
                    "Linguistic Society of Hong Kong. Like Pinyin, it marks tone, but Cantonese has more tones " +
                    "than Mandarin, so Jyutping represents them with numbers after each syllable rather than " +
                    "accent marks.",
            },
            { heading: "Revised Romanization of Korean" },
            {
                text:
                    "Korean is written in its own script, Hangul, not in Chinese characters or kana - but it has " +
                    "its own romanization system too, for exactly the same reasons Japanese and Chinese do. The " +
                    "current official system in South Korea is the Revised Romanization of Korean, introduced in " +
                    "2000. Under this system, 한국어 (\"Korean language\") becomes hangugeo, and 안녕하세요 (a common " +
                    "greeting) becomes annyeonghaseyo. An older system, McCune-Reischauer, is still used in some " +
                    "academic and North Korean contexts, and the two disagree on some spellings - another " +
                    "concrete example of a language having more than one active romanization standard at once.",
            },
            {
                text:
                    "This same pattern shows up across most languages that don't use the Latin alphabet natively - " +
                    "Thai, Arabic, Russian's Cyrillic script, and many others each have their own established " +
                    "romanization conventions, usually for the same mix of reasons: helping outsiders pronounce " +
                    "the language, enabling standard-keyboard typing, and giving international systems like maps " +
                    "and passports a consistent way to render names.",
            },
            { heading: "Romanization vs. transliteration vs. transcription" },
            {
                text:
                    "These three words get used loosely and interchangeably, but they describe distinct ideas. " +
                    "Romanization specifically means converting a non-Latin script into the Latin alphabet - it's " +
                    "the broad category everything above falls under. Transliteration is the wider concept: " +
                    "swapping characters from any script into another script's characters, generally aiming to " +
                    "preserve the original spelling as closely as possible, letter for letter, even if that makes " +
                    "pronunciation a little less obvious. Transcription instead prioritizes capturing how a word " +
                    "actually sounds, using whatever symbols represent those sounds most clearly - a linguist " +
                    "writing a word out in the International Phonetic Alphabet (IPA) is transcribing it, not " +
                    "transliterating it. Hepburn rōmaji sits closer to transcription in spirit, since it's built " +
                    "around matching real pronunciation, while Kunrei-shiki sits closer to transliteration, since " +
                    "it prioritizes a consistent, predictable mapping from the kana grid over exact pronunciation.",
            },
            { heading: "The bottom line for Japanese learners" },
            {
                parts: [
                    "Use rōmaji freely as a beginner - to type, to sound words out, to read this very sentence " +
                    "aloud without knowing kana yet. But don't let it become a crutch you never put down. Start " +
                    "learning ",
                    { to: "/lessons", label: "hiragana and katakana" },
                    " as early as possible, and practice ",
                    { to: "/kana-writing-practice", label: "writing them by hand" },
                    " so the actual script becomes as automatic as the Latin alphabet already is for you. Rōmaji " +
                    "got you in the door - real reading happens in kana and kanji.",
                ],
            },
        ],
    },
    {
        slug: "hiragana-katakana-writing-practice-sheets",
        ptSlug: "fichas-hiragana-katakana-para-imprimir",
        title: "Hiragana and Katakana Writing Practice Sheets: Build Your Own (Free, A4, With Stroke Order)",
        date: "2026-09-28",
        excerpt:
            "Choose the exact kana you want to practice, pick how much tracing help you get, and print an A4 " +
            "sheet with stroke order on every row - or write on the sheet on screen if you don't have a printer.",
        tags: ["Kana", "Tools"],
        keywords: ["hiragana", "katakana", "worksheets", "handwriting", "printable"],
        content: [
            {
                text:
                    "If you've decided to write hiragana and katakana by hand, the next question is what to " +
                    "actually put in front of you. A page of the same character printed forty times isn't much " +
                    "help if you can't see how the strokes are ordered, and a fixed PDF can't adapt to the rows " +
                    "you're working on this week. This post walks through building a sheet that does, using the " +
                    "free worksheet generator here.",
            },
            { heading: "Why write kana by hand at all?" },
            {
                parts: [
                    "Recognizing a character when you see it and producing it from memory are two different " +
                    "skills, and only the second one asks you to remember the shape, the proportions and the " +
                    "stroke order. Typing is a perfectly good way to use Japanese day to day, but a few minutes " +
                    "of handwriting per session gives the shapes something extra to hold on to. It's one of the " +
                    "tips in our post on ",
                    { to: "/blog/tips-to-memorize-kana-faster", label: "memorizing hiragana and katakana faster" },
                    ", and this is the longer version of it.",
                ],
            },
            { heading: "What a useful practice sheet has" },
            {
                list: [
                    "A model character at the start of each row, so you always have something to compare against",
                    "A stroke-order strip showing the character being built one stroke at a time",
                    "Squares big enough to write in properly, with optional guide lines through the middle of each one",
                    "Enough squares per character to build a rhythm - you choose from 1 to 60 (rounded up to fill the last row), and 12 is the default",
                    "A way to take the help away gradually, so you're not tracing forever",
                ],
            },
            {
                text:
                    "That last point is what the three practice styles are for. Tracing puts a faded copy of the " +
                    "character in every square, which suits your very first pass at a new row. Guided starts with " +
                    "a darker faded copy and fades out to empty squares, so the first squares in a row are traced " +
                    "and the later ones you write from memory. Blank shows only the model character followed by " +
                    "empty squares, for when you already know the row and want to test yourself. Which one you " +
                    "pick is a matter of preference - Guided is the default.",
            },
            { heading: "Build your first sheet in about two minutes" },
            {
                list: [
                    "Open the worksheet generator and choose Hiragana. Every basic hiragana is selected at first, so press Clear.",
                    "Pick the row you're learning - the あ row (あ・い・う・え・お) is a natural first choice - or tap individual characters to add them.",
                    "Set how many practice squares you want per character and choose a practice style.",
                    "Leave stroke order and guide lines on if you want them, or switch them off for a cleaner page. Each kana row also shows its romaji.",
                    "Check the preview, then press Download PDF or Print.",
                ],
            },
            {
                parts: [
                    "Besides the 46 basic kana in each script, you can add the voiced forms written with dakuten " +
                    "and handakuten (が, ざ, ぱ and so on). For why those voiced sounds appear when words join, see " +
                    "our post on ",
                    { to: "/blog/what-is-rendaku-japanese-sequential-voicing", label: "rendaku" },
                    ". Combination sounds such as きゃ and しゅ, small kana (ゃ, ゅ, ょ, っ) and the " +
                    "long-vowel mark ー aren't included on the sheets.",
                ],
            },
            { heading: "Pick a gentle pace" },
            {
                text:
                    "A sheet holds at most ten characters per A4 page, and the squares get larger when there are " +
                    "fewer characters on a page. That makes one or two rows per session a reasonable starting " +
                    "point - a suggestion to adjust to your own schedule and energy, not a timeline. A short " +
                    "session you actually finish beats an ambitious stack of pages you don't.",
            },
            { heading: "Print it properly" },
            {
                text:
                    "The sheets are laid out for A4 paper, which is the standard in most of Europe. In the print " +
                    "dialog, choose A4 and print at 100 percent (\"actual size\") rather than \"fit to page\", so the " +
                    "squares stay the size they were designed to be. The worksheets are A4 only, so if you're on " +
                    "US Letter paper the margins may not come out the way you expect. The sheet also has Name and " +
                    "Date fields at the top, which you can ignore or use as you like.",
            },
            { heading: "No printer? Practice online" },
            {
                text:
                    "Next to the preview there's a Practice online option. It lets you draw on the sheet on screen " +
                    "with a mouse or your finger, zoom in up to 300 percent for more room, and clear the drawing " +
                    "to try again. To be clear about what it is: your drawing isn't saved and it isn't graded, so " +
                    "treat it as a scratchpad for getting the shapes into your hand rather than a checker.",
            },
            { heading: "Katakana next" },
            {
                parts: [
                    "When you're comfortable with hiragana, the process is the same for katakana - ",
                    { to: "/worksheets?script=katakana", label: "open the generator on katakana" },
                    " and pick a row, for example ア・イ・ウ・エ・オ. Since katakana reuses the sounds you already " +
                    "know, you're mostly learning new shapes. If you're not sure how the two scripts differ, our " +
                    "post on ",
                    { to: "/blog/hiragana-vs-katakana", label: "hiragana vs katakana" },
                    " covers it.",
                ],
            },
            { heading: "When the kana are done: N5 kanji" },
            {
                parts: [
                    "The same generator also makes ",
                    { to: "/worksheets?script=kanji", label: "kanji sheets" },
                    " for the JLPT N5 kanji as listed on NihongoTech, and each row can include the meaning and " +
                    "the on'yomi and kun'yomi readings next to the stroke order. To see how a kanji is drawn " +
                    "before you print it, the ",
                    { to: "/kanji-writing-practice", label: "kanji writing practice" },
                    " page animates it, and the ",
                    { to: "/kana-writing-practice", label: "kana writing practice" },
                    " page does the same for hiragana and katakana. If you'd like a structured path through both, " +
                    "the ",
                    { to: "/lessons", label: "lessons" },
                    " are the place to start.",
                ],
            },
            { heading: "Quick answers" },
            {
                list: [
                    "Is it free? Yes for all hiragana, katakana and JLPT N5 kanji, with every layout option. N4-N1 kanji, non-JLPT kanji and custom kanji lists are Premium, which can't be bought yet.",
                    "Which paper size? A4 only.",
                    "Where is the PDF made? In your browser, on your device - it isn't built on a server.",
                    "Does it work without a printer? Yes, through Practice online, although the drawing isn't saved or graded.",
                ],
            },
            {
                parts: [
                    "Ready to try it? ",
                    { to: "/worksheets?script=hiragana", label: "Make your first sheet" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "what-is-rendaku-japanese-sequential-voicing",
        ptSlug: "o-que-e-rendaku-sonorizacao-sequencial",
        title: "Rendaku (連濁) Explained: Why 小屋 (koya) Becomes ごや (goya) in 犬小屋",
        date: "2026-09-29",
        excerpt:
            "You know 小屋 (koya, hut), then 犬小屋 is read inugoya. What is rendaku, why do sounds change in " +
            "compound words, and why is it a tendency rather than a rule?",
        tags: ["Kanji", "Beginner"],
        keywords: ["rendaku", "sequential voicing", "dakuten", "compound words", "pronunciation"],
        content: [
            {
                text:
                    "You learned 小屋 (こや, koya) as \"hut\" or \"shed.\" Then you meet 犬小屋, \"doghouse,\" and it " +
                    "isn't いぬこや - it's いぬごや (inugoya). The こ turned into ご. That isn't a typo, and it isn't " +
                    "a one-off exception to memorize: it's rendaku (連濁, れんだく), and it's one of the most common " +
                    "reasons a word you already know sounds slightly different inside a longer word.",
            },
            {
                parts: [
                    "This post explains what rendaku is, the four sound changes behind it, and the honest caveat " +
                    "that it's a strong tendency, not a rule that works for every compound. It's the longer " +
                    "companion to the ",
                    { to: "/lessons", label: "rendaku lesson" },
                    " in the lessons section.",
                ],
            },
            { heading: "What is rendaku?" },
            {
                text:
                    "Rendaku is often called sequential voicing. In many compound words, the first sound of the " +
                    "second part is voiced: it takes the dakuten (゛) you met when learning hiragana. Only that one " +
                    "sound changes - the first part of the compound and the rest of the second part stay exactly as " +
                    "they were.",
            },
            { heading: "Why こや becomes ごや" },
            {
                parts: [
                    "On its own, 小屋 is こや (koya), \"hut\" or \"shed.\" It's built from ",
                    { to: "/kanji/小", label: "小" },
                    " (こ, \"small\") and ",
                    { to: "/kanji/屋", label: "屋" },
                    " (や, \"house, shop\"), the same 屋 as in 花屋 (はなや, hanaya, \"florist\"). Put another word " +
                    "in front of it and 小屋 becomes the second part of a compound, so its first sound こ (ko) is " +
                    "voiced to ご (go). Three you can try:",
                ],
            },
            {
                list: [
                    "犬小屋 (いぬごや, inugoya) - doghouse, kennel: いぬ + こや",
                    "山小屋 (やまごや, yamagoya) - mountain hut: やま + こや",
                    "鶏小屋 (にわとりごや, niwatorigoya) - chicken coop: にわとり + こや",
                ],
            },
            {
                text:
                    "Only the first sound of the second part changes: こ → ご. The や stays や, because only sounds " +
                    "from the k, s, t, and h rows have a voiced partner. The first parts (犬, 山, 鶏) are untouched.",
            },
            { heading: "The four sound changes" },
            {
                text:
                    "These are the same pairs as in the dakuten chart you learn with hiragana. Rendaku uses them in " +
                    "one direction only: from the plain sound to the voiced one.",
            },
            {
                list: [
                    "k → g: か き く け こ become が ぎ ぐ げ ご - 手紙 (てがみ, tegami, letter) is て + かみ",
                    "s → z: さ し す せ そ become ざ じ ず ぜ ぞ, with し becoming じ (ji) - 青空 (あおぞら, aozora, blue sky) is あお + そら",
                    "t → d: た ち つ て と become だ ぢ づ で ど, where ぢ and づ sound like じ and ず - 友達 (ともだち, tomodachi, friend) is とも + たち",
                    "h → b: は ひ ふ へ ほ become ば び ぶ べ ぼ, with ふ becoming ぶ - 花火 (はなび, hanabi, fireworks) is はな + ひ",
                ],
            },
            { heading: "A note on ぢ, づ and ぱ" },
            {
                parts: [
                    "ち → ぢ and つ → づ: these sound exactly like じ (ji) and ず (zu). Spelling normally uses じ and " +
                    "ず. Modern spelling keeps ぢ and づ in compounds you can still split into two parts, as in 鼻血 " +
                    "(はなぢ, hanaji, \"nosebleed\") and 手作り (てづくり, tezukuri, \"handmade\"), and in repeated " +
                    "sounds such as ちぢむ. Some words use じ or ず even though they come from ち or つ, such as " +
                    "世界中 (せかいじゅう), so learn the spelling together with the word. On a computer or phone " +
                    "keyboard with a Japanese romaji input, ぢ is typed \"di\" and づ is typed \"du.\" In Hepburn " +
                    "romanization both ぢ and じ are written ji, and both づ and ず are written zu - our post on ",
                    { to: "/blog/romanization-systems", label: "romanization systems" },
                    " explains why the systems differ.",
                ],
            },
            {
                text:
                    "h → p is a different case. The p sounds (ぱ ぴ ぷ ぺ ぽ, written with handakuten ゜) appear in " +
                    "words such as 一本 (いっぽん, ippon) and 六本 (ろっぽん, roppon), usually after a doubling っ. " +
                    "That comes from the fixed counter patterns in Lesson 9 (compare 三本 さんぼん, with b after " +
                    "ん), not from rendaku, which only ever voices (゛) and never produces a p sound.",
            },
            { heading: "More words to try" },
            {
                text:
                    "Every word below is built from two parts you may already know. The parts and the sound change " +
                    "are shown at the end of each line.",
            },
            {
                list: [
                    "犬小屋 (いぬごや, inugoya) - doghouse: いぬ + こや, こ → ご",
                    "山小屋 (やまごや, yamagoya) - mountain hut: やま + こや, こ → ご",
                    "鶏小屋 (にわとりごや, niwatorigoya) - chicken coop: にわとり + こや, こ → ご",
                    "手紙 (てがみ, tegami) - letter: て + かみ, か → が",
                    "青空 (あおぞら, aozora) - blue sky: あお + そら, そ → ぞ",
                    "小島 (こじま, kojima) - small island: こ + しま, し → じ",
                    "友達 (ともだち, tomodachi) - friend: とも + たち, た → だ",
                    "本棚 (ほんだな, hondana) - bookshelf: ほん + たな, た → だ",
                    "手作り (てづくり, tezukuri) - handmade: て + つくり, つ → づ",
                    "花火 (はなび, hanabi) - fireworks: はな + ひ, ひ → び",
                    "手袋 (てぶくろ, tebukuro) - gloves: て + ふくろ, ふ → ぶ",
                    "折り紙 (おりがみ, origami) - paper folding: おり + かみ, か → が",
                ],
            },
            { heading: "Rendaku is a tendency, not a rule" },
            {
                text:
                    "Rendaku is common, but it doesn't happen in every compound, and no simple formula tells you " +
                    "for certain which ones get it. Two words can share a second part and still behave " +
                    "differently: 出口 (でぐち, deguchi, \"exit\") voices 口, but 早口 (はやくち, hayakuchi, " +
                    "\"fast talking\") doesn't - no simple rule for beginners predicts this one. The practical " +
                    "rule: learn each compound as one word, together with its reading. The tendencies below tell " +
                    "you what to expect, not what's guaranteed.",
            },
            {
                list: [
                    "花屋 (はなや, hanaya, florist): や has no voiced partner, so there's nothing to voice. Compare 小屋 → ごや, where it's the こ that changes.",
                    "北風 (きたかぜ, kitakaze, north wind): かぜ already contains a voiced sound (ぜ). This is Lyman's law, explained next.",
                    "電車 (でんしゃ, densha, train): a Chinese-derived (on'yomi) compound, 電 (でん) + 車 (しゃ). Rendaku is rare here.",
                ],
            },
            {
                text:
                    "Lyman's law: if the second part already contains a voiced consonant (a b, d, g, or z sound), " +
                    "rendaku usually doesn't happen. That's why 北風 is きたかぜ and not きたがぜ - the ぜ in かぜ " +
                    "is already voiced. Compare 折り紙 (おりがみ, origami), where かみ has no voiced sound, so " +
                    "か → が is fine.",
            },
            {
                text:
                    "Native Japanese words versus Chinese-derived words: rendaku mostly happens when the second " +
                    "part is a native Japanese (和語, わご, wago) word - the ones usually read with kun'yomi, like " +
                    "紙, 空, 犬 and 棚. That is why 本棚 (ほんだな) still voices, even though 本 is on'yomi. It is " +
                    "rare in compounds made of Chinese-derived (on'yomi) parts, which is why 電車 is でんしゃ and " +
                    "not でんじゃ. It is also rare in katakana loanwords.",
            },
            {
                text:
                    "These two tendencies are a simplification for beginners: linguists describe more detailed " +
                    "conditions, and both have exceptions.",
            },
            { heading: "Repeated words with 々" },
            {
                text:
                    "The repeat mark 々 stands for the kanji before it, and the second copy is often voiced: 時々 " +
                    "(ときどき, tokidoki, \"sometimes\") and 人々 (ひとびと, hitobito, \"people\"). It's not " +
                    "automatic, and it can't happen when the second sound has no voiced partner: 色々 " +
                    "(いろいろ, iroiro, \"various\") stays いろいろ.",
            },
            { heading: "How to actually learn these words" },
            {
                list: [
                    "Learn the compound as one word. Write 手紙 down as てがみ, not as て + かみ - the reading you'll actually see and hear is the voiced one.",
                    "When a native Japanese compound is new to you, expect a voiced second part if it starts with a k, s, t, or h sound, then check the reading in a dictionary or in the vocabulary lists. A wrong guess is normal - the pattern is a tendency, not a rule.",
                    "If a word you know the parts of sounds slightly off, it's usually rendaku. The parts are the same words you already know, with one voiced sound.",
                ],
            },
            { heading: "Quick answers" },
            {
                list: [
                    "What is rendaku? The voicing of the first sound of the second part of a compound word, such as こや → ごや in 犬小屋.",
                    "Is rendaku predictable? Partly. There are tendencies (native Japanese words, Lyman's law), but 出口 (でぐち) and 早口 (はやくち) show that you still have to learn each word.",
                    "Why is 手紙 read てがみ and not てかみ? It's て + かみ, and the か of かみ is voiced by rendaku.",
                    "Is ぱ a rendaku sound? No. Rendaku only voices with dakuten (゛); the ぱ row comes from other patterns, such as the counters in the lessons.",
                    "Does rendaku happen in loanwords? Rarely.",
                ],
            },
            {
                parts: [
                    "Want to practice? Work through the ",
                    { to: "/lessons", label: "rendaku lesson" },
                    ", try ",
                    { to: "/kana-writing-practice", label: "writing the voiced kana by hand" },
                    ", or print ",
                    { to: "/worksheets?script=hiragana", label: "practice sheets with が, ざ and ば" },
                    ". If you're still sorting out the two kana scripts, our post on ",
                    { to: "/blog/hiragana-vs-katakana", label: "hiragana vs katakana" },
                    " is a good place to start.",
                ],
            },
        ],
    },
    {
        slug: "godan-vs-ichidan-japanese-verb-groups",
        ptSlug: "verbos-godan-e-ichidan-grupos-de-verbos-em-japones",
        title: "Godan vs Ichidan Verbs: What 五段 Really Means (and Why 売る Is Godan)",
        date: "2026-10-01",
        excerpt:
            "Japanese verbs fall into Godan, Ichidan and irregular groups, and 五段 means \"five steps.\" See the " +
            "five steps with 売る, learn why ending in る doesn't make a verb Ichidan, and get the て-form rule for " +
            "every Godan ending.",
        tags: ["Grammar", "Beginner"],
        keywords: ["godan", "ichidan", "verb groups", "conjugation", "grammar"],
        content: [
            {
                text:
                    "You've learned that 食べる (たべる, taberu, \"to eat\") becomes 食べます (tabemasu) when you drop る " +
                    "and add ます. Then you meet 売る (うる, uru, \"to sell\"), try the same trick, and get 売ます. But the " +
                    "correct form is 売ります (urimasu). The difference is that 食べる is an Ichidan verb and 売る is a " +
                    "Godan verb, and Japanese verbs conjugate differently depending on which group they belong to.",
            },
            {
                parts: [
                    "This post explains the three verb groups, why 五段 (godan) means \"five steps,\" how to tell a Godan " +
                    "verb from an Ichidan verb when both end in る, and the て-form rule for every Godan ending. It's " +
                    "the longer companion to ",
                    { to: "/lessons?lesson=11.5", label: "Lesson 11.5" },
                    " and the ",
                    { to: "/grammar/n5-verb-groups", label: "verb groups grammar page" },
                    ".",
                ],
            },
            { heading: "The three verb groups" },
            {
                text:
                    "Every Japanese verb belongs to one of three groups, and the group decides how it conjugates. " +
                    "Different books use different names for the same groups, so here they are side by side:",
            },
            {
                table: {
                    caption: "The three Japanese verb groups and their other names",
                    headers: ["Japanese name", "Textbook label", "Also called", "Dictionary form ends in"],
                    rows: verbGroupNames.map((item) => [
                        `${item.japanese} (${item.reading}, ${item.romaji})`,
                        item.label,
                        item.nickname,
                        item.ends,
                    ]),
                },
            },
            {
                text:
                    "The group numbers are a textbook convention. Most Japanese-as-a-foreign-language textbooks use " +
                    "Group 1 for Godan and Group 2 for Ichidan, but a few number them differently, so when in doubt " +
                    "rely on the Japanese names or on u-verb and ru-verb. This site uses Godan, Ichidan and irregular, " +
                    "and mentions the other names where they help.",
            },
            {
                text:
                    "The important point is that verbs are sorted by how they conjugate, not simply by whether they " +
                    "end in る. Every dictionary form ends in an u-sound, and Godan verbs can end in any of nine kana: " +
                    "う, く, ぐ, す, つ, ぬ, ぶ, む or る. Ichidan verbs always end in る.",
            },
            { heading: "What 五段 means: five steps" },
            {
                text:
                    "段 (だん, dan) means \"step\" or \"level.\" The kana chart (五十音図, ごじゅうおんず, gojūon-zu) is " +
                    "arranged in five steps by vowel sound: the あ step (a), the い step (i), the う step (u), the え " +
                    "step (e) and the お step (o). 五段 (ごだん, godan) therefore means \"five steps\": as a Godan verb " +
                    "conjugates, its last kana moves through all five steps.",
            },
            {
                text:
                    "Take 売る (うる, uru, \"to sell\"). Its last kana is る, which belongs to the ら row of the chart: " +
                    "ら, り, る, れ, ろ. Everything in front of it, 売, never changes. Only the last kana steps up and down:",
            },
            {
                table: {
                    caption: "The five steps of the Godan verb 売る (うる, to sell)",
                    headers: ["Step (段)", "Form", "Reading", "Used for"],
                    rows: godanFiveStepRows(godanFiveSteps.map((item) => item.use)),
                },
            },
            {
                text:
                    "Read the last kana down the table: ら → り → る → れ → ろ. That is the five steps, and it is the " +
                    "reason Godan verbs have that name. The dictionary form (売る) sits on the う step, which is why " +
                    "every verb in the dictionary ends in an u-sound. The endings (ない, ます, ば, う) are the same for " +
                    "every Godan verb; only the step the last kana stands on changes.",
            },
            { heading: "The same five steps for every Godan ending" },
            {
                text:
                    "Every Godan ending works the same way, using its own row of the kana chart. 書く moves through か, " +
                    "き, く, け, こ; 住む moves through ま, み, む, め, も. One small twist: a verb ending in う, like 買う, " +
                    "uses わ instead of あ on its first step: 買わない, not 買あない.",
            },
            {
                table: {
                    caption: "Godan verbs of every ending on all five steps",
                    headers: [
                        "Ending",
                        "Verb",
                        "あ step + ない",
                        "い step + ます",
                        "う step (dictionary)",
                        "え step + ば",
                        "お step + う",
                    ],
                    rows: godanEndingRows(),
                },
            },
            {
                text:
                    "Notice that 住む (すむ, sumu, \"to live somewhere\") follows exactly the same logic as 売る. You only " +
                    "need to know which row of the chart the verb's last kana belongs to.",
            },
            { heading: "Ichidan: one step only" },
            {
                text:
                    "一段 (いちだん, ichidan) means \"one step.\" An Ichidan verb's stem, everything before る, never moves " +
                    "to another step. It stays on the い step (見る, 起きる) or on the え step (食べる, 寝る). To conjugate, " +
                    "you drop る and add the ending. Japanese school grammar calls these 上一段 and 下一段 verbs, the " +
                    "\"upper\" and \"lower\" one-step verbs, after the い step above う and the え step below it.",
            },
            {
                table: {
                    caption: "Godan 売る compared with Ichidan 食べる",
                    headers: ["Form", "Godan: 売る (うる)", "Ichidan: 食べる (たべる)"],
                    rows: godanVsIchidanRows(godanVsIchidan.map((item) => item.form)),
                },
            },
            {
                text:
                    "In the Godan column the highlighted last kana moves across the five steps. In the Ichidan column " +
                    "the stem 食べ never changes; the highlight shows the ending you attach after dropping る.",
            },
            { heading: "Irregular verbs: する and 来る" },
            {
                text:
                    "Only two verbs are truly irregular: する (suru, \"to do\") and 来る (くる, kuru, \"to come\"). They " +
                    "follow neither pattern and are simply memorized. 来る is the trickier one: the kanji 来 stays the " +
                    "same, but its reading changes (こ, き, く, く, こ) from form to form. Every する compound, such as " +
                    "勉強する (べんきょうする, \"to study\"), conjugates exactly like する.",
            },
            {
                table: {
                    caption: "The irregular verbs する and 来る",
                    headers: ["Form", "する (suru)", "来る (くる, kuru)"],
                    rows: irregularRows(irregularForms.map((item) => item.form)),
                },
            },
            { heading: "The る trap: why 売る is Godan even though it ends in る" },
            {
                text:
                    "Ichidan verbs end in る, so beginners often assume every verb ending in る is Ichidan. It isn't. " +
                    "売る ends in る and is Godan: its polite form is 売ります (urimasu), where る becomes ります. Compare " +
                    "食べる, an Ichidan verb, where る simply becomes ます: 食べます (tabemasu). A quick guide to the " +
                    "dictionary form:",
            },
            {
                table: {
                    caption: "How to tell the verb group from the dictionary form",
                    headers: ["Dictionary form ends in", "Group", "Why", "Examples"],
                    rows: verbGroupRecognition.map((item) => [item.rule, item.group, item.why, item.examples]),
                },
            },
            {
                text:
                    "That second row is the answer for 売る: う is an u-sound, and Ichidan verbs only exist after an い " +
                    "or え sound, so 売る must be Godan. The third row is where you need to be careful.",
            },
            {
                text:
                    "A short list of common verbs ends in る after an い or え sound and still conjugates as Godan. " +
                    "They look Ichidan but aren't, so learn them as a list. Their polite forms give them away: る " +
                    "becomes ります. The list is not complete (others include 減る, 蹴る, 限る, 参る and 喋る): if a " +
                    "verb ending in い or え plus る is not on it, check it on the conjugation page.",
            },
            {
                table: {
                    caption: "Common Godan verbs that look like Ichidan verbs",
                    headers: ["Verb", "Meaning", "Polite form"],
                    rows: godanLookAlikeRows(godanLookAlikes.map((item) => item.meaning)),
                },
            },
            {
                text:
                    "Some pairs of verbs even share the same reading but belong to different groups. Only the kanji " +
                    "and the context tell you which is which, so learn the group together with the word:",
            },
            {
                table: {
                    caption: "Verbs with the same reading but different groups",
                    headers: ["Verb", "Meaning", "Group", "Polite form"],
                    rows: verbSamePairRows(verbSamePairs.map((item) => item.meaning)),
                },
            },
            {
                text:
                    "Two Godan verbs also have one irregular quirk each. 行く (いく, iku, \"to go\") is regular everywhere " +
                    "except its て-form and past (行って, 行った), and ある (\"to exist\") has the plain negative ない " +
                    "instead of あらない. A handful of honorific Godan verbs, such as いらっしゃる and くださる, have an " +
                    "irregular polite form (いらっしゃいます, くださいます); you will meet them in polite and honorific " +
                    "speech later.",
            },
            {
                parts: [
                    "Not sure about a verb? The ",
                    { to: "/conjugation", label: "Verb Conjugation page" },
                    " labels every verb as Godan, Ichidan or irregular and shows its full table, so you can check " +
                    "any verb you are unsure about.",
                ],
            },
            { heading: "Godan て-form: the ending decides" },
            {
                text:
                    "The て-form of a Godan verb depends on its last kana. Historically, the て-form was the い step " +
                    "plus て (売りて), which was awkward to say and was smoothed into 売って; the same kind of smoothing " +
                    "gave んで and いて. The result is five short rules:",
            },
            {
                table: {
                    caption: "Godan verbs in the て-form, grouped by ending",
                    headers: ["Sound change", "Dictionary form", "て-form", "Meaning"],
                    rows: godanTeFormRows(
                        godanTeForms.map((item) => item.change),
                        godanTeForms.map((item) => item.meaning)
                    ),
                },
            },
            {
                text:
                    "行く (いく, iku, \"to go\") is the one exception: it should become 行いて by the く rule, but its real " +
                    "て-form is 行って (itte). 売る → 売って and 住む → 住んで follow the same rules as every other Godan " +
                    "verb with those endings.",
            },
            {
                parts: [
                    "Ichidan verbs just drop る and add て (食べる → 食べて), and the irregular verbs are して (する) and " +
                    "来て (きて, 来る). The same sound changes also build the plain past (た-form): 売った, 住んだ, 書いた. " +
                    "The ",
                    { to: "/lessons?lesson=28", label: "て-form lesson" },
                    " covers the て-form in full, with the sentence patterns it unlocks.",
                ],
            },
            { heading: "Example sentences" },
            { text: "Godan verbs:" },
            {
                list: [
                    "古い本を売ります。 (ふるい ほんを うります, Furui hon o urimasu.) - I'll sell my old books.",
                    "大切な本は売らない。 (たいせつな ほんは うらない, Taisetsu na hon wa uranai.) - I don't sell books that matter to me. (plain speech)",
                    "いらない本を売ろう。 (いらない ほんを うろう, Iranai hon o urō.) - Let's sell the books we don't need. (plain speech; polite: 売りましょう)",
                    "古い本を売って、新しい本を買います。 (ふるい ほんを うって、あたらしい ほんを かいます, Furui hon o utte, atarashii hon o kaimasu.) - I'll sell my old books and buy new ones.",
                    "私は東京に住んでいます。 (わたしは とうきょうに すんでいます, Watashi wa Tōkyō ni sunde imasu.) - I live in Tokyo.",
                    "友達に手紙を書きます。 (ともだちに てがみを かきます, Tomodachi ni tegami o kakimasu.) - I write a letter to a friend.",
                    "先生と日本語で話します。 (せんせいと にほんごで はなします, Sensei to nihongo de hanashimasu.) - I talk with my teacher in Japanese.",
                ],
            },
            { text: "Ichidan verbs:" },
            {
                list: [
                    "朝ご飯にパンを食べます。 (あさごはんに パンを たべます, Asagohan ni pan o tabemasu.) - I eat bread for breakfast.",
                    "六時に起きて、朝ご飯を食べます。 (ろくじに おきて、あさごはんを たべます, Rokuji ni okite, asagohan o tabemasu.) - I get up at six and eat breakfast.",
                    "毎日ニュースを見ます。 (まいにち ニュースを みます, Mainichi nyūsu o mimasu.) - I watch the news every day.",
                ],
            },
            { text: "Irregular verbs:" },
            {
                list: [
                    "毎日、日本語を勉強します。 (まいにち、にほんごを べんきょうします, Mainichi, nihongo o benkyō shimasu.) - I study Japanese every day.",
                    "明日、友達が来ます。 (あした、ともだちが きます, Ashita, tomodachi ga kimasu.) - A friend is coming tomorrow.",
                ],
            },
            { text: "Look-alike verbs, with the same reading but a different group:" },
            {
                list: [
                    "家に帰ります。 (いえに かえります, Ie ni kaerimasu.) - I go home. 帰る is Godan: る becomes ります.",
                    "部屋を変えます。 (へやを かえます, Heya o kaemasu.) - I'm going to change rooms. 変える is Ichidan: る becomes ます.",
                    "髪を切ります。 (かみを きります, Kami o kirimasu.) - I'm going to get my hair cut. 切る is Godan.",
                    "白いシャツを着ます。 (しろい シャツを きます, Shiroi shatsu o kimasu.) - I wear a white shirt. 着る is Ichidan.",
                ],
            },
            { heading: "How to learn the groups" },
            {
                list: [
                    "Learn each new verb together with its group. When you add 売る to your vocabulary, write it down as 売る (Godan), not just 売る.",
                    "Check the kana before る. After an あ, う or お sound, the verb is Godan. After an い or え sound, it is probably Ichidan - check the exception list before you decide.",
                    "Say the polite form out loud. 売ります and 食べます sound different, and saying them trains the difference.",
                    "Learn the exceptions as a group: 帰る, 入る, 走る, 切る, 知る, 要る, 滑る and 握る are all Godan.",
                    "When in doubt, check a conjugation table. Every verb on the conjugation page shows its group.",
                ],
            },
            { heading: "Quick answers" },
            {
                list: [
                    "What does 五段 mean? \"Five steps.\" The last kana of a Godan verb moves through the five steps あ, い, う, え, お of the kana chart as the verb conjugates, as in 売らない, 売ります, 売る, 売れば, 売ろう.",
                    "What does 一段 mean? \"One step.\" An Ichidan verb keeps its stem and only swaps る for an ending.",
                    "Is 売る Godan or Ichidan? Godan. It ends in る, but the sound before る is う, and Ichidan verbs only exist after an い or え sound.",
                    "Do all verbs that end in る belong to the same group? No. They can be Godan (売る, 帰る), Ichidan (食べる, 見る) or irregular (する, 来る).",
                    "What is the て-form of 住む? 住んで (すんで, sunde). む, ぶ and ぬ all become んで.",
                    "What is the て-form of 売る? 売って (うって, utte). う, つ and る all become って.",
                    "Which verbs are Group 1, Group 2 and Group 3? In most textbooks Godan verbs are Group 1, Ichidan verbs are Group 2, and する and 来る are Group 3.",
                ],
            },
            {
                parts: [
                    "Want to practice? Work through ",
                    { to: "/lessons?lesson=11.5", label: "Lesson 11.5" },
                    ", read the ",
                    { to: "/grammar/n5-verb-groups", label: "verb groups grammar page" },
                    ", or look up any verb on the ",
                    { to: "/conjugation", label: "conjugation page" },
                    ". If the kana chart is new to you, our post on ",
                    { to: "/blog/hiragana-vs-katakana", label: "hiragana vs katakana" },
                    " is a good place to start.",
                ],
            },
        ],
    },
    {
        slug: "aprender-japones-em-portugal-curso-n5",
        title: "Aprender Japonês em Portugal: Como é realmente um Curso de N5",
        date: "2026-09-22",
        lang: "pt-PT",
        excerpt:
            "Portugal não tem atualmente um local de exame JLPT ativo, mas isso não significa que não haja onde " +
            "estudar - eis um relato em primeira mão de um curso de N5 totalmente remoto, dado a partir de casa, " +
            "da Da Vinci Language School.",
        tags: ["JLPT", "Study Tips"],
        keywords: ["Portugal", "N5", "remoto", "online", "JLPT"],
        content: [
            {
                parts: [
                    "Como podes verificar aqui ",
                    {
                        to: "/pt/blog/jlpt-europe-countries-with-no-test-site",
                        label: "no nosso artigo sobre locais de exame JLPT na Europa",
                    },
                    " , Portugal não tem atualmente nenhum local ativo para realizar o JLPT. Isso é " +
                        "um problema real para quem quer fazer o exame, mas não significa que não haja onde " +
                        "aprender japonês em Portugal. Uma de nós tem estado a frequentar um curso totalmente " +
                        "remoto, dado a partir de casa, por isso aqui fica um relato honesto de como é essa " +
                        "experiência.",
                ],
            },
            { heading: "A escola: Da Vinci Language School (aulas online, à distância, sediada em Faro)" },
            {
                parts: [
                    "A Da Vinci Language School é gerida pela Ginásios da Educação Da Vinci (GEDAVINCI LDA), " +
                        "entidade formadora certificada pela DGERT, sediada em Faro. O curso de japonês em si é " +
                        "totalmente remoto e online: aulas de grupo em tempo real via Zoom, e não vídeos pré-" +
                        "gravados nem presença física. Isto significa que podes assistir às aulas a partir de " +
                        "casa - não é preciso viver perto de Faro, nem sequer no Algarve, qualquer pessoa em " +
                        "Portugal com uma ligação à internet estável pode participar. " +
                        "Podes ver os cursos de línguas atuais, incluindo japonês, no ",
                    { href: "https://www.ginasiosdavinci.com/cursos-de-linguas/", label: "site da própria Da Vinci" },
                    ".",
                ],
            },
            { heading: "Como o curso está estruturado" },
            {
                text:
                    "O japonês de iniciação está dividido em dois módulos consecutivos, N5.1 e N5.2, cada um " +
                    "com 25 horas de aulas, distribuídas em duas sessões de 1h30 por semana, ao longo de cerca " +
                    "de dois meses. Cada módulo termina com um teste avaliado, e concluir o módulo dá direito a " +
                    "certificado. A escola tem ainda um curso à parte, indicado apenas como \"N5\" (sem .1/.2), " +
                    "vocacionado especificamente para a preparação para o exame JLPT, distinto dos dois módulos " +
                    "de iniciação.",
            },
            {
                text:
                    "Preços à data desta publicação: o módulo N5.1 custa 300€, o N5.2 custa 275€, e o curso de " +
                    "preparação para exame \"N5\" custa 137,5€ - todos para 25 horas de aulas em grupo. Há " +
                    "também aulas privadas disponíveis com a mesma professora, fora dos cursos de grupo, para " +
                    "quem preferir um ritmo mais rápido ou individual.",
            },
            { heading: "Coisas práticas a saber antes de te inscreveres" },
            {
                text:
                    "Alguns pontos úteis a conhecer se estiveres a considerar um curso semelhante (comuns a " +
                    "este tipo de formação certificada em grupo, não exclusivos desta escola): a assiduidade é " +
                    "registada por sessão, e faltar a mais do que uma determinada percentagem do total de " +
                    "horas (normalmente entre 10% e 20%) pode significar não aprovação no módulo. Desistir a " +
                    "meio geralmente não dá direito a reembolso, exceto quando o atraso ou cancelamento é da " +
                    "responsabilidade da escola - por exemplo, um grupo que não atinja o número mínimo de " +
                    "participantes. Obter o certificado final costuma exigir cumprir o mínimo de assiduidade e " +
                    "ser aprovado no curso, não apenas chegar ao fim do calendário. Confirma sempre as " +
                    "condições específicas da escola antes de pagar, porque estes detalhes variam.",
            },
            { heading: "Isto substitui a falta de um local de exame?" },
            {
                parts: [
                    "Não - frequentar um curso como este não muda o facto de Portugal não ter atualmente " +
                        "nenhum local ativo para o JLPT. Se o objetivo é mesmo fazer o exame, vais continuar a " +
                        "precisar de viajar, e a nossa ",
                    { to: "/pt/jlpt-test-sites", label: "página de locais de exame JLPT" },
                    " pode indicar-te a alternativa ativa mais próxima. O que um curso como este resolve é a " +
                        "questão mais básica de onde aprender a língua entretanto, com feedback e estrutura " +
                        "reais, em vez de estudar sozinho/a.",
                ],
            },
            {
                parts: [
                    "Se estás em Portugal e procuras uma opção estruturada, vale a pena procurar uma escola de " +
                        "línguas local que ofereça aulas de japonês em direto ou presenciais - e combinar isso " +
                        "com as ferramentas gratuitas daqui para a prática diária: ",
                    { to: "/pt/lessons", label: "lições" },
                    ", ",
                    { to: "/pt/kanji-roulette", label: "prática de kanji" },
                    " e ",
                    { to: "/pt/study-materials", label: "recomendações de materiais de estudo" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "fichas-hiragana-katakana-para-imprimir",
        title: "Fichas de Hiragana e Katakana para Imprimir: Crie a Sua, com Ordem dos Traços",
        date: "2026-09-28",
        lang: "pt-PT",
        excerpt:
            "Escolha exatamente os kana que quer praticar, decida quanta ajuda de decalque quer e imprima uma " +
            "ficha A4 com a ordem dos traços em cada linha - ou escreva na ficha, no ecrã, se não tiver impressora.",
        tags: ["Kana", "Tools"],
        keywords: ["hiragana", "katakana", "fichas", "caligrafia", "imprimir"],
        content: [
            {
                text:
                    "Se decidiu escrever hiragana e katakana à mão, a pergunta seguinte é o que pôr à sua frente. " +
                    "Uma folha com o mesmo caracter repetido quarenta vezes não ajuda muito se não vir a ordem dos " +
                    "traços, e um PDF fixo não se adapta às linhas em que está a trabalhar esta semana. Este artigo " +
                    "mostra como criar uma ficha que o faz, com o gerador de fichas gratuito deste site.",
            },
            { heading: "Porquê escrever kana à mão?" },
            {
                parts: [
                    "Reconhecer um caracter quando o vê e escrevê-lo de memória são duas competências diferentes, e " +
                    "só a segunda o obriga a lembrar-se da forma, das proporções e da ordem dos traços. Escrever " +
                    "no teclado é uma ótima forma de usar o japonês no dia a dia, mas uns minutos de caligrafia por " +
                    "sessão dão às formas um apoio extra. É uma das dicas do nosso artigo sobre como ",
                    { to: "/pt/blog/tips-to-memorize-kana-faster", label: "memorizar hiragana e katakana mais depressa" },
                    ", e este é o desenvolvimento dessa ideia.",
                ],
            },
            { heading: "O que tem uma boa ficha de prática" },
            {
                list: [
                    "Um caracter modelo no início de cada linha, para ter sempre com que comparar",
                    "Uma faixa com a ordem dos traços, que mostra o caracter a ser construído traço a traço",
                    "Quadrados suficientemente grandes para escrever bem, com linhas-guia opcionais a passar pelo meio de cada um",
                    "Quadrados suficientes por caracter para criar ritmo - pode escolher entre 1 e 60 (arredondado para preencher a última linha), e o valor por omissão é 12",
                    "Uma forma de retirar a ajuda aos poucos, para não ficar a decalcar para sempre",
                ],
            },
            {
                text:
                    "É para esse último ponto que servem os três estilos de prática. O Decalque coloca uma cópia " +
                    "esbatida do caracter em todos os quadrados, o que é adequado à primeira passagem por uma linha " +
                    "nova. A prática Guiada começa com uma cópia esbatida mais escura e vai desaparecendo até aos " +
                    "quadrados vazios, por isso os primeiros quadrados de cada linha são decalcados e os últimos " +
                    "escreve-os de memória. O estilo Em branco mostra só o caracter modelo, seguido de quadrados " +
                    "vazios, para quando já conhece a linha e quer testar-se. Qual escolher é uma questão de " +
                    "preferência - a prática Guiada é a opção por omissão.",
            },
            { heading: "Crie a sua primeira ficha em cerca de dois minutos" },
            {
                list: [
                    "Abra o gerador de fichas e escolha Hiragana. No início, todos os hiragana básicos estão selecionados, por isso carregue em Limpar.",
                    "Escolha a linha que está a aprender - a linha あ (あ・い・う・え・お) é uma primeira escolha natural - ou toque em caracteres individuais para os adicionar.",
                    "Defina quantos quadrados de prática quer por caracter e escolha um estilo de prática.",
                    "Deixe a ordem dos traços e as linhas-guia ativas, se as quiser, ou desligue-as para uma página mais limpa. Cada linha de kana mostra também o respetivo romaji.",
                    "Veja a pré-visualização e carregue em Descarregar PDF ou em Imprimir.",
                ],
            },
            {
                parts: [
                    "Além dos 46 kana básicos de cada escrita, pode acrescentar as formas sonoras escritas com " +
                    "dakuten e handakuten (が, ざ, ぱ e assim por diante). Para perceber porque é que esses sons " +
                    "sonoros aparecem quando as palavras se juntam, veja o nosso artigo sobre o ",
                    { to: "/blog/o-que-e-rendaku-sonorizacao-sequencial", label: "rendaku" },
                    ". Os sons combinados, como きゃ e しゅ, os kana pequenos (ゃ, ゅ, ょ, っ) e o " +
                    "sinal de vogal longa ー não estão incluídos nas fichas.",
                ],
            },
            { heading: "Escolha um ritmo tranquilo" },
            {
                text:
                    "Uma ficha tem no máximo dez caracteres por página A4, e os quadrados ficam maiores quando há " +
                    "menos caracteres numa página. Por isso, uma ou duas linhas por sessão são um ponto de partida " +
                    "razoável - uma sugestão para ajustar ao seu próprio horário e disposição, e não um calendário. " +
                    "Uma sessão curta que acaba vale mais do que uma pilha ambiciosa de páginas que não faz.",
            },
            { heading: "Imprima com cuidado" },
            {
                text:
                    "As fichas estão preparadas para papel A4, o formato normal em Portugal. Na janela de impressão, " +
                    "escolha A4 e imprima a 100 por cento (\"tamanho real\") em vez de \"ajustar à página\", para que " +
                    "os quadrados mantenham o tamanho para que foram pensados. As fichas são só em A4; por isso, se " +
                    "usar papel US Letter, as margens podem não ficar como espera. No topo da ficha há também " +
                    "campos para o Nome e a Data, que pode usar ou ignorar.",
            },
            { heading: "Sem impressora? Pratique online" },
            {
                text:
                    "Junto à pré-visualização há a opção Praticar online. Permite desenhar na ficha, no ecrã, com o " +
                    "rato ou com o dedo, aumentar o zoom até 300 por cento para ter mais espaço e limpar o desenho " +
                    "para recomeçar. Para ser claro quanto ao que é: o desenho não é guardado nem avaliado, por isso " +
                    "trate-o como um rascunho para habituar a mão às formas, e não como um verificador.",
            },
            { heading: "Katakana a seguir" },
            {
                parts: [
                    "Quando se sentir à vontade com o hiragana, o processo é o mesmo para o katakana - ",
                    { to: "/pt/worksheets?script=katakana", label: "abra o gerador em katakana" },
                    " e escolha uma linha, por exemplo ア・イ・ウ・エ・オ. Como o katakana reutiliza os sons que já " +
                    "conhece, só tem de aprender formas novas. Se não tiver a certeza de como as duas escritas se " +
                    "distinguem, o nosso artigo sobre ",
                    { to: "/pt/blog/hiragana-vs-katakana", label: "hiragana vs katakana" },
                    " explica-o.",
                ],
            },
            { heading: "Quando os kana estiverem feitos: kanji N5" },
            {
                parts: [
                    "O mesmo gerador cria também ",
                    { to: "/pt/worksheets?script=kanji", label: "fichas de kanji" },
                    " para os kanji N5 do JLPT tal como estão listados no NihongoTech, e cada linha pode incluir o " +
                    "significado e as leituras on'yomi e kun'yomi ao lado da ordem dos traços. Para ver como se " +
                    "desenha um kanji antes de o imprimir, a página de ",
                    { to: "/pt/kanji-writing-practice", label: "prática de escrita de kanji" },
                    " anima-o, e a de ",
                    { to: "/pt/kana-writing-practice", label: "prática de escrita de kana" },
                    " faz o mesmo para o hiragana e o katakana. Se quiser um percurso estruturado pelos dois, as ",
                    { to: "/pt/lessons", label: "lições" },
                    " são o melhor ponto de partida.",
                ],
            },
            { heading: "Respostas rápidas" },
            {
                list: [
                    "É gratuito? Sim, para todo o hiragana, katakana e kanji N5 do JLPT, com todas as opções de layout. Os kanji N4-N1, os kanji fora do JLPT e as listas de kanji personalizadas são Premium, que ainda não pode ser adquirido.",
                    "Que formato de papel? Apenas A4.",
                    "Onde é criado o PDF? No seu navegador, no seu dispositivo - não é gerado num servidor.",
                    "Funciona sem impressora? Sim, através de Praticar online, embora o desenho não seja guardado nem avaliado.",
                ],
            },
            {
                parts: [
                    "Quer experimentar? ",
                    { to: "/pt/worksheets?script=hiragana", label: "Crie a sua primeira ficha" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "o-que-e-rendaku-sonorizacao-sequencial",
        title: "Rendaku (連濁) Explicado: Porque é que 小屋 (koya) Passa a ごや (goya) em 犬小屋",
        date: "2026-09-29",
        lang: "pt-PT",
        excerpt:
            "Conhece 小屋 (koya, cabana), mas 犬小屋 lê-se inugoya. O que é o rendaku, porque mudam os sons nas " +
            "palavras compostas e porque é uma tendência e não uma regra?",
        tags: ["Kanji", "Beginner"],
        keywords: ["rendaku", "sonorização sequencial", "dakuten", "palavras compostas", "pronúncia"],
        content: [
            {
                text:
                    "Aprendeu 小屋 (こや, koya) como \"cabana\" ou \"abrigo\". Depois encontra 犬小屋, \"casota de " +
                    "cão\", e não se lê いぬこや - lê-se いぬごや (inugoya). O こ passou a ご. Não é um erro de " +
                    "escrita, nem uma exceção isolada que tenha de decorar: é o rendaku (連濁, れんだく), e é uma " +
                    "das razões mais comuns para uma palavra que já conhece soar um pouco diferente dentro de uma " +
                    "palavra mais comprida.",
            },
            {
                parts: [
                    "Este artigo explica o que é o rendaku, as quatro mudanças de som por trás dele e o aviso " +
                    "honesto de que é uma forte tendência, e não uma regra que funcione em todas as palavras " +
                    "compostas. É o complemento mais longo da ",
                    { to: "/pt/lessons", label: "lição sobre rendaku" },
                    " que está nas lições.",
                ],
            },
            { heading: "O que é o rendaku?" },
            {
                text:
                    "O rendaku costuma ser traduzido como sonorização sequencial (também chamada vozeamento). Em " +
                    "muitas palavras compostas, o primeiro som da segunda parte é sonorizado: recebe o dakuten (゛) " +
                    "que conheceu ao aprender hiragana. Só esse som muda - a primeira parte da palavra composta e o " +
                    "resto da segunda parte ficam exatamente como estavam.",
            },
            { heading: "Porque é que こや passa a ごや" },
            {
                parts: [
                    "Sozinho, 小屋 lê-se こや (koya), \"cabana\" ou \"abrigo\". É formado por ",
                    { to: "/pt/kanji/小", label: "小" },
                    " (こ, \"pequeno\") e ",
                    { to: "/pt/kanji/屋", label: "屋" },
                    " (や, \"casa, loja\"), o mesmo 屋 de 花屋 (はなや, hanaya, \"florista\"). Se lhe puser outra " +
                    "palavra à frente, 小屋 passa a ser a segunda parte de uma palavra composta, e o seu primeiro " +
                    "som こ (ko) é sonorizado e passa a ご (go). Três para experimentar:",
                ],
            },
            {
                list: [
                    "犬小屋 (いぬごや, inugoya) - casota de cão, canil: いぬ + こや",
                    "山小屋 (やまごや, yamagoya) - refúgio de montanha: やま + こや",
                    "鶏小屋 (にわとりごや, niwatorigoya) - galinheiro: にわとり + こや",
                ],
            },
            {
                text:
                    "Só muda o primeiro som da segunda parte: こ → ご. O や continua や, porque só os sons das " +
                    "linhas k, s, t e h têm um par sonoro. As primeiras partes (犬, 山, 鶏) não se alteram.",
            },
            { heading: "As quatro mudanças de som" },
            {
                text:
                    "São os mesmos pares do quadro de dakuten que se aprende com o hiragana. O rendaku usa-os num " +
                    "único sentido: do som simples para o som sonoro.",
            },
            {
                list: [
                    "k → g: か き く け こ passam a が ぎ ぐ げ ご - 手紙 (てがみ, tegami, carta) é て + かみ",
                    "s → z: さ し す せ そ passam a ざ じ ず ぜ ぞ, com し a passar a じ (ji) - 青空 (あおぞら, aozora, céu azul) é あお + そら",
                    "t → d: た ち つ て と passam a だ ぢ づ で ど, em que ぢ e づ soam como じ e ず - 友達 (ともだち, tomodachi, amigo) é とも + たち",
                    "h → b: は ひ ふ へ ほ passam a ば び ぶ べ ぼ, com ふ a passar a ぶ - 花火 (はなび, hanabi, fogo de artifício) é はな + ひ",
                ],
            },
            { heading: "Uma nota sobre ぢ, づ e ぱ" },
            {
                parts: [
                    "ち → ぢ e つ → づ: soam exatamente como じ (ji) e ず (zu). A ortografia normal usa じ e ず. A " +
                    "ortografia moderna mantém ぢ e づ em palavras compostas que ainda se sentem como duas partes, " +
                    "como 鼻血 (はなぢ, hanaji, \"hemorragia nasal\") e 手作り (てづくり, tezukuri, \"feito à " +
                    "mão\"), e em sons repetidos como ちぢむ. Algumas palavras usam じ ou ず apesar de virem de ち " +
                    "ou つ, como 世界中 (せかいじゅう), por isso aprenda a grafia junto com a palavra. Num teclado " +
                    "de computador ou telemóvel com introdução de romaji japonês, o ぢ escreve-se \"di\" e o づ " +
                    "escreve-se \"du\". Na romanização Hepburn, tanto ぢ como じ se escrevem ji, e tanto づ como ず " +
                    "se escrevem zu - o nosso artigo sobre os ",
                    { to: "/pt/blog/romanization-systems", label: "sistemas de romanização" },
                    " explica porque é que os sistemas diferem.",
                ],
            },
            {
                text:
                    "h → p é um caso diferente. Os sons p (ぱ ぴ ぷ ぺ ぽ, escritos com handakuten ゜) aparecem em " +
                    "palavras como 一本 (いっぽん, ippon) e 六本 (ろっぽん, roppon), normalmente depois de um っ que " +
                    "duplica a consoante. Isso vem dos padrões fixos dos contadores da Lição 9 (compare 三本 " +
                    "さんぼん, com b depois de ん), e não do rendaku, que só sonoriza (゛) e nunca produz um som p.",
            },
            { heading: "Mais palavras para experimentar" },
            {
                text:
                    "Todas as palavras abaixo são formadas por duas partes que talvez já conheça. As partes e a " +
                    "mudança de som estão indicadas no fim de cada linha.",
            },
            {
                list: [
                    "犬小屋 (いぬごや, inugoya) - casota de cão: いぬ + こや, こ → ご",
                    "山小屋 (やまごや, yamagoya) - refúgio de montanha: やま + こや, こ → ご",
                    "鶏小屋 (にわとりごや, niwatorigoya) - galinheiro: にわとり + こや, こ → ご",
                    "手紙 (てがみ, tegami) - carta: て + かみ, か → が",
                    "青空 (あおぞら, aozora) - céu azul: あお + そら, そ → ぞ",
                    "小島 (こじま, kojima) - pequena ilha (ilhéu): こ + しま, し → じ",
                    "友達 (ともだち, tomodachi) - amigo: とも + たち, た → だ",
                    "本棚 (ほんだな, hondana) - estante: ほん + たな, た → だ",
                    "手作り (てづくり, tezukuri) - feito à mão: て + つくり, つ → づ",
                    "花火 (はなび, hanabi) - fogo de artifício: はな + ひ, ひ → び",
                    "手袋 (てぶくろ, tebukuro) - luvas: て + ふくろ, ふ → ぶ",
                    "折り紙 (おりがみ, origami) - arte de dobrar papel: おり + かみ, か → が",
                ],
            },
            { heading: "O rendaku é uma tendência, não uma regra" },
            {
                text:
                    "O rendaku é comum, mas não acontece em todas as palavras compostas, e não existe nenhuma " +
                    "fórmula simples que diga com certeza quais delas o têm. Duas palavras podem ter a mesma " +
                    "segunda parte e comportar-se de forma diferente: 出口 (でぐち, deguchi, \"saída\") sonoriza o " +
                    "口, mas 早口 (はやくち, hayakuchi, \"fala rápida\") não - nenhuma regra simples para " +
                    "principiantes permite prever este caso. A regra prática: aprenda cada palavra composta como " +
                    "uma só palavra, com a respetiva leitura. As tendências abaixo dizem-lhe o que esperar, não o " +
                    "que está garantido.",
            },
            {
                list: [
                    "花屋 (はなや, hanaya, florista): や não tem par sonoro, por isso não há nada a sonorizar. Compare com 小屋 → ごや, em que é o こ que muda.",
                    "北風 (きたかぜ, kitakaze, vento norte): かぜ já contém um som sonoro (ぜ). É a lei de Lyman, explicada a seguir.",
                    "電車 (でんしゃ, densha, comboio): uma palavra composta de origem chinesa (on'yomi), 電 (でん) + 車 (しゃ). O rendaku é raro aqui.",
                ],
            },
            {
                text:
                    "Lei de Lyman: se a segunda parte já contém uma consoante sonora (um som b, d, g ou z), o " +
                    "rendaku normalmente não acontece. É por isso que 北風 se lê きたかぜ e não きたがぜ - o ぜ de " +
                    "かぜ já é sonoro. Compare com 折り紙 (おりがみ, origami), em que かみ não tem nenhum som " +
                    "sonoro, por isso か → が não tem problema.",
            },
            {
                text:
                    "O rendaku acontece sobretudo quando a segunda parte é uma palavra japonesa nativa (和語, わご, " +
                    "wago) - as que normalmente se leem com kun'yomi, como 紙, 空, 犬 e 棚. É por isso que 本棚 " +
                    "(ほんだな) sonoriza, apesar de 本 ser on'yomi. É raro em compostos formados só por partes de " +
                    "origem chinesa (on'yomi), e por isso 電車 é でんしゃ e não でんじゃ. É também raro em palavras " +
                    "estrangeiras escritas em katakana.",
            },
            {
                text:
                    "Estas duas tendências são uma simplificação para principiantes: os linguistas descrevem " +
                    "condições mais detalhadas, e ambas têm exceções.",
            },
            { heading: "Palavras repetidas com 々" },
            {
                text:
                    "O sinal de repetição 々 representa o kanji que o antecede, e a segunda cópia é muitas vezes " +
                    "sonorizada: 時々 (ときどき, tokidoki, \"às vezes\") e 人々 (ひとびと, hitobito, \"pessoas\"). " +
                    "Não é automático, e não pode acontecer quando o segundo som não tem par sonoro: 色々 " +
                    "(いろいろ, iroiro, \"variado\") continua いろいろ.",
            },
            { heading: "Como aprender mesmo estas palavras" },
            {
                list: [
                    "Aprenda a palavra composta como uma só palavra. Anote 手紙 como てがみ, e não como て + かみ - a leitura que vai realmente ver e ouvir é a sonorizada.",
                    "Quando uma palavra composta japonesa nativa é nova para si, espere uma segunda parte sonorizada se começar por um som k, s, t ou h, e depois confirme a leitura num dicionário ou nas listas de vocabulário. Enganar-se é normal - o padrão é uma tendência, não uma regra.",
                    "Se uma palavra cujas partes conhece soar ligeiramente diferente, é normalmente rendaku. As partes são as mesmas palavras que já conhece, com um som sonorizado.",
                ],
            },
            { heading: "Respostas rápidas" },
            {
                list: [
                    "O que é o rendaku? A sonorização do primeiro som da segunda parte de uma palavra composta, como こや → ごや em 犬小屋.",
                    "O rendaku é previsível? Em parte. Há tendências (palavras japonesas nativas, lei de Lyman), mas 出口 (でぐち) e 早口 (はやくち) mostram que ainda é preciso aprender cada palavra.",
                    "Porque é que 手紙 se lê てがみ e não てかみ? É て + かみ, e o か de かみ é sonorizado pelo rendaku.",
                    "O ぱ é um som de rendaku? Não. O rendaku só sonoriza com dakuten (゛); a linha ぱ vem de outros padrões, como os contadores das lições.",
                    "O rendaku acontece em palavras estrangeiras? Raramente.",
                ],
            },
            {
                parts: [
                    "Quer praticar? Faça a ",
                    { to: "/pt/lessons", label: "lição sobre rendaku" },
                    ", experimente ",
                    { to: "/pt/kana-writing-practice", label: "escrever à mão os kana sonoros" },
                    " ou imprima ",
                    { to: "/pt/worksheets?script=hiragana", label: "fichas de prática com が, ざ e ば" },
                    ". Se ainda está a perceber as duas escritas de kana, o nosso artigo sobre ",
                    { to: "/pt/blog/hiragana-vs-katakana", label: "hiragana vs katakana" },
                    " é um bom ponto de partida.",
                ],
            },
        ],
    },
    {
        slug: "verbos-godan-e-ichidan-grupos-de-verbos-em-japones",
        title: "Verbos Godan vs Ichidan: o que Significa Realmente 五段 (e Porque é que 売る é Godan)",
        date: "2026-10-01",
        lang: "pt-PT",
        excerpt:
            "Os verbos japoneses dividem-se em godan, ichidan e irregulares, e 五段 significa \"cinco passos\". " +
            "Veja os cinco passos com 売る, perceba porque é que terminar em る não torna um verbo ichidan e " +
            "aprenda a regra da forma-て para cada terminação godan.",
        tags: ["Grammar", "Beginner"],
        keywords: ["godan", "ichidan", "grupos de verbos", "conjugação", "gramática"],
        content: [
            {
                text:
                    "Já aprendeu que 食べる (たべる, taberu, \"comer\") passa a 食べます (tabemasu) quando se retira o る " +
                    "e se junta ます. Depois encontra 売る (うる, uru, \"vender\"), experimenta o mesmo truque e obtém " +
                    "売ます. Mas a forma correta é 売ります (urimasu). A diferença é que 食べる é um verbo ichidan e 売る " +
                    "é um verbo godan, e os verbos japoneses conjugam-se de forma diferente consoante o grupo a que " +
                    "pertencem.",
            },
            {
                parts: [
                    "Este artigo explica os três grupos de verbos, porque é que 五段 (godan) significa \"cinco " +
                    "passos\", como distinguir um verbo godan de um verbo ichidan quando ambos terminam em る e a regra " +
                    "da forma-て para cada terminação godan. É o complemento mais longo da ",
                    { to: "/pt/lessons?lesson=11.5", label: "Lição 11.5" },
                    " e da ",
                    { to: "/pt/grammar/n5-verb-groups", label: "página de gramática sobre os grupos de verbos" },
                    ".",
                ],
            },
            { heading: "Os três grupos de verbos" },
            {
                text:
                    "Todos os verbos japoneses pertencem a um de três grupos, e é o grupo que decide como se conjugam. " +
                    "Os livros usam nomes diferentes para os mesmos grupos, por isso aqui ficam lado a lado:",
            },
            {
                table: {
                    caption: "Os três grupos de verbos japoneses e os seus outros nomes",
                    headers: ["Nome japonês", "Designação nos manuais", "Também chamados", "A forma de dicionário termina em"],
                    rows: verbGroupNames.map((item, index) => [
                        `${item.japanese} (${item.reading}, ${item.romaji})`,
                        ...ptVerbGroupNames[index],
                    ]),
                },
            },
            {
                text:
                    "Os números dos grupos são uma convenção dos manuais. A maioria dos manuais de japonês como língua " +
                    "estrangeira usa o Grupo 1 para godan e o Grupo 2 para ichidan, mas alguns numeram-nos de outra " +
                    "forma, por isso, em caso de dúvida, guie-se pelos nomes japoneses ou por verbo em u e verbo em ru. " +
                    "Este site usa godan, ichidan e irregular, e menciona os outros nomes quando ajudam.",
            },
            {
                text:
                    "O ponto importante é que os verbos são classificados pela forma como se conjugam, e não " +
                    "simplesmente por terminarem ou não em る. Todas as formas de dicionário terminam num som em u, e " +
                    "os verbos godan podem terminar em qualquer uma de nove kana: う, く, ぐ, す, つ, ぬ, ぶ, む ou る. " +
                    "Os verbos ichidan terminam sempre em る.",
            },
            { heading: "O que significa 五段: cinco passos" },
            {
                text:
                    "段 (だん, dan) significa \"passo\" ou \"nível\". O quadro de kana (五十音図, ごじゅうおんず, " +
                    "gojūon-zu) organiza-se em cinco passos segundo a vogal: o passo あ (a), o passo い (i), o passo う " +
                    "(u), o passo え (e) e o passo お (o). 五段 (ごだん, godan) significa, portanto, \"cinco passos\": à " +
                    "medida que um verbo godan se conjuga, a sua última kana percorre os cinco passos.",
            },
            {
                text:
                    "Veja 売る (うる, uru, \"vender\"). A sua última kana é る, que pertence à linha ら do quadro: ら, " +
                    "り, る, れ, ろ. Tudo o que vem antes, 売, nunca muda. Só a última kana sobe e desce:",
            },
            {
                table: {
                    caption: "Os cinco passos do verbo godan 売る (うる, vender)",
                    headers: ["Passo (段)", "Forma", "Leitura", "Usa-se para"],
                    rows: godanFiveStepRows(ptGodanStepUses),
                },
            },
            {
                text:
                    "Leia a última kana ao longo da tabela: ら → り → る → れ → ろ. São os cinco passos, e é por isso " +
                    "que os verbos godan têm este nome. A forma de dicionário (売る) está no passo う, e é por isso que " +
                    "todos os verbos no dicionário terminam num som em u. As terminações (ない, ます, ば, う) são iguais " +
                    "em todos os verbos godan; só muda o passo em que está a última kana.",
            },
            { heading: "Os mesmos cinco passos para todas as terminações godan" },
            {
                text:
                    "Todas as terminações godan funcionam da mesma maneira, usando a sua própria linha do quadro de " +
                    "kana. 書く percorre か, き, く, け, こ; 住む percorre ま, み, む, め, も. Há apenas uma pequena " +
                    "particularidade: um verbo que termina em う, como 買う, usa わ em vez de あ no primeiro passo: " +
                    "買わない, e não 買あない.",
            },
            {
                table: {
                    caption: "Verbos godan de todas as terminações nos cinco passos",
                    headers: [
                        "Terminação",
                        "Verbo",
                        "Passo あ + ない",
                        "Passo い + ます",
                        "Passo う (dicionário)",
                        "Passo え + ば",
                        "Passo お + う",
                    ],
                    rows: godanEndingRows(),
                },
            },
            {
                text:
                    "Repare que 住む (すむ, sumu, \"viver num sítio\") segue exatamente a mesma lógica de 売る. Só " +
                    "precisa de saber a que linha do quadro pertence a última kana do verbo.",
            },
            { heading: "Ichidan: um só passo" },
            {
                text:
                    "一段 (いちだん, ichidan) significa \"um passo\". O radical de um verbo ichidan, tudo o que vem antes " +
                    "de る, nunca passa para outro passo. Fica no passo い (見る, 起きる) ou no passo え (食べる, 寝る). " +
                    "Para conjugar, retira-se o る e junta-se a terminação. A gramática escolar japonesa chama a estes " +
                    "verbos 上一段 e 下一段, os verbos de um passo \"superior\" e \"inferior\", por causa do passo い " +
                    "acima de う e do passo え abaixo dele.",
            },
            {
                table: {
                    caption: "Godan 売る comparado com ichidan 食べる",
                    headers: ["Forma", "Godan: 売る (うる)", "Ichidan: 食べる (たべる)"],
                    rows: godanVsIchidanRows(ptVerbFormLabels),
                },
            },
            {
                text:
                    "Na coluna godan, a última kana destacada percorre os cinco passos. Na coluna ichidan, o radical " +
                    "食べ nunca muda; o destaque mostra a terminação que se junta depois de retirar o る.",
            },
            { heading: "Verbos irregulares: する e 来る" },
            {
                text:
                    "Só dois verbos são verdadeiramente irregulares: する (suru, \"fazer\") e 来る (くる, kuru, " +
                    "\"vir\"). Não seguem nenhum dos dois padrões e têm de ser decorados. 来る é o mais complicado: o " +
                    "kanji 来 mantém-se, mas a sua leitura muda (こ, き, く, く, こ) de forma para forma. Todos os " +
                    "compostos com する, como 勉強する (べんきょうする, \"estudar\"), conjugam-se exatamente como する.",
            },
            {
                table: {
                    caption: "Os verbos irregulares する e 来る",
                    headers: ["Forma", "する (suru)", "来る (くる, kuru)"],
                    rows: irregularRows(ptVerbFormLabels),
                },
            },
            { heading: "A armadilha do る: porque é que 売る é godan, apesar de terminar em る" },
            {
                text:
                    "Os verbos ichidan terminam em る, por isso os principiantes assumem muitas vezes que todos os " +
                    "verbos que terminam em る são ichidan. Não são. 売る termina em る e é godan: a sua forma formal é " +
                    "売ります (urimasu), em que る passa a ります. Compare com 食べる, um verbo ichidan, em que る passa " +
                    "simplesmente a ます: 食べます (tabemasu). Um guia rápido para a forma de dicionário:",
            },
            {
                table: {
                    caption: "Como descobrir o grupo do verbo pela forma de dicionário",
                    headers: ["A forma de dicionário termina em", "Grupo", "Porquê", "Exemplos"],
                    rows: verbGroupRecognition.map((item, index) => [...ptVerbGroupRecognition[index], item.examples]),
                },
            },
            {
                text:
                    "Essa segunda linha é a resposta para 売る: う é um som em u, e os verbos ichidan só existem " +
                    "depois de um som い ou え, por isso 売る tem de ser godan. É na terceira linha que tem de ter " +
                    "cuidado.",
            },
            {
                text:
                    "Uma pequena lista de verbos comuns termina em る depois de um som い ou え e conjuga-se na " +
                    "mesma como godan. Parecem ichidan, mas não são, por isso aprenda-os como uma lista. As suas " +
                    "formas formais denunciam-nos: る passa a ります. A lista não está completa (há outros, como 減る, " +
                    "蹴る, 限る, 参る e 喋る): se um verbo que termina em い ou え mais る não constar dela, confirme-o " +
                    "na página de conjugação.",
            },
            {
                table: {
                    caption: "Verbos godan comuns que parecem verbos ichidan",
                    headers: ["Verbo", "Significado", "Forma formal"],
                    rows: godanLookAlikeRows(ptGodanLookAlikeMeanings),
                },
            },
            {
                text:
                    "Alguns pares de verbos têm até a mesma leitura, mas pertencem a grupos diferentes. Só os kanji e " +
                    "o contexto dizem qual é qual, por isso aprenda o grupo juntamente com a palavra:",
            },
            {
                table: {
                    caption: "Verbos com a mesma leitura mas grupos diferentes",
                    headers: ["Verbo", "Significado", "Grupo", "Forma formal"],
                    rows: verbSamePairRows(ptVerbSamePairMeanings),
                },
            },
            {
                text:
                    "Dois verbos godan têm ainda, cada um, uma irregularidade. 行く (いく, iku, \"ir\") é regular em tudo, " +
                    "exceto na forma-て e no passado (行って, 行った), e ある (\"existir\") tem a negativa informal ない " +
                    "em vez de あらない. Alguns verbos godan honoríficos, como いらっしゃる e くださる, têm uma forma " +
                    "formal irregular (いらっしゃいます, くださいます); vai encontrá-los mais tarde na linguagem " +
                    "formal e honorífica.",
            },
            {
                parts: [
                    "Não tem a certeza sobre um verbo? A ",
                    { to: "/pt/conjugation", label: "página Conjugação de Verbos" },
                    " indica se cada verbo é godan, ichidan ou irregular e mostra a respetiva tabela completa, por " +
                    "isso pode confirmar qualquer verbo de que tenha dúvidas.",
                ],
            },
            { heading: "Forma-て dos verbos godan: é a terminação que decide" },
            {
                text:
                    "A forma-て de um verbo godan depende da sua última kana. Historicamente, a forma-て era o passo い " +
                    "mais て (売りて), o que era incómodo de dizer e foi simplificado para 売って; o mesmo tipo de " +
                    "simplificação deu んで e いて. O resultado são cinco regras curtas:",
            },
            {
                table: {
                    caption: "Verbos godan na forma-て, agrupados por terminação",
                    headers: ["Mudança de som", "Forma de dicionário", "Forma-て", "Significado"],
                    rows: godanTeFormRows(ptTeFormChanges, ptTeFormMeanings),
                },
            },
            {
                text:
                    "行く (いく, iku, \"ir\") é a única exceção: pela regra do く devia passar a 行いて, mas a sua " +
                    "verdadeira forma-て é 行って (itte). 売る → 売って e 住む → 住んで seguem as mesmas regras de " +
                    "todos os outros verbos godan com essas terminações.",
            },
            {
                parts: [
                    "Os verbos ichidan limitam-se a retirar o る e a juntar て (食べる → 食べて), e os irregulares dão " +
                    "して (する) e 来て (きて, 来る). As mesmas mudanças de som formam também o passado informal " +
                    "(forma-た): 売った, 住んだ, 書いた. A ",
                    { to: "/pt/lessons?lesson=28", label: "lição sobre a forma-て" },
                    " aborda a forma-て por completo, com os padrões de frase que ela permite.",
                ],
            },
            { heading: "Frases de exemplo" },
            { text: "Verbos godan:" },
            {
                list: [
                    "古い本を売ります。 (ふるい ほんを うります, Furui hon o urimasu.) - Vou vender os meus livros velhos.",
                    "大切な本は売らない。 (たいせつな ほんは うらない, Taisetsu na hon wa uranai.) - Não vendo os livros que são importantes para mim. (linguagem informal)",
                    "いらない本を売ろう。 (いらない ほんを うろう, Iranai hon o urō.) - Vamos vender os livros de que não precisamos. (linguagem informal; forma formal: 売りましょう)",
                    "古い本を売って、新しい本を買います。 (ふるい ほんを うって、あたらしい ほんを かいます, Furui hon o utte, atarashii hon o kaimasu.) - Vou vender os meus livros velhos e comprar livros novos.",
                    "私は東京に住んでいます。 (わたしは とうきょうに すんでいます, Watashi wa Tōkyō ni sunde imasu.) - Vivo em Tóquio.",
                    "友達に手紙を書きます。 (ともだちに てがみを かきます, Tomodachi ni tegami o kakimasu.) - Escrevo uma carta a um amigo.",
                    "先生と日本語で話します。 (せんせいと にほんごで はなします, Sensei to nihongo de hanashimasu.) - Falo com o meu professor em japonês.",
                ],
            },
            { text: "Verbos ichidan:" },
            {
                list: [
                    "朝ご飯にパンを食べます。 (あさごはんに パンを たべます, Asagohan ni pan o tabemasu.) - Como pão ao pequeno-almoço.",
                    "六時に起きて、朝ご飯を食べます。 (ろくじに おきて、あさごはんを たべます, Rokuji ni okite, asagohan o tabemasu.) - Levanto-me às seis e tomo o pequeno-almoço.",
                    "毎日ニュースを見ます。 (まいにち ニュースを みます, Mainichi nyūsu o mimasu.) - Vejo as notícias todos os dias.",
                ],
            },
            { text: "Verbos irregulares:" },
            {
                list: [
                    "毎日、日本語を勉強します。 (まいにち、にほんごを べんきょうします, Mainichi, nihongo o benkyō shimasu.) - Estudo japonês todos os dias.",
                    "明日、友達が来ます。 (あした、ともだちが きます, Ashita, tomodachi ga kimasu.) - Amanhã vem cá um amigo.",
                ],
            },
            { text: "Verbos parecidos, com a mesma leitura mas um grupo diferente:" },
            {
                list: [
                    "家に帰ります。 (いえに かえります, Ie ni kaerimasu.) - Vou para casa. 帰る é godan: る passa a ります.",
                    "部屋を変えます。 (へやを かえます, Heya o kaemasu.) - Vou mudar de quarto. 変える é ichidan: る passa a ます.",
                    "髪を切ります。 (かみを きります, Kami o kirimasu.) - Vou cortar o cabelo. 切る é godan.",
                    "白いシャツを着ます。 (しろい シャツを きます, Shiroi shatsu o kimasu.) - Visto uma camisa branca. 着る é ichidan.",
                ],
            },
            { heading: "Como aprender os grupos" },
            {
                list: [
                    "Aprenda cada verbo novo juntamente com o seu grupo. Quando juntar 売る ao seu vocabulário, anote 売る (godan), e não apenas 売る.",
                    "Veja a kana antes de る. Depois de um som あ, う ou お, o verbo é godan. Depois de um som い ou え, é provavelmente ichidan - confirme a lista de exceções antes de decidir.",
                    "Diga a forma formal em voz alta. 売ります e 食べます soam diferente, e dizê-las treina essa diferença.",
                    "Aprenda as exceções como um grupo: 帰る, 入る, 走る, 切る, 知る, 要る, 滑る e 握る são todos godan.",
                    "Em caso de dúvida, consulte uma tabela de conjugação. Todos os verbos da página de conjugação mostram o respetivo grupo.",
                ],
            },
            { heading: "Respostas rápidas" },
            {
                list: [
                    "O que significa 五段? \"Cinco passos.\" A última kana de um verbo godan percorre os cinco passos あ, い, う, え, お do quadro de kana à medida que o verbo se conjuga, como em 売らない, 売ります, 売る, 売れば, 売ろう.",
                    "O que significa 一段? \"Um passo.\" Um verbo ichidan mantém o radical e limita-se a trocar o る por uma terminação.",
                    "売る é godan ou ichidan? Godan. Termina em る, mas o som antes de る é う, e os verbos ichidan só existem depois de um som い ou え.",
                    "Todos os verbos que terminam em る pertencem ao mesmo grupo? Não. Podem ser godan (売る, 帰る), ichidan (食べる, 見る) ou irregulares (する, 来る).",
                    "Qual é a forma-て de 住む? 住んで (すんで, sunde). む, ぶ e ぬ passam todos a んで.",
                    "Qual é a forma-て de 売る? 売って (うって, utte). う, つ e る passam todos a って.",
                    "Que verbos são do Grupo 1, do Grupo 2 e do Grupo 3? Na maioria dos manuais, os verbos godan são do Grupo 1, os verbos ichidan do Grupo 2, e する e 来る do Grupo 3.",
                ],
            },
            {
                parts: [
                    "Quer praticar? Faça a ",
                    { to: "/pt/lessons?lesson=11.5", label: "Lição 11.5" },
                    ", leia a ",
                    { to: "/pt/grammar/n5-verb-groups", label: "página de gramática sobre os grupos de verbos" },
                    " ou consulte qualquer verbo na ",
                    { to: "/pt/conjugation", label: "página de conjugação" },
                    ". Se o quadro de kana ainda é novidade para si, o nosso artigo sobre ",
                    { to: "/pt/blog/hiragana-vs-katakana", label: "hiragana vs katakana" },
                    " é um bom ponto de partida.",
                ],
            },
        ],
    },
    {
        slug: "guia-viagem-japao-portugal-documentos-entrada",
        title: "Antes de Viajar para o Japão a Partir de Portugal: Documentos, Entrada e Checklist",
        date: "2026-10-04",
        lang: "pt-PT",
        excerpt:
            "Um guia pré-viagem para residentes em Portugal: saiba quando é preciso visto, prepare os documentos de entrada e confirme saúde, medicação, alfândega e contactos antes de partir.",
        tags: ["Travel", "Beginner"],
        keywords: ["viagem ao Japão", "documentos de entrada", "passaporte", "visto", "Visit Japan Web"],
        content: [
            {
                callout:
                    "Última verificação: 4 de outubro de 2026. Os requisitos de fronteira podem mudar sem aviso. Confirme sempre os links oficiais deste guia imediatamente antes da partida; a decisão final de admissão cabe às autoridades japonesas.",
            },
            {
                text:
                    "Este guia é para residentes em Portugal que planeiam uma viagem curta ao Japão. A regra do visto depende da nacionalidade e do documento de viagem, não da residência em Portugal: ter autorização de residência portuguesa não transforma outro passaporte num passaporte português. Se não viajar com passaporte português, comece por confirmar o seu caso com a Embaixada do Japão em Portugal.",
            },
            { heading: "1. Entrada turística com passaporte português" },
            {
                text:
                    "Segundo a Embaixada do Japão em Portugal, quem tenha um passaporte português válido está isento de visto para turismo ou outras atividades não remuneradas até 90 dias. A isenção não cobre trabalho remunerado, mesmo numa estadia curta. Para estudar, trabalhar, residir, fazer working holiday ou permanecer mais tempo, siga o procedimento de visto adequado antes de viajar.",
            },
            {
                parts: [
                    "Confirme a regra e o seu objetivo de viagem na ",
                    { href: "https://www.pt.emb-japan.go.jp/itpr_ja/00_000231.html", label: "página atual de visto de estada temporária da Embaixada do Japão em Portugal" },
                    ".",
                ],
            },
            { heading: "2. Documentos para ter consigo" },
            {
                text:
                    "Leve os originais e guarde cópias digitais seguras. Não existe neste guia uma regra genérica de ‘seis meses’ para o passaporte: a fonte oficial para o caso português fala em passaporte válido. Verifique também as exigências da companhia aérea e de qualquer país de trânsito.",
            },
            {
                list: [
                    "Passaporte português válido durante toda a viagem, em bom estado, com os dados iguais aos da reserva aérea.",
                    "Bilhete de regresso ou de continuação e o itinerário; tenha-os acessíveis no telemóvel e, de preferência, também offline.",
                    "Moradas, contactos e confirmações do alojamento para a primeira noite e restantes estadias.",
                    "Prova dos meios de pagamento que vai usar e contacto de emergência; o controlo de fronteira pode avaliar se a finalidade declarada da viagem é coerente.",
                    "Se um menor sair de Portugal sem quem exerce as responsabilidades parentais, confirme se precisa de autorização de saída autenticada antes de partir.",
                ],
            },
            { heading: "3. Antes do voo: imigração, alfândega e Visit Japan Web" },
            {
                text:
                    "À chegada, os visitantes estrangeiros passam pelo controlo de imigração e, salvo exceções legais, fornecem impressões digitais e fotografia. Todos os passageiros devem declarar os seus bens à alfândega. A Japan Customs recomenda a declaração eletrónica através do Visit Japan Web; os formulários continuam disponíveis no avião, navio ou na alfândega.",
            },
            {
                list: [
                    "Crie ou reveja o registo no Visit Japan Web antes de embarcar; não dependa de Wi-Fi do aeroporto para recuperar dados importantes.",
                    "Declare sempre bens, bagagem desacompanhada e artigos sujeitos a restrições. Se tiver dúvidas, escolha o canal vermelho ou peça ajuda a um agente aduaneiro.",
                    "Se entrar ou sair do Japão com dinheiro ou meios de pagamento acima do equivalente a 1 000 000 de ienes, há declaração obrigatória à alfândega japonesa.",
                    "Se sair da União Europeia por Portugal com 10 000 € ou mais em dinheiro líquido, tem de declarar o montante à Alfândega portuguesa.",
                ],
            },
            {
                parts: [
                    "Use o ",
                    { href: "https://www.vjw-lp.digital.go.jp/en/", label: "Visit Japan Web oficial" },
                    " e consulte os ",
                    { href: "https://www.customs.go.jp/english/summary/passenger.htm", label: "procedimentos para passageiros da Japan Customs" },
                    ". Para a declaração na saída de Portugal, consulte o ",
                    { href: "https://info.portaldasfinancas.gov.pt/pt/apoio_ao_contribuinte/Cidadaos/Compras/Viajantes/Dinheiro_liquido/Paginas/default.aspx", label: "Portal das Finanças" },
                    ".",
                ],
            },
            { heading: "4. Saúde, seguro e medicação" },
            {
                text:
                    "À data de verificação deste guia, o Japão não exige certificado de vacinação contra a COVID-19, teste antes da partida, teste à chegada nem período de isolamento. Isto não substitui recomendações clínicas individuais. O seguro de viagem internacional não é apresentado pela Embaixada como requisito de entrada, mas é recomendado porque os custos médicos podem ser elevados.",
            },
            {
                list: [
                    "Marque uma consulta do viajante com antecedência se precisar de aconselhamento individual, vacinas ou gestão de doença crónica.",
                    "Leve receita, carta médica em inglês e a medicação na embalagem original. A Embaixada indica, em geral, até um mês de medicamento sujeito a receita e até dois meses de medicamento sem receita para uso pessoal; quantidades maiores e certas substâncias exigem confirmação ou autorização prévia.",
                    "Verifique especificamente psicotrópicos, estimulantes, narcóticos, dispositivos médicos e medicamentos que possam ser controlados. Não presuma que um medicamento legal em Portugal pode entrar no Japão sem formalidades.",
                    "Escolha seguro com despesas médicas, assistência 24 horas, repatriamento e, se lhe fizer diferença, encaminhamento para cuidados com apoio linguístico ou pagamento direto.",
                ],
            },
            {
                parts: [
                    "Consulte a orientação da ",
                    { href: "https://www.pt.emb-japan.go.jp/itpr_ja/00_000722.html", label: "Embaixada sobre saúde e transporte de medicação" },
                    ", a recomendação oficial de ",
                    { href: "https://www.pt.emb-japan.go.jp/itpr_ja/00_000719.html", label: "seguro de viagem" },
                    " e as ",
                    { href: "https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/0000121431_00209.html", label: "medidas de fronteira do Ministério da Saúde japonês" },
                    ".",
                ],
            },
            { heading: "5. Alimentos, plantas, animais e artigos restritos" },
            {
                text:
                    "Não deixe esta verificação para a fila da chegada. Carne e produtos de origem animal, fruta, legumes, sementes e plantas podem estar proibidos ou sujeitos a inspeção. Animais de companhia exigem preparação muito antecipada: para cães e gatos vindos de regiões não designadas, o processo oficial inclui microchip, vacinas contra a raiva, teste de anticorpos, espera de 180 dias e notificação prévia de pelo menos 40 dias.",
            },
            {
                list: [
                    "Não embale enchidos, presunto, carne, fruta, vegetais, sementes ou plantas sem confirmar primeiro a regra aplicável.",
                    "Declare artigos de valor, tabaco, álcool e bagagem enviada separadamente conforme as regras aduaneiras; as franquias e restrições não são a mesma coisa.",
                    "Se viajar com um animal, comece a preparação vários meses antes e siga apenas o processo da Animal Quarantine Service japonesa e da autoridade veterinária portuguesa.",
                ],
            },
            {
                parts: [
                    "Veja a página da Embaixada sobre ",
                    { href: "https://www.pt.emb-japan.go.jp/itpr_pt/00_000723.html", label: "animais, plantas, carne, fruta e legumes" },
                    " e o guia oficial da ",
                    { href: "https://www.maff.go.jp/aqs/english/animal/dog/import-other.html", label: "Animal Quarantine Service para cães e gatos" },
                    ".",
                ],
            },
            { heading: "Checklist final" },
            {
                list: [
                    "Confirmar nacionalidade, finalidade e duração; pedir visto se a isenção não se aplicar.",
                    "Confirmar passaporte, voos de ida e saída, trânsito, alojamento e contactos de emergência.",
                    "Guardar cópias seguras do passaporte, seguro, reservas e prescrições; partilhar o itinerário com alguém de confiança.",
                    "Preencher ou preparar o Visit Japan Web e rever a declaração aduaneira.",
                    "Rever medicação, alimentos, plantas, animais, dinheiro e bens de valor antes de fazer a mala.",
                    "Contratar e ler o seguro; saber como pedir assistência e ter o número da apólice disponível offline.",
                    "Voltar a abrir as fontes oficiais 48–72 horas antes da partida, incluindo as regras da companhia aérea e do trânsito.",
                ],
            },
            { heading: "Fontes oficiais verificadas" },
            {
                parts: [
                    { href: "https://www.pt.emb-japan.go.jp/itpr_ja/00_000231.html", label: "Embaixada do Japão em Portugal — Visto de estada temporária" },
                    "; ",
                    { href: "https://www.pt.emb-japan.go.jp/itpr_ja/00_000240.html", label: "Embaixada do Japão em Portugal — Procedimentos à entrada" },
                    "; ",
                    { href: "https://www.customs.go.jp/english/summary/passenger.htm", label: "Japan Customs — Procedures of Passenger Clearance" },
                    "; ",
                    { href: "https://www.pt.emb-japan.go.jp/itpr_ja/00_000722.html", label: "Embaixada do Japão em Portugal — Saúde no Japão" },
                    "; ",
                    { href: "https://info.portaldasfinancas.gov.pt/pt/apoio_ao_contribuinte/Cidadaos/Compras/Viajantes/Dinheiro_liquido/Paginas/default.aspx", label: "Portal das Finanças — Dinheiro líquido" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "keigo-linguagem-de-respeito-em-japones",
        title: "Keigo Explicado: Teineigo, Sonkeigo e Kenjōgo",
        date: "2026-10-08",
        lang: "pt-PT",
        excerpt:
            "O keigo (敬語) é a forma como o japonês mostra respeito. Conheça os três tipos, teineigo, sonkeigo " +
            "e kenjōgo, com exemplos simples e uma pergunta-chave.",
        tags: ["Grammar", "Beginner"],
        keywords: ["keigo", "teineigo", "sonkeigo", "kenjōgo", "linguagem honorífica japonesa", "japonês educado"],
        content: [
            {
                text:
                    "O keigo (敬語, けいご) é a forma como o japonês mostra respeito através da escolha das " +
                    "palavras e das formas verbais. Tem fama de ser difícil, mas um modelo simples leva-o longe. " +
                    "Existem três tipos. Para o sonkeigo e o kenjōgo, a pergunta-chave é: de quem é esta ação e a " +
                    "quem estou a mostrar respeito? O teineigo é mais simples: é apenas cortesia para com a " +
                    "pessoa com quem está a falar.",
            },
            {
                table: {
                    caption: "Os três tipos de keigo",
                    headers: ["Tipo", "Japonês", "Finalidade"],
                    rows: [
                        ["Teineigo", "丁寧語 (ていねいご)", "Fala educada"],
                        ["Sonkeigo", "尊敬語 (そんけいご)", "Fala respeitosa, eleva a outra pessoa"],
                        ["Kenjōgo", "謙譲語 (けんじょうご)", "Fala humilde, baixa as suas próprias ações"],
                    ],
                },
            },
            { heading: "Teineigo (丁寧語): a fala educada do dia a dia" },
            {
                text:
                    "O teineigo é o estilo educado que se aprende primeiro: frases que terminam em です e verbos " +
                    "que terminam em ます. Não eleva nem baixa ninguém em particular. Compare a forma simples " +
                    "勉強する (べんきょうする, \"estudar\") com a forma educada 勉強します.",
            },
            {
                list: [
                    "私は学生です。 (Watashi wa gakusei desu.) - Sou estudante.",
                    "毎日日本語を勉強します。 (Mainichi nihongo o benkyō shimasu.) - Estudo japonês todos os dias.",
                ],
            },
            {
                parts: [
                    "Esta é a base, e o estilo a usar com professores, funcionários de lojas e qualquer pessoa " +
                    "que não conheça bem. Se o ます ainda é novidade, comece pela ",
                    { to: "/pt/grammar/n5-masu-polite-present", label: "página de gramática do ます" },
                    ".",
                ],
            },
            { heading: "Sonkeigo (尊敬語): elevar a outra pessoa" },
            {
                text:
                    "O sonkeigo mostra respeito elevando as ações de outra pessoa. Use-o para o que a outra " +
                    "pessoa faz, como um professor ou um cliente, ou o seu chefe quando fala dentro da sua " +
                    "própria empresa, e nunca para as suas próprias ações. Vários verbos do dia a dia têm uma " +
                    "forma especial de sonkeigo:",
            },
            {
                list: [
                    "食べる, 飲む (たべる, のむ, comer, beber) → 召し上がる (めしあがる)",
                    "行く, 来る, いる (ir, vir, estar) → いらっしゃる",
                    "言う (いう, dizer) → おっしゃる",
                    "見る (みる, ver) → ご覧になる (ごらんになる)",
                    "する (fazer) → なさる",
                ],
            },
            {
                list: [
                    "先生はコーヒーを召し上がります。 (Sensei wa kōhī o meshiagarimasu.) - O professor vai tomar um café.",
                    "社長はいらっしゃいますか。 (Shachō wa irasshaimasu ka.) - O presidente está?",
                ],
            },
            {
                text:
                    "Muitos outros verbos seguem um padrão: お + o radical de ます + になる. Por exemplo, 読む " +
                    "(よむ, \"ler\") passa a お読みになる (およみになる). Antes de ます, いらっしゃる, おっしゃる e " +
                    "なさる ficam com い em vez de り: いらっしゃいます, おっしゃいます, なさいます.",
            },
            { heading: "Kenjōgo (謙譲語): baixar o seu próprio lado" },
            {
                text:
                    "O kenjōgo mostra respeito a partir da direção oposta: baixa as suas próprias ações, ou as do " +
                    "seu lado, como a sua família ou a sua empresa, para que a outra pessoa fique elevada por " +
                    "comparação. Quando fala com alguém de fora da sua empresa ou família, o seu próprio chefe ou " +
                    "os seus pais também contam como o seu lado, por isso baixa-os em vez de os elevar. Use-o " +
                    "para o que faz, sobretudo quando envolve a outra pessoa (visitá-la, dar-lhe ou mostrar-lhe " +
                    "algo, receber algo dela), e nunca para o que a outra pessoa faz. Muitas ações do dia a dia, " +
                    "como 勉強する, não têm uma forma especial de kenjōgo e ficam simplesmente em teineigo. Estes " +
                    "verbos têm formas especiais:",
            },
            {
                list: [
                    "食べる, 飲む (comer, beber) → いただく (também \"receber\")",
                    "行く, 来る (ir, vir) → 参る (まいる) ou 伺う (うかがう)",
                    "言う (dizer) → 申す (もうす) ou 申し上げる (もうしあげる)",
                    "見る (ver) → 拝見する (はいけんする)",
                    "する (fazer) → いたす",
                ],
            },
            {
                list: [
                    "私はコーヒーをいただきます。 (Watashi wa kōhī o itadakimasu.) - Vou tomar um café.",
                    "私は田中と申します。 (Watashi wa Tanaka to mōshimasu.) - Chamo-me Tanaka. (A apresentação pessoal humilde habitual.)",
                    "明日伺います。 (Ashita ukagaimasu.) - Amanhã vou fazer-lhe uma visita.",
                ],
            },
            {
                text:
                    "O padrão correspondente é お + o radical de ます + する. Por exemplo, 持つ (もつ, " +
                    "\"segurar, levar\") passa a お持ちする (おもちする), e お持ちします significa \"levo isto por " +
                    "si\". Use 伺う quando vai visitar a pessoa a quem mostra respeito (a casa, o escritório ou o " +
                    "local dela). 参る é mais geral: diz simplesmente \"ir\" ou \"vir\" de forma humilde e pode " +
                    "usar-se para qualquer destino.",
            },
            { heading: "Os mesmos verbos lado a lado" },
            {
                text:
                    "Eis os mesmos verbos em cada nível. Com exceção da forma simples, todas as colunas estão na " +
                    "forma ます.",
            },
            {
                table: {
                    caption: "Os mesmos verbos na forma simples, em teineigo, sonkeigo e kenjōgo",
                    headers: ["Significado", "Forma simples", "Teineigo", "Sonkeigo", "Kenjōgo"],
                    rows: [
                        ["comer / beber", "食べる / 飲む", "食べます / 飲みます", "召し上がります (めしあがります)", "いただきます"],
                        ["dizer", "言う", "言います", "おっしゃいます", "申します (もうします)"],
                        ["ir", "行く", "行きます", "いらっしゃいます", "参ります (まいります) / 伺います (うかがいます)"],
                        ["ver", "見る", "見ます", "ご覧になります (ごらんになります)", "拝見します (はいけんします)"],
                        ["fazer", "する", "します", "なさいます", "いたします"],
                    ],
                },
            },
            { heading: "Um erro comum" },
            {
                callout:
                    "Não use sonkeigo para si próprio. ×私がいらっしゃいます está errado porque o eleva a si; diga " +
                    "antes 私が参ります (Watashi ga mairimasu, \"eu vou\"). Do mesmo modo, nunca use kenjōgo para as " +
                    "ações da outra pessoa. Em caso de dúvida, volte à pergunta: se a ação é dela, use sonkeigo; " +
                    "se é sua e envolve a outra pessoa (visitar, dar, mostrar, pedir), use kenjōgo; para tudo o " +
                    "resto sobre si, basta o teineigo normal. O teineigo costuma estar por baixo, porque os " +
                    "verbos de sonkeigo e kenjōgo se combinam normalmente com ます.",
            },
            { heading: "Onde vai encontrar o keigo" },
            {
                text:
                    "Vai ouvir keigo em lojas e restaurantes, no trabalho, em e-mails e em anúncios. " +
                    "いらっしゃいませ (Irasshaimase, \"Bem-vindo\") usa o verbo de sonkeigo いらっしゃる, e " +
                    "申し訳ございません (もうしわけございません, mōshiwake gozaimasen, \"Lamento muito\") é um " +
                    "pedido de desculpa muito educado. Até o いただきます, dito antes de uma refeição, vem do " +
                    "verbo de kenjōgo いただく, que significa literalmente \"receber com humildade\".",
            },
            {
                parts: [
                    "Domine primeiro です e ます, porque o levam longe na maioria das conversas. Depois aprenda " +
                    "algumas frases feitas dos outros dois tipos, como 〜と申します, いただきます e " +
                    "いらっしゃいませ. Alguns " +
                    "manuais, e a diretriz do governo japonês de 2007 (que tem cinco), usam mais categorias: o " +
                    "kenjōgo divide-se em dois, e é por isso que 伺う e 参る se comportam de forma diferente, e " +
                    "um quinto tipo abrange o お educado em palavras como お茶. Três é o melhor ponto de partida. " +
                    "Para ver como qualquer verbo recebe ます, use a ",
                    { to: "/pt/conjugation", label: "página de Conjugação de Verbos" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "learn-japanese-with-tanaka-san-youtube-channel",
        ptSlug: "canal-youtube-tanaka-san-japones",
        title: "Learn Japanese with Tanaka san on YouTube: An Independent Guide",
        date: "2026-10-10",
        excerpt:
            "An independent guide to the Learn Japanese with Tanaka san YouTube channel: its series, subtitles " +
            "and free extras, where to start, and a study routine.",
        tags: ["Study Tips", "Beginner"],
        keywords: [
            "Learn Japanese with Tanaka san",
            "Japanese YouTube channel",
            "Japanese listening practice",
            "shadowing practice Japanese",
            "Egg-san's Diary",
            "Tanaka Radio",
        ],
        content: [
            {
                parts: [
                    "\"Learn Japanese with Tanaka san\" is a YouTube channel built around two recurring illustrated " +
                        "characters: Tanaka-san, a tuna sushi nigiri, and Egg-san, an egg sushi nigiri who is " +
                        "learning Japanese from her, as described on the ",
                    { href: "https://www.youtube.com/@japanese_tanakasan", label: "channel's own page" },
                    ". This is an independent write-up from NihongoTech - we have no partnership, sponsorship or " +
                        "affiliation with the channel or its creator. Nothing below reproduces her scripts or " +
                        "transcripts. The facts come from the channel's own pages, her website and her own " +
                        "introduction on ",
                    { href: "https://note.com/japanese_tanaka/n/n2f701618c871", label: "note.com" },
                    ", and we checked them on 10 October 2026, so details such as episode counts, captions and " +
                        "prices may have changed since.",
                ],
            },
            { heading: "Who's behind the channel" },
            {
                text:
                    "In her first note.com post, published on 31 March 2024, the creator - who goes by Tanaka and " +
                    "lists Japan as her home - says she makes the videos mostly on her own, from voice recording " +
                    "and illustration to subtitles and editing, and is thinking about building a team. For longer " +
                    "videos she sometimes asks translators to write the English subtitles. She estimates she has " +
                    "made more than 100 characters in total (she has never counted, and some were made together " +
                    "with viewers), far beyond Tanaka-san and Egg-san. Her stated guiding question is " +
                    "どうしたら飽きずに楽しく学習を続けることができるか - roughly, \"how can learners keep " +
                    "going without getting bored, and enjoy it.\" She describes the channel as " +
                    "illustrated reading material for beginners and radio-style talk for intermediate and " +
                    "advanced learners.",
            },
            {
                parts: [
                    "Beyond YouTube, the same post mentions a self-published four-panel comic book that teaches " +
                        "onomatopoeia, character goods (stickers, keychains, can badges) that she sold at a café in " +
                        "Kyoto, and an online community where learners study together at set times. Her ",
                    { href: "https://japanese-tanaka-san.com/", label: "official website" },
                    ", linked from her video descriptions, names that community Tanaka Club (members-only, joined " +
                        "through a YouTube channel membership) and lists her books, including Handy Japanese " +
                        "Conversations, which it describes as a beginner-level book that teaches practical phrases " +
                        "through four-panel manga, with explanations in English. The channel's About tab shows it " +
                        "joined YouTube on 5 September 2020.",
                ],
            },
            { heading: "What's actually on the channel" },
            {
                text:
                    "The channel's Videos tab and playlists show several clearly labelled recurring series rather " +
                    "than disconnected one-off uploads:",
            },
            {
                list: [
                    "Egg-san's Diary (titled \"Easy Japanese Story\" on each video) - short, illustrated beginner " +
                        "stories following Egg-san's daily life, 20 episodes when we checked. The creator's playlist " +
                        "description calls them \"Easy Japanese stories for N5 learners,\" and her website gives " +
                        "every episode a one-to-three-star rating and one key grammar point.",
                    "Listen, Dictate, Shadow! (Japanese Mini Conversation) - short, natural dialogues, four " +
                        "episodes when we checked, each repeated through the same practice sequence (more on that " +
                        "below).",
                    "Tanaka Radio, 26 numbered episodes when we checked, plus the spin-offs Bedtime Tanaka Radio " +
                        "and Tanaka Radio Bonus Talk - longer, podcast-style talk about daily life, language " +
                        "learning and listener letters.",
                    "Conversation Practice and Japanese Short Story videos, such as \"Ordering Fast Food in " +
                        "Japanese\" and \"Capsule Toys in Japan.\"",
                    "Kanji Camp flashcard videos - short drills built from the kanji in each block of days in the " +
                        "creator's Kanji Camp PDF series (more on that below).",
                    "Standalone vocabulary videos, such as \"50 Useful Japanese Compliment Phrases.\"",
                ],
            },
            {
                text:
                    "Captions vary by video. Every spoken-dialogue video we checked - Egg-san's Diary #19, " +
                    "Listen, Dictate, Shadow! #4 and Tanaka Radio #26, plus the Ordering Fast Food video - has " +
                    "manually added English and Japanese subtitles, and most also have Japanese auto-generated " +
                    "captions. The two on-screen-text videos we checked (a Kanji Camp flashcard video and the " +
                    "Compliment Phrases video) had no caption track at all, though both put the reading or " +
                    "meaning on screen. And not everything is public: some Tanaka Radio Bonus Talk episodes and " +
                    "creator-talk uploads carry a \"Members only\" badge, and her community page lists " +
                    "members-only videos and early access among the perks of a paid YouTube channel membership.",
            },
            { heading: "Free and paid extras" },
            {
                text:
                    "The videos are free to watch, but the extras around them are a mix, so it helps to know " +
                    "what you're getting before you click:",
            },
            {
                list: [
                    "Egg-san's Diary: her website calls the series completely free, supported by donations from " +
                        "learners and teachers. Each episode has a PDF with a Reading Script (no furigana, no " +
                        "explanations) and a Learning Script (with furigana and grammar explanations).",
                    "Listen, Dictate, Shadow!: the two episodes we checked, #1 and #4, each link a free " +
                        "transcript PDF.",
                    "Tanaka Radio: the episode we checked (#26) links a free vocabulary and phrases PDF. Its full " +
                        "transcript with furigana is a paid download (€2.68 when we checked), free to Premium-level " +
                        "Tanaka Club members.",
                    "Kanji Camp: a paid PDF series, sold as separate PDFs or a bundle, with a free sample of the " +
                        "complete Day 1 lesson. Her website says it covers the 240 kanji taught in the first two " +
                        "grades of Japanese elementary school - a school-grade list, not a JLPT-level list.",
                ],
            },
            { heading: "A closer look at six videos" },
            { heading: "Egg-san's Diary #19 - \"A Visitor at Midnight\" (beginner)" },
            {
                parts: [
                    "A two-minute illustrated short story ",
                    { href: "https://www.youtube.com/watch?v=GUEFm_lt-A0", label: "(watch on YouTube)" },
                    " over simple artwork, with English and Japanese subtitles available. Her website rates this " +
                        "episode two stars out of three and lists 〜かもしれない as its key grammar point, and a " +
                        "free transcript PDF is linked from the description. It's a good test of whether the " +
                        "format suits you: if you can follow along with the Japanese captions on, the rest of " +
                        "the playlist, which the creator pitches at N5 learners, should feel approachable too. If " +
                        "it feels too fast, step back to episode #1 (see the playlists below), though its key " +
                        "grammar point, 〜なければいけない, is an N4 pattern on our grammar pages.",
                ],
            },
            { heading: "おすすめは何ですか？- Listen, Dictate, Shadow! #4 (beginner to intermediate)" },
            {
                parts: [
                    "A roughly seven-minute episode ",
                    { href: "https://www.youtube.com/watch?v=S4CHGm0ouPo", label: "(watch on YouTube)" },
                    " - the title means roughly \"What do you recommend?\" - and the clearest example of the " +
                        "channel's most structured format. Its own description breaks the episode into six " +
                        "timestamped steps: listen without subtitles, listen with English subtitles, dictation, " +
                        "check your answers, read aloud, then shadowing, with a short \"Tips from Tanaka\" " +
                        "segment on the title phrase partway through. You don't have to copy their timestamps " +
                        "exactly - the idea worth borrowing is reusing the same short conversation for several " +
                        "different kinds of practice instead of watching it once and moving on.",
                ],
            },
            { heading: "Tanaka Radio #26 - \"I Don't Like Summer, but I Want to Enjoy It\" (intermediate to advanced)" },
            {
                parts: [
                    "A roughly six-and-a-half-minute, monologue-style episode ",
                    { href: "https://www.youtube.com/watch?v=8ukRMLt5nY8", label: "(watch on YouTube)" },
                    ", with English and Japanese subtitles available. The description's timestamps split it " +
                        "into the creator's own talk (roughly the first five minutes) and a section " +
                        "of letters from listeners. In her note.com introduction she describes the radio as " +
                        "aimed at intermediate and advanced learners (中〜上級者向けのラジオ), so treat it as a " +
                        "next step once graded beginner stories start feeling too slow, not a starting point. A " +
                        "free vocabulary and phrases PDF is linked; the full transcript is the paid extra.",
                ],
            },
            { heading: "Kanji Camp: flashcard videos and the paid PDF series" },
            {
                parts: [
                    "The Kanji Camp flashcard videos, such as ",
                    { href: "https://www.youtube.com/watch?v=xmhc12cGfuE", label: "Kanji Flashcards | Kanji Camp 1 (Day 1-7)" },
                    ", run about four and a half minutes and use the kanji from days 1 to 7 of the first Kanji " +
                        "Camp PDF as on-screen flashcards, split into five \"levels.\" There's no caption track " +
                        "on this one, but the card we sampled showed the kanji with its hiragana reading above " +
                        "and an English meaning below. The channel's own explainer, ",
                    { href: "https://www.youtube.com/watch?v=nSOQdg28Btg", label: "\"Learn Kanji the Fun Way: New Kanji Camp is Here!\"" },
                    " (about four minutes, with English and Japanese captions), introduces the series as PDFs " +
                        "that teach kanji through manga. According to her website, each lesson has a video, a " +
                        "two-page manga, a kanji list, a worksheet and a vocabulary list, and the PDFs themselves " +
                        "are paid, so the free videos are a taster rather than the whole course.",
                ],
            },
            { heading: "\"50 Useful Japanese Compliment Phrases\" (beginner to intermediate vocabulary)" },
            {
                parts: [
                    "A phrase list of just under nine minutes ",
                    { href: "https://www.youtube.com/watch?v=N0O6VO5AvNU", label: "(watch on YouTube)" },
                    " with no caption track at the time of checking. That matters less than usual, because the " +
                        "frame we sampled showed an English line followed by a Formal and a Casual Japanese " +
                        "version, with furigana over the kanji. It's a reasonable source of ready-to-use phrases " +
                        "for the vocabulary step of the routine below, and the formal/casual pairing is a handy " +
                        "way to notice register - our ",
                    { to: "/blog/keigo-teineigo-sonkeigo-kenjogo-explained", label: "keigo guide" },
                    " explains the polite です/ます style that formal Japanese is built on.",
                ],
            },
            { heading: "Three playlists to start with" },
            {
                text:
                    "If you'd rather start from a playlist than hunt for individual episodes, these three cover " +
                    "the channel's main listening formats, from graded beginner stories to podcast-style talk:",
            },
            {
                parts: [
                    { href: "https://www.youtube.com/playlist?list=PLkK7KO2TnEcxrvBgXstE_jby8ylv-RGCl", label: "Egg-san's Diary | Easy Japanese Stories" },
                    " - the creator's own description pitches this one at N5 learners, so it's a sensible first " +
                        "stop if you're newer to Japanese. Start at the beginning with ",
                    { href: "https://www.youtube.com/watch?v=eXgMsg8WouU", label: "episode #1, \"Cleaning Day\"" },
                    ", which her website rates one star out of three (key grammar 〜なければいけない), rather " +
                        "than at the newest upload.",
                ],
            },
            {
                parts: [
                    { href: "https://www.youtube.com/playlist?list=PLkK7KO2TnEcy9N74F7IRw0Dcmg2NAnvMj", label: "Listen, Dictate, Shadow! (Japanese Mini Conversation)" },
                    " - the playlist to pick if you want the structured listen/dictate/shadow method itself: " +
                        "the series description says each dialogue is repeated through listening, dictation and " +
                        "shadowing. Episode #4's description points newcomers to ",
                    { href: "https://www.youtube.com/watch?v=4x_mcq67ing", label: "episode #1, うん・ううん (casual \"yes\" / \"no\")" },
                    " as the place to start.",
                ],
            },
            {
                parts: [
                    { href: "https://www.youtube.com/playlist?list=PLkK7KO2TnEczjRVTgW2fSGxRgWay3Z5a4", label: "Tanaka Radio | Japanese Podcast" },
                    " - described on YouTube as a cozy podcast about daily life, language learning and Japanese " +
                        "culture. It's the one to move on to once short graded stories feel easy, for longer, " +
                        "less-simplified listening practice.",
                ],
            },
            { heading: "A study routine built around these videos" },
            {
                text:
                    "The channel's own Listen, Dictate, Shadow! format already bakes in a useful idea: reuse the " +
                    "same short clip for several different kinds of practice instead of watching it once. Here's " +
                    "a routine that borrows that idea and loops back into NihongoTech's own tools for the parts " +
                    "a YouTube video can't check for you.",
            },
            {
                list: [
                    "First pass, no subtitles: watch one short episode (an Egg-san's Diary story or a " +
                        "Listen, Dictate, Shadow! conversation) with captions off and just notice how much you " +
                        "catch by ear.",
                    "Second pass, English captions: replay it with English subtitles to fill the gaps in what " +
                        "you heard. Keep the Japanese text hidden for now.",
                    "Dictation: pause every line or two and write down what you hear before you look at any " +
                        "Japanese text, then check your answers against the Japanese captions or the free " +
                        "transcript PDF - this is the step that turns passive listening into active recall, and " +
                        "it only works if you haven't seen the script first.",
                    "Shadowing: play it again and speak each line aloud right behind the audio, matching rhythm " +
                        "and pace rather than just reading along.",
                    "Vocabulary notes: pull five to ten new words or set phrases per video into a running list, " +
                        "rather than every word in the episode - a two-minute Egg-san's Diary story or the " +
                        "Compliment Phrases video is a reasonable daily amount.",
                    "Replay later: come back to the same clip a few days on, with captions off, and see how much " +
                        "more you catch than the first time.",
                ],
            },
            {
                parts: [
                    "Egg-san's Diary also pairs well with grammar reference, because her website names one key " +
                        "grammar point per episode and several of them have a page here: episode #2 (〜ませんか) " +
                        "matches our ",
                    { to: "/grammar/n5-masenka", label: "ませんか page" },
                    ", episode #3 (〜たい) our ",
                    { to: "/grammar/n5-tai-desu", label: "たいです page" },
                    ", episode #16 (〜たり、〜たり) our ",
                    { to: "/grammar/n4-tari-tari", label: "たり〜たりします page" },
                    " and episode #20 (〜ので) our ",
                    { to: "/grammar/n4-node", label: "ので page" },
                    ". Episode #1's 〜なければいけない means the same as 〜なければなりません (\"must\" or " +
                        "\"have to\"), though いけない sounds a little more conversational - see our ",
                    { to: "/grammar/n4-nakereba-narimasen", label: "なければなりません page" },
                    ".",
                ],
            },
            {
                parts: [
                    "Then close the loop with tools that check the parts a video can't: if a word you logged is " +
                        "in the JLPT lists, say it aloud on ",
                    { to: "/speaking-practice", label: "Speaking Practice" },
                    " and let your browser check it, look the rest up with full readings in the JLPT-tagged ",
                    { to: "/vocabulary", label: "vocabulary list" },
                    ", read a short story on ",
                    { to: "/reading-practice", label: "Reading Practice" },
                    " once Egg-san's Diary feels comfortable, and turn any kanji you noticed in a Kanji Camp " +
                        "video into something you'll actually retain with ",
                    { to: "/kanji-roulette", label: "Kanji Roulette" },
                    ". If Egg-san's Diary still feels fast, NihongoTech's own ",
                    { to: "/lessons", label: "N5 lessons" },
                    " cover the hiragana, katakana and core vocabulary and grammar it builds on, though some " +
                        "episodes' key grammar - 〜なければいけない, 〜たり、〜たり and 〜ので - sits at N4 on our " +
                        "grammar pages, so a few episodes will stretch an N5 learner.",
                ],
            },
            { heading: "Where it fits next to structured study and JLPT prep" },
            {
                parts: [
                    "Think of the channel as listening and shadowing practice that sits beside a structured " +
                        "course, not instead of one. The JLPT includes a listening section at every level, so " +
                        "regular listening practice helps whichever level you're aiming for, but nothing we found " +
                        "from the creator describes the videos as built for, or approved for, JLPT preparation. " +
                        "Use our ",
                    { to: "/learning-roadmap", label: "learning roadmap" },
                    " to decide what to study in what order, and let the videos supply the ear training.",
                ],
            },
            { heading: "Strengths and limitations" },
            { text: "Strengths:" },
            {
                list: [
                    "Manually added English and Japanese subtitles on every spoken-dialogue video we checked, " +
                        "not just one language or auto-captions alone.",
                    "A genuinely varied spread of formats from one channel - graded beginner stories, " +
                        "structured mini-conversations, longer intermediate/advanced podcast talk, and kanji " +
                        "flashcards - useful for mixing listening, reading and speaking instead of just one.",
                    "Free transcript PDFs for Egg-san's Diary and the Listen, Dictate, Shadow! episodes we " +
                        "checked, so you can check your dictation without paying for anything.",
                    "A back catalogue going back to September 2020, so there's plenty to work through once " +
                        "you're past the newest uploads.",
                ],
            },
            { text: "Limitations:" },
            {
                list: [
                    "No JLPT-aligned curriculum that we could find. The \"N5\" label is the creator's own " +
                        "description of the Egg-san's Diary playlist; her website rates episodes with one to " +
                        "three stars and a key grammar point rather than JLPT levels, and Kanji Camp follows " +
                        "Japanese school grades. Pair the channel with a structured resource if you need " +
                        "level-by-level JLPT coverage.",
                    "Captions aren't universal: the on-screen flashcard and phrase-list videos we checked had " +
                        "no caption track at all.",
                    "Some of the deepest extras are paid - the Tanaka Radio full transcripts, the Kanji Camp PDFs " +
                        "and the Tanaka Club membership - though the videos and the Egg-san's Diary and " +
                        "Listen, Dictate, Shadow! transcripts are free.",
                    "Some uploads are members-only, so not every video is available to everyone.",
                    "By the creator's own account this is largely a one-person operation, so upload pace and " +
                        "depth depend on that.",
                ],
            },
            { heading: "Credit, and an honest disclosure" },
            {
                parts: [
                    "Everything above comes from what we actually watched and read on the ",
                    { href: "https://www.youtube.com/@japanese_tanakasan", label: "channel's own YouTube page" },
                    ", her ",
                    { href: "https://japanese-tanaka-san.com/", label: "official website" },
                    " and her own introduction on ",
                    { href: "https://note.com/japanese_tanaka/n/n2f701618c871", label: "note.com" },
                    ". All credit for the channel, its characters and its content goes to its creator, Tanaka. " +
                        "This page is an independent recommendation from NihongoTech - we have no partnership, " +
                        "sponsorship or endorsement relationship with the channel, and nothing here was provided " +
                        "or approved by its creator. We link to the videos on YouTube rather than embedding or " +
                        "re-uploading them.",
                ],
            },
            {
                parts: [
                    "Looking for other recommended resources alongside this one? See our ",
                    { to: "/study-materials", label: "study material recommendations" },
                    ".",
                ],
            },
        ],
    },
    {
        slug: "canal-youtube-tanaka-san-japones",
        title: "Learn Japanese with Tanaka san no YouTube: Um Guia Independente",
        date: "2026-10-10",
        lang: "pt-PT",
        excerpt:
            "Um guia independente do canal de YouTube Learn Japanese with Tanaka san: as séries, as legendas e " +
            "os extras gratuitos, por onde começar e uma rotina de estudo.",
        tags: ["Study Tips", "Beginner"],
        keywords: [
            "Learn Japanese with Tanaka san",
            "canal de YouTube japonês",
            "prática de audição em japonês",
            "shadowing em japonês",
            "Egg-san's Diary",
            "Tanaka Radio",
        ],
        content: [
            {
                parts: [
                    "O \"Learn Japanese with Tanaka san\" é um canal de YouTube construído em torno de duas " +
                        "personagens ilustradas recorrentes: a Tanaka-san, um nigiri de sushi de atum, e a " +
                        "Egg-san, um nigiri de sushi de ovo que está a aprender japonês com ela, como se descreve " +
                        "na ",
                    { href: "https://www.youtube.com/@japanese_tanakasan", label: "própria página do canal" },
                    ". Este é um artigo independente do NihongoTech - não temos qualquer parceria, patrocínio ou " +
                        "afiliação com o canal ou com a sua criadora. Nada aqui reproduz os seus guiões ou " +
                        "transcrições. Os factos vêm das próprias páginas do canal, do seu site e da sua " +
                        "apresentação pessoal no ",
                    { href: "https://note.com/japanese_tanaka/n/n2f701618c871", label: "note.com" },
                    ", e foram verificados a 10 de outubro de 2026, por isso pormenores como o número de " +
                        "episódios, as legendas e os preços podem ter mudado entretanto.",
                ],
            },
            { heading: "Quem está por detrás do canal" },
            {
                text:
                    "No seu primeiro texto no note.com, publicado a 31 de março de 2024, a criadora - que se " +
                    "apresenta como Tanaka e indica o Japão como o sítio onde vive - diz que faz os vídeos " +
                    "sobretudo sozinha, da gravação de voz e da ilustração às legendas e à edição, e que está a " +
                    "pensar em formar uma equipa. Nos vídeos mais longos, por vezes pede a tradutores que façam " +
                    "as legendas em inglês. Calcula já ter criado mais de 100 personagens no total (nunca as " +
                    "contou, e algumas foram criadas em conjunto com os espectadores), muito além da Tanaka-san " +
                    "e da Egg-san. A pergunta que diz orientar o canal é どうしたら飽きずに楽しく学習を続けることが" +
                    "できるか - grosso modo, \"como é que quem aprende consegue continuar a estudar sem se " +
                    "aborrecer e com prazer\". Descreve o canal como leituras ilustradas para principiantes e " +
                    "conversas ao estilo de rádio para alunos de nível intermédio a avançado.",
            },
            {
                parts: [
                    "Para além do YouTube, o mesmo texto refere um livro de banda desenhada de quatro quadros, " +
                        "de edição de autor, que ensina onomatopeias, artigos com as personagens (autocolantes, " +
                        "porta-chaves, crachás de lata) que vendeu num café em Quioto, e uma comunidade online onde os " +
                        "alunos estudam juntos em horários marcados. O seu ",
                    { href: "https://japanese-tanaka-san.com/", label: "site oficial" },
                    ", ligado nas descrições dos seus vídeos, dá a essa comunidade o nome Tanaka Club (só para " +
                        "membros, com acesso através de uma subscrição de membro do canal de YouTube) e lista os " +
                        "seus livros, incluindo o Handy Japanese Conversations, que descreve como um livro de " +
                        "nível principiante que ensina frases práticas através de banda desenhada de quatro " +
                        "quadros, com explicações em inglês. O separador \"Acerca de\" do canal mostra que este " +
                        "juntou-se ao YouTube a 5 de setembro de 2020.",
                ],
            },
            { heading: "O que há realmente no canal" },
            {
                text:
                    "O separador de vídeos e as listas de reprodução do canal mostram várias séries recorrentes " +
                    "claramente identificadas, e não uploads avulsos sem relação entre si:",
            },
            {
                list: [
                    "Egg-san's Diary (identificada como \"Easy Japanese Story\" em cada vídeo) - histórias " +
                        "curtas e ilustradas para principiantes, a acompanhar o dia a dia da Egg-san, com 20 " +
                        "episódios quando verificámos. A descrição da lista de reprodução, escrita pela criadora, " +
                        "fala em \"Easy Japanese stories for N5 learners\" (histórias fáceis de japonês para " +
                        "alunos de N5), e o site dela atribui a cada episódio uma classificação de uma a três " +
                        "estrelas e um ponto gramatical principal.",
                    "Listen, Dictate, Shadow! (Japanese Mini Conversation) - diálogos curtos e naturais, com " +
                        "quatro episódios quando verificámos, cada um repetido ao longo da mesma sequência de " +
                        "prática (mais sobre isto adiante).",
                    "Tanaka Radio, com 26 episódios numerados quando verificámos, e as derivações Bedtime " +
                        "Tanaka Radio e Tanaka Radio Bonus Talk - conversa mais longa, em formato de podcast, " +
                        "sobre o dia a dia, a aprendizagem da língua e cartas de ouvintes.",
                    "Vídeos Conversation Practice e Japanese Short Story, como \"Ordering Fast Food in " +
                        "Japanese\" e \"Capsule Toys in Japan\".",
                    "Vídeos de flashcards Kanji Camp - exercícios curtos construídos a partir dos kanji de cada " +
                        "bloco de dias da série de PDFs Kanji Camp da criadora (mais sobre isto adiante).",
                    "Vídeos avulsos de vocabulário, como \"50 Useful Japanese Compliment Phrases\".",
                ],
            },
            {
                text:
                    "As legendas variam de vídeo para vídeo. Todos os vídeos com diálogo falado que verificámos " +
                    "- Egg-san's Diary #19, Listen, Dictate, Shadow! #4 e Tanaka Radio #26, mais o vídeo " +
                    "Ordering Fast Food - têm legendas em inglês e em japonês inseridas manualmente, e a maioria " +
                    "tem também legendas automáticas em japonês. Os dois vídeos só com texto no ecrã que " +
                    "verificámos (um vídeo de flashcards Kanji Camp e o vídeo de frases de elogio) não tinham " +
                    "nenhuma faixa de legendas, embora ambos mostrem no ecrã a leitura ou o significado. E nem " +
                    "tudo é público: alguns episódios Tanaka Radio Bonus Talk e uploads de conversa da criadora " +
                    "têm o selo \"Members only\" (só para membros), e a página da comunidade dela indica vídeos " +
                    "só para membros e acesso antecipado entre as vantagens de uma subscrição paga de membro do " +
                    "canal de YouTube.",
            },
            { heading: "Extras gratuitos e pagos" },
            {
                text:
                    "Os vídeos são gratuitos, mas os extras que os acompanham são uma mistura, por isso convém " +
                    "saber o que está a obter antes de clicar:",
            },
            {
                list: [
                    "Egg-san's Diary: o site dela descreve a série como totalmente gratuita, apoiada por " +
                        "donativos de alunos e professores. Cada episódio tem um PDF com um Reading Script (sem " +
                        "furigana e sem explicações) e um Learning Script (com furigana e explicações de " +
                        "gramática).",
                    "Listen, Dictate, Shadow!: os dois episódios que verificámos, o n.º 1 e o n.º 4, têm cada " +
                        "um uma transcrição em PDF gratuita ligada na descrição.",
                    "Tanaka Radio: o episódio que verificámos (n.º 26) tem uma lista de vocabulário e frases em " +
                        "PDF gratuita. A transcrição completa com furigana é um descarregamento pago (2,68 € " +
                        "quando verificámos), gratuito para membros de nível Premium do Tanaka Club.",
                    "Kanji Camp: uma série de PDFs paga, vendida em PDFs individuais ou num pacote, com uma " +
                        "amostra gratuita da lição completa do Dia 1. O site dela diz que abrange os 240 kanji " +
                        "ensinados nos dois primeiros anos da escola primária japonesa - uma lista por ano " +
                        "escolar, não por nível do JLPT.",
                ],
            },
            { heading: "Um olhar mais de perto sobre seis vídeos" },
            { heading: "Egg-san's Diary #19 - \"A Visitor at Midnight\" (principiante)" },
            {
                parts: [
                    "Uma história curta ilustrada, com dois minutos ",
                    { href: "https://www.youtube.com/watch?v=GUEFm_lt-A0", label: "(ver no YouTube)" },
                    ", sobre ilustrações simples, com legendas em inglês e em japonês disponíveis. O site da " +
                        "criadora classifica este episódio com duas estrelas em três e indica 〜かもしれない como " +
                        "ponto gramatical principal, e a descrição inclui uma ligação para uma transcrição em " +
                        "PDF gratuita. É um bom teste para ver se o formato lhe serve: se conseguir acompanhar " +
                        "com as legendas em japonês ligadas, o resto da lista de reprodução, que a criadora " +
                        "indica para alunos de N5, deverá parecer igualmente acessível. Se parecer rápido demais, " +
                        "recue até ao episódio n.º 1 (veja as listas de reprodução abaixo), embora o seu ponto " +
                        "gramatical principal, 〜なければいけない, seja um padrão de N4 nas nossas páginas de gramática.",
                ],
            },
            { heading: "おすすめは何ですか？- Listen, Dictate, Shadow! #4 (principiante a intermédio)" },
            {
                parts: [
                    "Um episódio de cerca de sete minutos ",
                    { href: "https://www.youtube.com/watch?v=S4CHGm0ouPo", label: "(ver no YouTube)" },
                    " - o título significa, grosso modo, \"O que recomenda?\" - e é o exemplo mais claro do " +
                        "formato mais estruturado do canal. A própria descrição divide o episódio em seis passos " +
                        "com indicação de tempo: ouvir sem legendas, ouvir com legendas em inglês, ditado, " +
                        "confirmar as respostas, ler em voz alta e, por fim, shadowing, com um pequeno segmento " +
                        "\"Tips from Tanaka\" sobre a expressão do título a meio. Não precisa de seguir " +
                        "exatamente os tempos indicados - a ideia que vale a pena aproveitar é reutilizar a " +
                        "mesma conversa curta para vários tipos de prática diferentes, em vez de a ver uma vez " +
                        "e passar à frente.",
                ],
            },
            { heading: "Tanaka Radio #26 - \"I Don't Like Summer, but I Want to Enjoy It\" (intermédio a avançado)" },
            {
                parts: [
                    "Um episódio de cerca de seis minutos e meio, em formato de monólogo ",
                    { href: "https://www.youtube.com/watch?v=8ukRMLt5nY8", label: "(ver no YouTube)" },
                    ", com legendas em inglês e em japonês disponíveis. Os tempos da descrição dividem-no entre " +
                        "a conversa da própria criadora (cerca dos primeiros cinco minutos) e uma secção de " +
                        "cartas de ouvintes. Na sua apresentação no note.com, ela descreve o rádio como dirigido " +
                        "a alunos de nível intermédio e avançado (中〜上級者向けのラジオ), por isso encare-o como " +
                        "um passo seguinte, quando as histórias graduadas para principiantes começarem a parecer " +
                        "lentas, e não como um ponto de partida. Há uma lista de vocabulário e frases em PDF " +
                        "gratuita ligada na descrição; a transcrição completa é o extra pago.",
                ],
            },
            { heading: "Kanji Camp: vídeos de flashcards e a série paga de PDFs" },
            {
                parts: [
                    "Os vídeos de flashcards Kanji Camp, como o ",
                    { href: "https://www.youtube.com/watch?v=xmhc12cGfuE", label: "Kanji Flashcards | Kanji Camp 1 (Day 1-7)" },
                    ", têm cerca de quatro minutos e meio e usam os kanji dos dias 1 a 7 do primeiro PDF Kanji " +
                        "Camp como flashcards no ecrã, divididos em cinco \"níveis\". Este vídeo não tem faixa de " +
                        "legendas, mas o cartão que amostrámos mostrava o kanji com a leitura em hiragana por " +
                        "cima e o significado em inglês por baixo. O vídeo explicativo do próprio canal, ",
                    { href: "https://www.youtube.com/watch?v=nSOQdg28Btg", label: "\"Learn Kanji the Fun Way: New Kanji Camp is Here!\"" },
                    " (cerca de quatro minutos, com legendas em inglês e em japonês), apresenta a série como " +
                        "PDFs que ensinam kanji através de manga. Segundo o site dela, cada lição tem um vídeo, " +
                        "uma manga de duas páginas, uma lista de kanji, uma folha de exercícios e uma lista de " +
                        "vocabulário, e os PDFs são pagos, por isso os vídeos gratuitos são uma amostra e não o " +
                        "curso completo.",
                ],
            },
            { heading: "\"50 Useful Japanese Compliment Phrases\" (vocabulário principiante a intermédio)" },
            {
                parts: [
                    "Uma lista de frases com pouco menos de nove minutos ",
                    { href: "https://www.youtube.com/watch?v=N0O6VO5AvNU", label: "(ver no YouTube)" },
                    " e sem faixa de legendas no momento da verificação. Isso importa menos do que o habitual, " +
                        "porque o fotograma que amostrámos mostrava uma linha em inglês seguida de uma versão " +
                        "em japonês Formal e outra Casual, com furigana por cima dos kanji. É uma fonte razoável " +
                        "de frases prontas a usar para o passo de vocabulário da rotina abaixo, e o par " +
                        "formal/informal é uma forma útil de reparar no registo - o nosso ",
                    { to: "/blog/keigo-linguagem-de-respeito-em-japones", label: "guia de keigo" },
                    " explica o estilo educado です/ます em que o japonês formal assenta.",
                ],
            },
            { heading: "Três listas de reprodução por onde começar" },
            {
                text:
                    "Se preferir começar por uma lista de reprodução em vez de procurar episódios individuais, " +
                    "estas três cobrem os principais formatos de audição do canal, das histórias graduadas para " +
                    "principiantes à conversa em formato de podcast:",
            },
            {
                parts: [
                    { href: "https://www.youtube.com/playlist?list=PLkK7KO2TnEcxrvBgXstE_jby8ylv-RGCl", label: "Egg-san's Diary | Easy Japanese Stories" },
                    " - a descrição da própria criadora indica esta lista para alunos de N5, por isso é uma " +
                        "primeira paragem sensata se estiver a começar em japonês. Comece pelo princípio, com o ",
                    { href: "https://www.youtube.com/watch?v=eXgMsg8WouU", label: "episódio n.º 1, \"Cleaning Day\"" },
                    ", que o site dela classifica com uma estrela em três (ponto gramatical principal " +
                        "〜なければいけない), e não pelo upload mais recente.",
                ],
            },
            {
                parts: [
                    { href: "https://www.youtube.com/playlist?list=PLkK7KO2TnEcy9N74F7IRw0Dcmg2NAnvMj", label: "Listen, Dictate, Shadow! (Japanese Mini Conversation)" },
                    " - a lista a escolher se quiser o método estruturado de ouvir/ditar/fazer shadowing em " +
                        "si: a descrição da série diz que cada diálogo é repetido através de audição, ditado e " +
                        "shadowing. A descrição do episódio n.º 4 remete quem chega de novo para o ",
                    { href: "https://www.youtube.com/watch?v=4x_mcq67ing", label: "episódio n.º 1, うん・ううん (\"sim\" / \"não\" informais)" },
                    " como ponto de partida.",
                ],
            },
            {
                parts: [
                    { href: "https://www.youtube.com/playlist?list=PLkK7KO2TnEczjRVTgW2fSGxRgWay3Z5a4", label: "Tanaka Radio | Japanese Podcast" },
                    " - descrita no YouTube como um podcast acolhedor sobre o dia a dia, a aprendizagem de " +
                        "línguas e a cultura japonesa. É a lista para onde avançar quando as histórias curtas " +
                        "graduadas já parecerem fáceis, para uma prática de audição mais longa e menos " +
                        "simplificada.",
                ],
            },
            { heading: "Uma rotina de estudo construída à volta destes vídeos" },
            {
                text:
                    "O próprio formato Listen, Dictate, Shadow! do canal já tem incorporada uma ideia útil: " +
                    "reaproveitar o mesmo excerto curto para vários tipos de prática, em vez de o ver só uma " +
                    "vez. Eis uma rotina que parte dessa ideia e volta a ligar-se às próprias ferramentas do " +
                    "NihongoTech para as partes que um vídeo de YouTube não consegue verificar por si.",
            },
            {
                list: [
                    "Primeira passagem, sem legendas: veja um episódio curto (uma história de Egg-san's Diary " +
                        "ou uma conversa de Listen, Dictate, Shadow!) com as legendas desligadas e repare " +
                        "apenas no que consegue apanhar só pelo ouvido.",
                    "Segunda passagem, com legendas em inglês: volte a vê-lo com legendas em inglês para " +
                        "preencher as lacunas do que ouviu. Por agora, mantenha o texto em japonês escondido.",
                    "Ditado: pause a cada uma ou duas frases e escreva o que ouve antes de olhar para qualquer " +
                        "texto em japonês e só depois confirme com as legendas em japonês ou com a transcrição " +
                        "em PDF gratuita - é este passo que transforma a audição passiva em evocação ativa, e só " +
                        "funciona se ainda não tiver visto o texto.",
                    "Shadowing: volte a reproduzir o vídeo e diga cada frase em voz alta logo a seguir ao " +
                        "áudio, acompanhando o ritmo e a cadência, e não apenas a ler o texto.",
                    "Notas de vocabulário: registe cinco a dez palavras ou expressões novas por vídeo para uma " +
                        "lista contínua, em vez de todas as palavras do episódio - uma história de dois " +
                        "minutos de Egg-san's Diary ou o vídeo de frases de elogio é uma quantidade razoável por " +
                        "dia.",
                    "Repetir mais tarde: volte ao mesmo excerto alguns dias depois, com as legendas " +
                        "desligadas, e veja quanto mais consegue apanhar do que da primeira vez.",
                ],
            },
            {
                parts: [
                    "O Egg-san's Diary combina também bem com a consulta de gramática, porque o site da " +
                        "criadora indica um ponto gramatical principal por episódio e vários deles têm uma " +
                        "página aqui: o episódio n.º 2 (〜ませんか) corresponde à nossa ",
                    { to: "/pt/grammar/n5-masenka", label: "página de ませんか" },
                    ", o n.º 3 (〜たい) à nossa ",
                    { to: "/pt/grammar/n5-tai-desu", label: "página de たいです" },
                    ", o n.º 16 (〜たり、〜たり) à nossa ",
                    { to: "/pt/grammar/n4-tari-tari", label: "página de たり〜たりします" },
                    " e o n.º 20 (〜ので) à nossa ",
                    { to: "/pt/grammar/n4-node", label: "página de ので" },
                    ". O 〜なければいけない do episódio n.º 1 significa o mesmo que 〜なければなりません (\"ter de\"), " +
                        "embora いけない soe um pouco mais conversacional - veja a nossa ",
                    { to: "/pt/grammar/n4-nakereba-narimasen", label: "página de なければなりません" },
                    ".",
                ],
            },
            {
                parts: [
                    "Depois, feche o ciclo com ferramentas que verificam o que um vídeo não consegue: se uma " +
                        "palavra que registou estiver nas listas do JLPT, diga-a em voz alta na ",
                    { to: "/pt/speaking-practice", label: "Prática de Conversação" },
                    " e deixe o navegador confirmá-la, procure as restantes com a leitura completa na ",
                    { to: "/pt/vocabulary", label: "lista de vocabulário" },
                    " organizada por nível do JLPT, leia uma história curta na ",
                    { to: "/pt/reading-practice", label: "Prática de Leitura" },
                    " quando o Egg-san's Diary já lhe parecer confortável, e transforme qualquer kanji que " +
                        "tenha notado num vídeo Kanji Camp em algo que realmente retém com o ",
                    { to: "/pt/kanji-roulette", label: "Roleta de kanji" },
                    ". Se o Egg-san's Diary ainda lhe parecer rápido, as ",
                    { to: "/pt/lessons", label: "lições de N5" },
                    " do NihongoTech cobrem o hiragana, o katakana e o vocabulário e a gramática essenciais em " +
                        "que a série assenta, embora o ponto gramatical principal de alguns episódios - " +
                        "〜なければいけない, 〜たり、〜たり e 〜ので - esteja no N4 nas nossas páginas de " +
                        "gramática, pelo que alguns episódios vão exigir um esforço extra a quem está no N5.",
                ],
            },
            { heading: "Onde se encaixa a par do estudo estruturado e do JLPT" },
            {
                parts: [
                    "Pense no canal como prática de audição e de shadowing que fica ao lado de um curso " +
                        "estruturado, e não no lugar dele. O JLPT inclui uma secção de compreensão oral em todos " +
                        "os níveis, por isso a prática regular de audição ajuda, seja qual for o nível a que se " +
                        "candidata, mas nada do que encontrámos da parte da criadora descreve os vídeos como " +
                        "feitos ou aprovados para a preparação para o JLPT. Use o nosso ",
                    { to: "/pt/learning-roadmap", label: "percurso de aprendizagem" },
                    " para decidir o que estudar e por que ordem, e deixe os vídeos treinarem o ouvido.",
                ],
            },
            { heading: "Pontos fortes e limitações" },
            { text: "Pontos fortes:" },
            {
                list: [
                    "Legendas em inglês e em japonês inseridas manualmente em todos os vídeos com diálogo " +
                        "falado que verificámos, e não apenas numa língua ou só por legendas automáticas.",
                    "Uma variedade genuína de formatos dentro do mesmo canal - histórias graduadas para " +
                        "principiantes, mini-conversas estruturadas, conversa mais longa de nível " +
                        "intermédio/avançado em formato de podcast, e flashcards de kanji - útil para combinar " +
                        "audição, leitura e fala em vez de treinar só uma competência.",
                    "Transcrições em PDF gratuitas para o Egg-san's Diary e para os episódios de Listen, " +
                        "Dictate, Shadow! que verificámos, para poder confirmar o seu ditado sem pagar nada.",
                    "Um catálogo que remonta a setembro de 2020, por isso há bastante material para explorar " +
                        "depois dos uploads mais recentes.",
                ],
            },
            { text: "Limitações:" },
            {
                list: [
                    "Nenhum currículo alinhado com o JLPT que tenhamos conseguido encontrar. O rótulo \"N5\" é " +
                        "a descrição que a própria criadora faz da lista de reprodução Egg-san's Diary; o site " +
                        "dela classifica os episódios com uma a três estrelas e um ponto gramatical principal, " +
                        "e não por níveis do JLPT, e o Kanji Camp segue os anos escolares japoneses. Combine o " +
                        "canal com um recurso estruturado se precisar de cobertura do JLPT nível a nível.",
                    "As legendas não são universais: os vídeos só com texto no ecrã, de flashcards e de listas " +
                        "de frases, que verificámos não tinham nenhuma faixa de legendas.",
                    "Alguns dos extras mais aprofundados são pagos - as transcrições completas do Tanaka " +
                        "Radio, os PDFs do Kanji Camp e a subscrição do Tanaka Club -, embora os vídeos e as " +
                        "transcrições do Egg-san's Diary e do Listen, Dictate, Shadow! sejam gratuitos.",
                    "Alguns uploads são só para membros, por isso nem todos os vídeos estão disponíveis para " +
                        "toda a gente.",
                    "Pelo que a própria criadora conta, isto é sobretudo um projeto de uma só pessoa, por isso " +
                        "o ritmo e a profundidade das publicações dependem disso.",
                ],
            },
            { heading: "Créditos, e uma nota honesta" },
            {
                parts: [
                    "Tudo o que foi dito acima vem do que efetivamente vimos e lemos na ",
                    { href: "https://www.youtube.com/@japanese_tanakasan", label: "própria página do canal no YouTube" },
                    ", no seu ",
                    { href: "https://japanese-tanaka-san.com/", label: "site oficial" },
                    " e na sua apresentação pessoal no ",
                    { href: "https://note.com/japanese_tanaka/n/n2f701618c871", label: "note.com" },
                    ". Todo o crédito pelo canal, pelas suas personagens e pelo seu conteúdo pertence à sua " +
                        "criadora, Tanaka. Este artigo é uma recomendação independente do NihongoTech - não " +
                        "temos qualquer parceria, patrocínio ou relação de aprovação com o canal, e nada aqui " +
                        "foi fornecido ou aprovado pela sua criadora. Ligamos aos vídeos no YouTube em vez de os " +
                        "incorporar ou de os voltar a carregar.",
                ],
            },
            {
                parts: [
                    "Procure outros recursos recomendados, além deste? Veja as nossas ",
                    { to: "/pt/study-materials", label: "recomendações de materiais de estudo" },
                    ".",
                ],
            },
        ],
    },
];

export function getBlogPostBySlug(slug) {
    return blogPosts.find((post) => post.slug === slug);
}
